/**
 * SDMS Result Entry — the QC Batch form's "Open SDMS" screen.
 *
 * Ported from QC-Batch-Prototype.html SCREEN 3 (#screenSdms / openSdms()).  The
 * "spreadsheet" is a STYLED HTML TABLE emulating Excel (column-letter header,
 * row numbers, sticky header, a metadata block, a field-row separator, and one
 * parameter block per run in the QC batch's sequence).  The per-test-method
 * SDMS TEMPLATE drives its columns — a new method is a config entry, not code.
 *
 * This is the RESULT-ENTRY entry point (from a saved QC batch).  It is distinct
 * from `SdmsSpreadsheet` (the Result-VALIDATION preview opened from the reporting
 * / Open-Package flow, which uses the heavy licensed Univer engine).  Here the
 * grid is a lightweight table so the entry screen is not blocked by ADR-0005.
 *
 * Scope (v1): faithful screen + template grid + editable Result cells + the full
 * ribbon.  Save / Submit / Recalculate / Rollback etc. are wired to the shared
 * notify service as "later phase" stubs until the entry SP + formula engine land.
 */
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { fmtLocalDate } from '../../utils/datetime';
import { useNotify } from '../../components/notifications';
import { runLoading } from '../../components/loading/loadingStore';
import { useHideLoadingBar } from '../../components/loading/useLoading';
import { useAuthStore } from '../../auth/store';
import { useConfirm, useSaveFlow, useDeleteFlow } from '../../components/confirm';
import { AuditTrailPopup } from '../../components/AuditTrailButton';
import { qcBatchApi, type SdmsTestLookup, type SdmsQcBatchLookup,
         type QcCreateBody, type QcCreateResult, type QcSeqRow, type QcBatchHeader, type SdmsCalcRow,
         type SdmsImportWrite, type SdmsMergeDataBody } from './qcBatch.api';
import { SdmsImportFilesDialog } from './SdmsImportFilesDialog';
import { assembleCalcRows, indexComputed, calcRowKey, type RoundParamLite } from './sdmsEngineCompute';
import { NON_RESULT_SHEETS, isResultSheet, overlaySheetInputs, ownFieldCells, EDIT_KEY_SEP, sheetCellText, ENGINE_OUTPUT_FIELD_KEYS } from './sdmsEngineOverlay';
import {
  computeDilutionIsReport, assembleDilutionRows, writeDilutionIsReportToSheet, isDFAdjustedFromRecords,
} from './sdmsDilutionIsReport';
import { unwrapLimsRound, isRepushableTemplateFormula, resultSheetSelector, runCodesOf } from './sdmsRecalc';
import { isSampleQcSplitSheet, SAMPLE_QC_SPLIT_MODEL } from './sdmsSampleQcSplit';
import { LookupCombo, type LookupData } from '../../components/LookupCombo';
import { useUnsavedGuard, confirmDiscardIfUnsaved } from '../../hooks/useUnsavedGuard';
import { useNavActions } from '../../hooks/useNavActions';
import { NotesListPopup, useNotesCount } from './SdmsProjectNotes';
import { SdmsBatchResultsPopup } from './SdmsBatchResultsPopup';
import { SdmsPackageDetail, type SdmsPackageRow } from './SdmsPackageDetail';
import { navigateToPage } from '../../layouts/navigateTo';
import { mergeSdmsTemplate, fieldRefCells, shiftFormula, canonicalDateText, decimalFormatOf, resubstituteFieldTokens, isDateNumFmt } from './sdmsMailMerge';
import { buildQcNotes } from './sdmsQcNotes';
import { buildQualifierNotes, fetchQualifierDefinitions } from './sdmsQualifierNotes';

/** Start a request NOW and read its outcome later (`await later()`), without an unhandled rejection
 *  being reported while other work runs in between. `later()` rejects exactly as the request did. */
function startEarly<T>(p: Promise<T>): () => Promise<T> {
  const settled = p.then(v => ({ ok: true as const, v }), (e: unknown) => ({ ok: false as const, e }));
  return async () => { const r = await settled; if (r.ok) return r.v; throw r.e; };
}

/** An in-memory copy of a picked file (same name, type and date), so reading it later does not depend on the file
 *  on disk staying as it was. Falls back to the original when it cannot be read now. */
async function copyFileInMemory(file: File): Promise<File> {
  try {
    return new File([await file.arrayBuffer()], file.name, { type: file.type, lastModified: file.lastModified });
  } catch (e) {
    console.warn('[SDMS] could not copy the imported file into memory; keeping the picked file', file.name, e);
    return file;
  }
}

/** The batch's run order as the merge records list it (SysSampleCode, first appearance) — the order
 *  OG's multi-instrument Raw Data summary lists the samples in (carryRawData). */
function sequenceCodes(records: readonly Record<string, unknown>[]): string[] {
  const out: string[] = []; const seen = new Set<string>();
  for (const r of records) {
    const k = Object.keys(r).find(x => x.toUpperCase() === 'SYSSAMPLECODE');
    const v = k ? String(r[k] ?? '').trim() : '';
    if (v && !seen.has(v.toUpperCase())) { seen.add(v.toUpperCase()); out.push(v); }
  }
  return out;
}
import { embedCalibrationSheets } from './sdmsAttachCalibration';
import { addImportRawDataSheet, rawDataSummaryTemplate, readsImportRawSheet,
  OG_IMPORT_RAW_SHEET, RAW_DATA_SUMMARY_SHEET, prefetchRawDataCarry } from './sdmsAttachRawData';
import { applyImportToOverlay, importValuesToSave, nonResultSheetsShowingFields, rawDataSummaryRows, recordsWithImport, refreshImportedSheets, type ImportOverlay } from './sdmsImportSequence';
import { overlaySdmsEnteredResults } from './sdmsEditRehydrate';
import { mergeImportWritesAcrossFiles, withSheetName } from './sdmsImportMerge';
import { importDateSerial } from './sdmsPersistText';
import { parseCellRef } from './sdmsDeptChainDropdown';
import { fetchQualifierSymbols } from './sdmsQualifierDropdown';
import { buildSdmsOpenWorkbook } from './sdmsOpenChain';
import { calibrationIdCell } from './sdmsCalibrationFields';
import { loadSdmsWorkbook } from './sdmsWorkbookIo';
import { limsRoundText } from './limsRoundFn';
import { SdmsQualifierPicker } from './SdmsQualifierPicker';
import { SdmsSheetPreparing, SdmsRecalcChip, type SheetPrepStage } from './SdmsSheetPreparing';
import { SdmsImportProgress, type ImportProgress, type ImportStep } from './SdmsImportProgress';
import { AppDialog } from '../../components/AppDialog/AppDialog';
import { extractSdmsReportData, type SdmsReportSheetData } from './sdmsReportData';
import type { SdmsReportInfo } from './qcBatch.api';
import { patchWorkbookCells, protectSheetsLikeOg, OG_PROTECTED_SUMMARY_SHEETS } from './sdmsXlsxPatch';
import { placeImportedPictures } from './sdmsImportImages';
import { useSdmsRibbonMinimize } from './useSdmsRibbonMinimize';
import { formatCombo } from '../../components/hotkeys';
import './sdmsEntry.css';

// The real spreadsheet control (Apache-2.0 Univer), shared with the Result
// Validation preview. Lazy so its chunk only loads on the blank-sheet path.
const SdmsUniverSheet = lazy(() => import('./SdmsUniverSheet').then(m => ({ default: m.SdmsUniverSheet })));
// Type-only import (no runtime cost, keeps the lazy chunk lazy) for the imperative
// handle the sheet exposes — `getEditedXlsx()` re-applies the live Univer model onto
// the loaded workbook and hands back the .xlsx bytes, which is how Save persists the
// analyst's typed results.
import type { SdmsUniverHandle } from './SdmsUniverSheet';
// QC Batch detail form (Phase 1 "QC Batch" ribbon action = open the linked batch,
// OG :9426). LAZY because QcBatchDetailPage statically imports THIS file (Open
// SDMS) — a static import back would be a cycle; a dynamic import breaks it.
const QcBatchDetailPage = lazy(() => import('./QcBatchDetailPage'));

/** One run from the QC batch's built sequence (samples + interleaved QC). */
export interface SdmsRun { code: string; qc: string; kind: 'sample' | 'qc' }

export interface SdmsEntrySeed {
  test:        string;
  method?:     string | null;
  matrix?:     string | null;
  instrument?: string | null;
  analyst?:    string | null;
  /** The minted QC Batch ID (display, e.g. QB…), or null when not yet saved. */
  qcBatchId?:  string | null;
  /** The QC batch Oid — audit source key (OG audit filters Source = QCBatchOid). */
  qcBatchOid?: string | null;
  runs:        SdmsRun[];
  /** OK→SDMS handoff: the built create payload (sequence + header). OG does NOT
   *  save an SDMS-template batch in the QC form — it hands the sequence to SDMS and
   *  the AnalyticalBatch is created (NewABID) from SDMS's Save (QCBatchController.cs
   *  :7213-7330). Present only on that handoff; null when opened on a saved batch. */
  pendingCreate?: QcCreateBody | null;
  /** Set when this seed came from the SDMS-hosted "New QC Batch" popup rather than
   *  the Analysis Queue → QC Batch walk. The docked workflow stepper describes that
   *  walk, so it stays hidden here — the user never took those steps. */
  fromSdmsPopup?: boolean;
}

/** Build a lightweight OOXML workbook from the handed-over QC sequence so the
 *  batch's samples/QC are VISIBLE in SDMS immediately (OG MailMerges the sequence
 *  into the per-test template — that template merge is a later phase; until then
 *  the sequence itself is shown). Goes through the same excelToUniver() path as
 *  every other sheet, so there's no second, unproven workbook constructor. */
async function buildSequenceWorkbook(seq: QcSeqRow[]): Promise<ArrayBuffer> {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Sequence');
  const cols: { header: string; width: number }[] = [
    { header: '#', width: 5 }, { header: 'QC Type', width: 14 }, { header: 'Code', width: 16 },
    { header: 'System ID', width: 15 }, { header: 'Job ID', width: 14 }, { header: 'Sample ID', width: 16 },
    { header: 'Sample Name', width: 26 }, { header: 'Component', width: 16 }, { header: 'DF', width: 8 },
    { header: 'Sample Amt', width: 12 }, { header: 'Final Vol', width: 12 }, { header: 'Units', width: 10 },
  ];
  ws.columns = cols;
  ws.getRow(1).font = { bold: true };
  seq.forEach((r, i) => ws.addRow([
    r.sort || i + 1, r.qcType, r.code, r.systemId ?? '', r.jobId ?? '', r.sampleId ?? '',
    r.sampleName ?? '', r.component ?? '', r.df, r.sampleAmount ?? '', r.finalVol ?? '', r.units ?? '',
  ]));
  // Extend the sheet's extents so it scrolls like Excel past the last data row.
  ws.getRow(Math.max(BLANK_SHEET_ROWS, seq.length + 5)).height = 15;
  return (await wb.xlsx.writeBuffer()) as ArrayBuffer;
}

/** Extents of the BLANK worksheet handed to Univer, so the empty sheet scrolls
 *  like Excel instead of ending at the last populated cell. */
const BLANK_SHEET_COLS = 60;
const BLANK_SHEET_ROWS = 300;

/** Grid-lookup column widths, sized for readability like OG's combos (which set
 *  Method explicitly to 350 — ctrl :2174 — and leave the rest to fit). */
/** Tooltip on the sheet actions while the "sheet not ready" panel is up. */
const SHEET_BUSY_TIP = 'The sheet is still loading — please wait until it is ready.';
/** Step 1 of the "sheet not ready" panel, in order: the network fetch and the client-side re-merge steps. The
 *  panel's percent moves as each one finishes (a step a path skips — calibration, saved values — just jumps). */
const DATA_STEPS = ['fetch', 'merge', 'summaries', 'dropdowns', 'calibration', 'rawdata', 'saved-values', 'dilution'] as const;
const SDMS_TEST_COL_W       = [190, 110, 250, 220, 70];                      // Test·Matrix·Method·TemplateName·Sx
const SDMS_BATCH_COL_W      = [160, 120, 180, 110, 150, 130];                // QCBatchID·JobID·Test·Matrix·Method·Template
const SDMS_BATCH_COL_W_VIEW = [160, 120, 180, 110, 150, 130, 120];           // …+ Status (View mode)

/** The blank canvas is a constant — build the empty .xlsx buffer ONCE (module
 *  scope) and reuse it across every SDMS open, so re-opening the screen never
 *  re-imports ExcelJS or rebuilds the buffer. */
let _blankBufPromise: Promise<ArrayBuffer> | null = null;
function blankWorkbook(): Promise<ArrayBuffer> {
  if (!_blankBufPromise) {
    _blankBufPromise = (async () => {
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Sheet1');
      // Real extents so the empty sheet scrolls like Excel (excelToUniver sizes
      // the canvas from columnCount/rowCount; a bare addWorksheet() reports 0).
      ws.columns = Array.from({ length: BLANK_SHEET_COLS }, () => ({ width: 11 }));
      ws.getRow(BLANK_SHEET_ROWS).height = 15;
      // ExcelJS `columnCount` counts CELLS, not width-only columns, so the widths above
      // reload as columnCount=0 → excelToUniver renders just ONE column and the blank grid
      // cannot fill a wide monitor (empty strip on the right, no horizontal scrollbar). An
      // empty-string cell in the last column makes columnCount survive as BLANK_SHEET_COLS
      // (null does NOT register it; '' does), so Univer draws all the columns. The row-height
      // above already does the equivalent for rowCount. (2026-09-21)
      ws.getRow(1).getCell(BLANK_SHEET_COLS).value = '';
      return (await wb.xlsx.writeBuffer()) as ArrayBuffer;
    })();
  }
  return _blankBufPromise;
}

// Warm the heavy spreadsheet chunk (Univer + ExcelJS) in the BACKGROUND as soon
// as this module is first evaluated — which happens when the Analysis/QC area
// loads (QcBatchDetailPage statically imports this file). By the time the user
// opens SDMS, the big Univer chunk is already downloaded + parsed and the blank
// buffer is built, so the sheet paints instantly on click instead of downloading
// Univer on the click itself. Idle-scheduled so it never competes with the
// initial render; failures are swallowed (it's a pure prefetch).
let _sdmsWarmStarted = false;
function warmSdmsSheet(): void {
  if (_sdmsWarmStarted || typeof window === 'undefined') return;
  _sdmsWarmStarted = true;
  const go = () => {
    import('./SdmsUniverSheet').catch(() => {});
    blankWorkbook().catch(() => {});
    // Also warm the QC Batch form chunk: Retrieve in a blank SDMS opens the
    // "New QC Batch" modal, which lazy-loads QcBatchDetailPage. Preloading it here
    // (idle, after this module finishes evaluating so the static import cycle is
    // already resolved) means the modal opens straight onto the form instead of
    // flashing the "Opening QC Batch…" Suspense fallback.
    import('./QcBatchDetailPage').catch(() => {});
  };
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback;
  if (ric) ric(go, { timeout: 2000 }); else setTimeout(go, 400);
}
warmSdmsSheet();

/**
 * Rendered two ways:
 *  • from the QC Batch form's "Open SDMS" — `seed` carries the batch context and
 *    `onBack` returns to the QC detail;
 *  • from the "Analysis → SDMS" nav item — no seed (blank sheet; Retrieve a batch),
 *    no Back (it's a top-level page). Matches OG nav item id 'Spreadsheet' (view 'SDMS').
 */
export default function SdmsResultEntryPage({ seed: seedProp, onBack, onCreated }: {
  seed?: SdmsEntrySeed;
  onBack?: () => void;
  /** Fired once Case-A Save has MINTED the AnalyticalBatch from `seed.pendingCreate`. The QC
   *  Batch form that handed the payload over stays mounted underneath (it swaps this page in)
   *  with its own "new & unsaved" guard still armed on `savedBatchId == null`; this lets it
   *  record the created batch so navigating away no longer prompts "Unsaved changes will be
   *  lost" for a batch that was just saved (user report 2026-09-22). */
  onCreated?: (created: { abOid: string; qcBatchId: string }) => void;
}) {
  const notify = useNotify();
  // No header progress bar on this screen (user 2026-09-28) — blocking scrims still show.
  useHideLoadingBar();
  // Clear (OG `clearcontrols`, SpreadsheetEntryController :17864) returns the screen to the
  // nav-opened state — no test, no batch, blank document — even when this page was opened
  // SEEDED (Open SDMS / OK→SDMS handoff). The seed is a prop, so Clear shadows it: from
  // then on every `seed`/`s`/`blank` read below behaves exactly as the nav-opened page.
  const [cleared, setCleared] = useState(false);
  const seed = cleared ? undefined : seedProp;
  // Current user's display name — the batch CREATOR on a fresh SDMS handoff.
  // OG's GetSdmsAnalyst returns the batch's CreatedBy.DisplayName for ANALYZEDBY/
  // ENTEREDBY (QCBatchController :9501/:17141); on a new batch that is the current
  // user. Was hard-defaulting to 'Service' when no saved header existed. (item C, 2026-09-01)
  const meName = useAuthStore(s => s.user?.name || s.user?.email || '');

  const confirm = useConfirm();
  const [full, setFull] = useState(true);
  const ribbon = useSdmsRibbonMinimize();
  // OG Mode combo is "View" / "Enter" (Review/Verify are commented out in OG,
  // SpreadsheetEntryController :2158). Enter = data entry; View = read-only load.
  //
  // OG derives the mode from the batch's status: Enter for qcstatus 0|1, View for
  // >1 (ctrl :2612 / :2697). The enum is 0 = Pending Result Entry, 1 = Pending
  // Submission, 2 = Pending Validation, 3 = Validated, 4 = Pending Approval,
  // 5 = Approved — so ENTER covers Pending Submission too (:2685 sets the ribbon
  // Status to "Pending Submission" inside that same branch). Reopening a
  // part-entered batch in Enter to finish it is normal OG usage, NOT an edge case.
  //
  // DEFAULT = 'Enter' (changed 2026-08-10, OG parity). `qcstatus` is never assigned
  // before OG's :2612 check, so a freshly-opened view carries the int default 0 —
  // which lands OG in Enter. AI previously hardcoded nav-opened to VIEW on the
  // reasoning that Enter "lists no batches"; that reasoning was wrong. Enter's list
  // is empty ONLY while no Test is picked, because the legacy sproc's Enter branch
  // is `WHERE TM.Oid = @TestMethodID` with no null-guard (verified on
  // AlpacaDWO_260804: Enter alone 0 rows, Enter + a test returns rows, View alone
  // 4371). Note Enter hides the Audit Trail action — also OG (modechange).
  const [mode, setMode] = useState<'Enter' | 'View'>('Enter');
  // View-mode Edit toggle (OG: Edit→"Update", Clear→"Cancel" while editing).
  const [editing, setEditing] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  // Unimplemented ribbon actions announce themselves in the shared MODAL (never
  // the top banner) — matching how OG surfaces such notices via ShowMessage.
  const stub = (name: string) =>
    void confirm.alert({ title: name, message: `${name} is not available yet.`, tone: 'info' });
  // Blank-sheet workbook. We hand Univer a REAL (empty) .xlsx buffer rather than a
  // hand-built Univer snapshot: that way the blank sheet goes through the exact
  // same excelToUniver() path the Result-Validation preview uses, so there's no
  // second, unproven way to construct a workbook. (A hand-rolled snapshot missing
  // fields Univer expects renders as a completely blank area — that cost two
  // failed attempts on 2026-07-28.)
  const [blankBuf, setBlankBuf] = useState<ArrayBuffer | null>(null);
  // The RETRIEVED batch workbook (its saved sheet, or its empty template) — this is
  // what makes the sheet actually fill once a QC batch is picked. Null = show the
  // blank canvas instead.
  const [batchBuf, setBatchBuf] = useState<ArrayBuffer | null>(null);
  // True when batchBuf is a fresh RE-MERGE (no cached formula results) so the sheet computes
  // its formulas even in read-only View; false only for the saved-blob fallback. (2026-09-16)
  const [bufRemerged, setBufRemerged] = useState(true);
  // Whole-sheet locks (OG SpreadsheetEntryController :3629-3690 / :4455-4512 — after the merge OG calls
  // `Worksheet.Protect(...)` on the "Instruments" sheet (when Traceability names it), the "Raw Data"
  // sheet, and the "Reagents" sheet when Traceability names it AND ResultEntryDefaultSettings
  // .ReagentEntrySDMS = No; every cell of those sheets is then locked, select-only). The result
  // sheets keep the per-cell FORMULA locks the Univer wrapper derives itself. (user 2026-09-23)
  const [lockedSheets, setLockedSheets] = useState<string[]>([]);
  /** Engine B output columns locked on the result sheets (null = none) — see refreshEngineLocks. */
  const [engineLockCols, setEngineLockCols] = useState<{ cols: number[]; fromRow: number } | null>(null);
  const reagentEntryRef = useRef<boolean>(true);   // fail-open like OG's "no settings row" case
  const lockedSheetsFor = (traceability: string | null | undefined): string[] => {
    const tr = (traceability ?? '').toUpperCase();
    const out = ['Raw Data'];
    if (tr.includes('INSTRUMENTS')) out.push('Instruments');
    if (tr.includes('REAGENTS') && !reagentEntryRef.current) out.push('Reagents');
    return out;
  };
  const [retrieving, setRetrieving] = useState(false);
  // "Sheet not ready" panel (SdmsSheetPreparing, user 2026-09-28): up from Retrieve until the first
  // calculation has settled; the sheet actions are disabled meanwhile (sheetBusy).
  const [prepStage, setPrepStage] = useState<SheetPrepStage>('data');
  const [prepPct, setPrepPct] = useState<number | null>(null);
  /** Remounts the panel for each sheet build, so a build never starts from the previous one's 99%. */
  const [prepRun, setPrepRun] = useState(0);
  /** Univer is (re)building a BATCH workbook — incl. the Edit-mode formula rebuild, which has no Retrieve. */
  const [sheetBuilding, setSheetBuilding] = useState(false);
  /** The on-open Engine B compute (engine-bound templates): its values land after the first paint. */
  const [engineOpenBusy, setEngineOpenBusy] = useState(false);
  const sheetBusy = retrieving || sheetBuilding || engineOpenBusy;
  // ONE ribbon action at a time. Submit's first click was silently saving — rounding plus a full recalculation —
  // and a second click started it again (user report 2026-09-30). ribbonAction locks every ribbon button from the
  // first click until the action (its dialogs, save and recalculation included) has finished; a click that lands
  // before the disabled state renders is dropped by the ref. sheetBusy keeps its own meaning (the sheet-not-ready panel).
  const [actionBusy, setActionBusy] = useState(false);
  const actionRef = useRef(false);
  const ribbonBusy = sheetBusy || actionBusy;
  const ribbonAction = (fn: () => unknown) => (): void => {
    if (actionRef.current) return;
    actionRef.current = true;
    setActionBusy(true);
    void Promise.resolve()
      .then(fn)
      .catch(e => console.warn('[SDMS] ribbon action failed', e))
      .finally(() => { actionRef.current = false; setActionBusy(false); });
  };
  /** Where a slow open spends its time, for the console (user 2026-09-28: ~22 s on a VOC batch). Each mark
   *  ends a phase: fetch (network) · merge · summaries · dropdowns · calibration · rawdata · saved-values ·
   *  dilution (the client-side re-merge steps) · handover · build (workbook) · calc (settled) · engine. */
  const openTimesRef = useRef<{ t0: number; marks: Array<[string, number]> } | null>(null);
  const markOpen = (phase: string) => {
    const o = openTimesRef.current;
    if (o && !o.marks.some(m => m[0] === phase)) o.marks.push([phase, performance.now()]);
  };
  /** One step-1 sub-step finished: time it, move the panel's percent, and yield one macrotask so the percent
   *  paints before the next (main-thread heavy) step starts. */
  const dataStep = async (phase: typeof DATA_STEPS[number]): Promise<void> => {
    markOpen(phase);
    setPrepPct(Math.round(((DATA_STEPS.indexOf(phase) + 1) * 100) / DATA_STEPS.length));
    await new Promise(res => setTimeout(res, 0));
  };
  const logOpenTimes = (label: string) => {
    const o = openTimesRef.current;
    if (!o) return;
    openTimesRef.current = null;
    let prev = o.t0;
    const parts = o.marks.map(([phase, at]) => { const s = `${phase} ${((at - prev) / 1000).toFixed(1)} s`; prev = at; return s; });
    console.info(`[SDMS] opened ${label || 'sheet'} in ${((performance.now() - o.t0) / 1000).toFixed(1)} s — ${parts.join(' · ')}`);
  };
  useEffect(() => {
    if (!retrieving) return;
    setPrepStage('data'); setPrepPct(null);
    if (!openTimesRef.current) openTimesRef.current = { t0: performance.now(), marks: [] };
  }, [retrieving]);
  /** A recalculation after the load (an edit, an import) — the chip shows once it has run 1 s. */
  const [recalc, setRecalc] = useState<{ pct: number | null } | null>(null);
  const recalcTimerRef = useRef<number | null>(null);
  const recalcPctRef = useRef<number | null>(null);
  const recalcShownRef = useRef(false);
  useEffect(() => () => { if (recalcTimerRef.current != null) window.clearTimeout(recalcTimerRef.current); }, []);
  /** Import Files progress panel (SdmsImportProgress, user 2026-10-05) — null when no import is running. */
  const [importProg, setImportProg] = useState<ImportProgress | null>(null);
  /** Move the import panel to `step` and yield one macrotask so it paints before the next (main-thread heavy) step. */
  const importStep = async (step: ImportStep, extra?: Partial<ImportProgress>): Promise<void> => {
    setImportProg(p => (p ? { ...p, ...extra, step, pct: null } : p));
    await new Promise(res => setTimeout(res, 0));
  };
  const onSheetRecalc = (pct: number | null, done: boolean) => {
    // During an import the panel shows the calculation: the refresh step ends in the full calculation of
    // replaceSheets, so its first count moves the panel to the calc step. Calculations started by the value
    // writes (write step) are not the final one and are not shown.
    if (!done) setImportProg(p => (p && (p.step === 'calc' || p.step === 'refresh')
      ? (p.step === 'calc' && p.pct === pct ? p : { ...p, step: 'calc', pct }) : p));
    if (done) {
      if (recalcTimerRef.current != null) { window.clearTimeout(recalcTimerRef.current); recalcTimerRef.current = null; }
      recalcShownRef.current = false;
      setRecalc(null);
      return;
    }
    recalcPctRef.current = pct;
    if (recalcShownRef.current) { setRecalc(r => (r && r.pct === pct ? r : { pct })); return; }
    if (recalcTimerRef.current == null) recalcTimerRef.current = window.setTimeout(() => {
      recalcTimerRef.current = null;
      recalcShownRef.current = true;
      setRecalc({ pct: recalcPctRef.current });
    }, 1000);
  };
  // The OK→SDMS handoff batch is saved FROM here (OG NewABID). Once SDMS Save
  // creates it, these hold the minted Oid/QB so audit/results/notes light up and
  // the header shows the real QC Batch ID.
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const [savedOid, setSavedOid] = useState<string | null>(null);
  // Import Files: OG's "Import Instrument File" dialog (open = staging files), and the raw files imported
  // into a batch that has no row yet — archived against it right after the first Save creates it.
  const [importOpen, setImportOpen] = useState(false);
  const [importTransferNames, setImportTransferNames] = useState<string[]>([]);   // the dialog's "Name" list
  // Each with the sample the import matched it to (MultipleInstruments templates — OG stamps it on the
  // RawDataAttachment, :7491), so the reopen's Raw Data summary / Sequence DataFile can find it.
  const pendingRawFilesRef = useRef<Array<{ file: File; sysSampleCode: string | null }>>([]);
  // Raw files the first Save could not attach, kept for THAT batch: its next Save attaches them (never a batch
  // opened later — the batch Oid is checked).
  const rawRetryRef = useRef<{ abOid: string; files: Array<{ file: File; sysSampleCode: string | null }> } | null>(null);
  // The loaded batch's merge inputs + the imports applied to its records since (OG's dtsample), so an
  // import can re-merge the Sequence sheet and rebuild the Raw Data summary as OG does. Only valid while
  // its `records` are still the loaded ones (engineRecordsRef) — see sdmsImportSequence.ts.
  const sampleMergeRef = useRef<{
    template: ArrayBuffer; header: Record<string, unknown>; records: Record<string, unknown>[];
    reagents: Record<string, unknown>[]; instruments: Record<string, unknown>[];
    traceability: string | null; model: number; entryFields: readonly string[] | null;
    continuousHeaderFields: readonly string[] | null;
    overlay: ImportOverlay;
  } | null>(null);
  // An instrument import (result files, not a calibration) ran since the sheet was merged: OG's import then reloads
  // each row's own Testparameter SigFig / CutOff (Dc_AcceptingImport :11970), so the first save must not copy the
  // rowset's (THM comparison 2026-09-29). Sent with the save; cleared when a batch is merged.
  const instrumentImportedRef = useRef(false);
  // Unsaved-changes guard: set true only when the analyst INTERACTIVELY edits a
  // sheet cell (armed by onUserEditStart), cleared on a successful persist. Gating
  // on a real user edit-start keeps programmatic writes — the load-time formula
  // compute, Engine B recompute, Apply Rounding overlays — from ever marking the
  // sheet dirty, so leaving a freshly-opened/viewed sheet never warns.
  const [dirty, setDirty] = useState(false);
  const userEditingRef = useRef(false);
  // A freshly-loaded sheet is clean until the analyst edits it — reset the guard
  // whenever a new batch buffer loads (Retrieve / mode switch / re-merge).
  useEffect(() => { setDirty(false); userEditingRef.current = false; }, [batchBuf]);

  // Top-bar title is ALWAYS "SDMS" while this screen is visible, however it was
  // opened — from a QC Batch, the QC Batches list (autoOpenSdms), or the SDMS
  // ribbon nav. Overrides the host page's / QC Batch's title and reverts on exit.
  // (User UI feedback 2026-09-16.)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: 'SDMS' }));
    return () => { window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: null })); };
  }, []);
  // OG SpreadsheetEntryController :2283-2293 — the "Data Package" ribbon button is shown only when the
  // global ResultEntryDefaultSettings.DataPackageValue = Yes. Default true (fail-open) until the
  // background fetch resolves, so the worst case is a brief flash-then-hide, never a blocked feature.
  const [dataPackageEnabled, setDataPackageEnabled] = useState<boolean>(true);
  useEffect(() => {
    let alive = true;
    void qcBatchApi.sdmsDataPackageEnabled()
      .then((v) => { if (alive) setDataPackageEnabled(v); })
      .catch(() => { /* keep the button available if the settings read fails */ });
    void qcBatchApi.sdmsReagentEntryEnabled()
      .then((v) => { if (alive) reagentEntryRef.current = v; })
      .catch(() => { /* fail-open: reagents stay editable, as OG with no settings row */ });
    return () => { alive = false; };
  }, []);
  // Set once Submit succeeds — advances the stepper's final step to done.
  // Only the setter is used now (drops to View on Submit); the flag's sole reader was
  // the removed workflow stepper. (2026-09-16)
  const [, setSubmitted] = useState(false);
  const [savedQb,  setSavedQb]  = useState<string | null>(null);
  // Where the merge put each header FIELD (`Sheet!A1`). QCBATCHID is merged BLANK
  // because the id does not exist until the save mints it, so persistWorkbook
  // stamps it into this cell before upload (client 2026-08-19).
  const fieldRefsRef = useRef<Record<string, string>>({});
  /** Bumped by each loadBatchSheet (and the seeded load's cleanup): a load whose number is no longer current is
   *  stale and must not touch the screen. */
  const loadSeqRef = useRef(0);
  // OG's ribbon Test + QCBatchID are COMBOs, not text boxes (SpreadsheetEntryController
  // :2249-2254 adds the SampleID/Test/QCBatchID combos to the SDMS tab). Cascade:
  // pick a Test → its QC batches load → pick one → Retrieve opens it.
  const [testList,  setTestList]  = useState<SdmsTestLookup[]>([]);
  const [testSel,   setTestSel]   = useState('');   // testMethodId (the real key)
  const [testText,  setTestText]  = useState('');   // what the closed combo shows
  const [testTpl,   setTestTpl]   = useState<number | null>(null); // its SDMS TemplateID
  const [batchList, setBatchList] = useState<SdmsQcBatchLookup[]>([]);
  const [batchSel,  setBatchSel]  = useState('');   // uqAnalyticalBatchID
  const [batchText, setBatchText] = useState('');
  // Date Filter (ribbon combo, default 3M) — limits the QCBatchID list to batches created
  // within the chosen window. 'All' = no limit. Value → months for the sproc's @Months.
  const [dateFilter, setDateFilter] = useState<'1M' | '3M' | '6M' | '1Y' | 'All'>('3M');
  const dateFilterMonths = ({ '1M': 1, '3M': 3, '6M': 6, '1Y': 12, All: null } as const)[dateFilter];

  const s: SdmsEntrySeed = seed ?? { test: '', runs: [] };
  const qb  = savedQb || s.qcBatchId || 'Autogenerate (Save to create QB)';
  // Nav-opened (no seed) → blank canvas until Retrieve; seeded → the batch's
  // workbook auto-loads. Both render the ONE Univer control (no template table).
  const blank = !seed;

  // OG enable/visibility rules (SpreadsheetEntryController modechange :16870+):
  //  • sheet editable in Enter mode, or in View mode while "Edit" is active;
  //  • Audit Trail shows in VIEW mode only (hidden in Enter);
  //  • batch-dependent actions (Submit/QC Batch/Data Package/Report/RollBack …)
  //    need a saved batch;  RollBack is View-only, Save is Enter/edit.
  // The batch these read-only actions target: the seeded QC batch, or the one
  // picked from the QCBatchID combo when nav-opened.
  const effBatchOid   = s.qcBatchOid || savedOid || batchSel;   // Oid (audit / detail key)
  // Ribbon "Project Notes (N)" — OG sets the item text to the batch's note count whenever the
  // batch is loaded (SpreadsheetEntryController :17824, SetCriteria(strAB, "SDMS Result Entry")
  // .GetCount()). Same batch-scoped feed the Project Notes popup lists (useNotesCount → the
  // result-validation project-notes route), auto-refreshed on add/edit/delete via the shared bump,
  // so the badge and the popup can never disagree. (user request 2026-09-22)
  const notesCount = useNotesCount(effBatchOid ?? '');
  const effBatchLabel = savedQb || s.qcBatchId || batchText || effBatchOid; // display "QB…"
  const effTest       = s.test       || testText;
  const hasBatch  = !!effBatchOid;
  // Role-Based MAC for this form (NAV_VIEW_MAPPING['sdms'] = ["SDMS"]). Folding
  // perm.write into sheetEdit is the ONE lever that makes a read-only role safe:
  // <SdmsUniverSheet editable={sheetEdit}> then renders the grid READ-ONLY (so the
  // cells cannot be typed into), and the ribbon's Recalculate / Import Files /
  // Apply Rounding / Qualifiers are already gated on !sheetEdit. The remaining
  // write actions are hidden individually below. Falls open for admins / no-MAC.
  // (owner report 2026-10-26: a Read-only SDMS grant still had the full ribbon
  //  AND typeable cells, because this page had no MAC gating at all.)
  const perm = useNavActions('sdms');
  const sheetEdit = (mode === 'Enter' || editing) && perm.write;
  useUnsavedGuard(dirty);
  const guardedBack = async () => { if (await confirmDiscardIfUnsaved()) onBack?.(); };

  // Imperative handle on the Univer sheet — Save reads the edited workbook through it.
  const sheetRef = useRef<SdmsUniverHandle | null>(null);

  // Calibration mode (New Calibration): keeps the merge context so Save can read each
  // Level sheet's entered values back (matched to the parameter identity keys) and post
  // them to the calibration writer. Cleared when a real QC batch is loaded/cleared.
  const [calibMode, setCalibMode] = useState(false);
  const calibCtxRef = useRef<{ templateId: number; testMethodId: string; fieldCells: Record<string, string>;
    records: Record<string, unknown>[] } | null>(null);
  const calibSavedRef = useRef<string | null>(null);
  // Attach Calibration (OG Dc_AcceptingAttachCalibation): the calibration whose sheet(s)
  // are embedded in the CURRENT batch, awaiting Save (persists the FK). Cleared on load/clear.
  const attachedCalibRef = useRef<{ oid: string; calibrationId: string | null } | null>(null);
  /** The loaded template's Calibration-sheet field cells (CALIBRATIONID G2 …) — a Save that creates the batch's
   *  calibration writes its new ID there (OG CalibAndABIDSupport after NewCBID). */
  const calibrationCellsRef = useRef<Record<string, string> | null>(null);
  // Set before an attach re-mount so onReady re-marks the workbook dirty (the [batchBuf]
  // effect at :244 resets dirty=false on every buffer change, clobbering an inline setDirty).
  const attachDirtyRef = useRef(false);

  // Open-existing calibration picker (OG SDMS 'Attach Calibration'): the saved
  // calibrations for the selected test; opening one re-merges its saved rows read-only.
  const [calibListOpen, setCalibListOpen] = useState(false);
  const [calibList, setCalibList] = useState<Array<{ oid: string; calibrationId: string | null;
    createdDate: string | null; createdByName: string | null; status: number | null }>>([]);

  // Qualifier picker (OG SDMS ribbon Qualifier -> Qualifiers popup): writes the picked
  // symbols into the cell the analyst selected. The picker component already exists.
  const [qualOpen, setQualOpen] = useState(false);
  // Data Package (OG SDMS ribbon): create the batch's DataPackage record, then open the review view.
  const [pkgOpen, setPkgOpen] = useState(false);
  // Set when the batch is submitted from INSIDE the Data Package popup while SDMS is in Enter mode; read when
  // the popup closes to leave SDMS for the Analysis Queue (OG DataPackageViewController.View_Closing, 2026-10-20).
  const submittedInPkgRef = useRef(false);
  // The batch's real header (Status / Created date / identity), fetched when the Data Package opens
  // so the shared SdmsPackageDetail shows the same Batch Information as Result Validation.
  const [pkgHeader, setPkgHeader] = useState<QcBatchHeader | null>(null);
  // The DataPackage's own workflow Status (from create-or-find) → drives Submit vs Validate in the popup.
  const [pkgStatus, setPkgStatus] = useState<number | null>(null);
  // OG Data Package header (persisted, from create-or-find): the QC batch's creator +
  // the package's create date. Shown in the popup header (not current-user/today).
  const [pkgCreated, setPkgCreated] = useState<{ by: string | null; date: string | null }>({ by: null, date: null });
  const [qualInitial, setQualInitial] = useState('');
  const qualTargetRef = useRef<{ sheetName: string; row: number; col: number } | null>(null);

  // Apply Rounding (OG SDMS ribbon): wraps each Result cell's formula as =_LIMSRound(...) using
  // the row's parameter settings (which the merge records carry). Needs the merge context.
  // Apply Rounding fetches its context on demand (works for a freshly-merged OR a retrieved batch),
  // so it only needs the batch's test method + template here.
  const roundMetaRef = useRef<{ testMethodId: string; templateId: number } | null>(null);
  // The PRISTINE template blob + Model of the sheet currently loaded — OG Recalculate re-pushes
  // every template formula from it (:26057). Set at each main-sheet merge; null before a load.
  const templateBufRef = useRef<{ buf: ArrayBuffer; model: number; mainName?: string } | null>(null);
  /** The template's main (index-0) worksheet name -- OG's Model-0 target for Apply Rounding /
   *  Recalculate. Read once from the blob's xl/workbook.xml and cached on templateBufRef. */
  const templateMainSheetName = async (): Promise<string | null> => {
    const t = templateBufRef.current;
    if (!t) return null;
    if (t.mainName !== undefined) return t.mainName;
    try {
      const JSZip = (await import('jszip')).default;
      const zip = await JSZip.loadAsync(t.buf);
      const xml = (await zip.file('xl/workbook.xml')?.async('string')) ?? '';
      const m = /<sheet\b[^>]*\bname="([^"]*)"/.exec(xml);
      const dec = (v: string) => v.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'");
      t.mainName = m ? dec(m[1]!) : '';
    } catch { t.mainName = ''; }
    return t.mainName;
  };
  /** OG's sheet selection for Apply Rounding / Recalculate (see resultSheetSelector). Run codes
   *  come from the merge records (Model 1 names its per-run sheets by SysSampleCode). */
  const resultSheetFilter = async (): Promise<(sheetName: string) => boolean> => {
    const t = templateBufRef.current;
    const runs = runCodesOf(engineRecordsRef.current);
    return resultSheetSelector(t?.model ?? 0, await templateMainSheetName(), runs);
  };
  // Engine B (server-side SDMS Formula Engine) gate + inputs. `engineBRef` is set on load from
  // GET sdms/has-formulas — TRUE only for templates whose SDMS Formula Setup has runnable (Fixed)
  // bindings; when false (the common case today) every Engine-B path below is skipped and the
  // sheet keeps its own Univer formulas untouched. `engineRecordsRef` keeps the load-time merge
  // records so runEngineBCompute can assemble the compute payload without a re-fetch.
  const engineBRef = useRef(false);
  const engineRecordsRef = useRef<Record<string, unknown>[]>([]);
  // On-edit Engine B recompute (bound templates only): SdmsUniverSheet fires onCellEdited →
  // we debounce, overlay the edited sheet values, and call recompute-sample-group. The pending
  // set accumulates edited (sheet,row) across the debounce window; the guard ref suppresses the
  // recompute's OWN write-back (which also raises SheetValueChanged) from re-triggering.
  const engineEditTimer = useRef<number | null>(null);
  const qualifierNotesTimer = useRef<number | null>(null);
  /** 0-based columns of the Engine B OUTPUT fields (Result / %Recovery / RPD / QCStatus), from the
   *  template's full FIELD→A1 map (loadEngineLookups) — a write there never triggers a recompute. */
  const engineOutColsRef = useRef<Set<number>>(new Set());
  const engineRecomputingRef = useRef(false);
  /** Whether the QC hover notes have been placed (after a Save / Recalculate) — an on-edit recompute rebuilds them. */
  const qcNotesShownRef = useRef(false);
  const enginePendingEditsRef = useRef<Set<string>>(new Set());
  // Dilution IsReport recompute (OG ApplyDilutionIsReport) — applies to ALL templates (not just
  // Engine-B ones): pick the least-dilute in-range run as the reportable row. Run ONLY after an import and
  // from the right-click "Apply Dilution Rule" (OG :5351) — never on load or on an edit, so what the analyst
  // sets stays (owner decision 2026-10-22). `dilutionColsRef` caches the 0-based NumericResult/DF/ISReport
  // columns (non-null = the template can take the rule).
  const dilutionColsRef = useRef<{ numericResult: number; df: number; isReport: number } | null>(null);
  useEffect(() => () => {
    if (engineEditTimer.current) window.clearTimeout(engineEditTimer.current);
  }, []);
  // OG's in-memory isRoundingApplied (set by Apply Rounding, cleared by Recalculate). Nothing
  // READS it today — OG's "submit without rounding?" warning is commented out (:8455) and its
  // ribbon button has no pressed state — so only the setter is bound. (2026-09-22)
  const [, setRoundingApplied] = useState(false);

  /** Persist the analyst's typed results: pull the edited .xlsx out of Univer and
   *  store it against the batch. Legacy's SDMS Save writes exactly this blob
   *  (`ABID.SpreadSheet = …SaveDocument(OpenXml)`) and creates NO structured result
   *  rows — those are the Submit phase — so blob-only is the faithful scope here.
   *  Returns false (having explained why) when there is nothing to persist to. */
  /** Every header cell the merge put FIELD("QCBATCHID") in — one per sheet (VOC 8086 repeats it at J5 on each run
   *  sheet), 0-based. */
  const qcBatchIdCells = (): Array<{ sheetName: string; row: number; col: number }> => {
    const out: Array<{ sheetName: string; row: number; col: number }> = [];
    for (const ref of fieldRefCells(fieldRefsRef.current, 'QCBATCHID')) {
      const [sheetName, a1ref] = ref.split('!');
      const m = /^([A-Z]+)(\d+)$/.exec(a1ref ?? '');
      if (!sheetName || !m) continue;
      let col = 0;
      for (const ch of m[1]!) col = col * 26 + (ch.charCodeAt(0) - 64);
      out.push({ sheetName, row: parseInt(m[2]!, 10) - 1, col: col - 1 });
    }
    return out;
  };

  /** Attach instrument files imported before the batch existed (OG writes RawDataAttachment at Save time).
   *  Each upload is tried twice; a file that still fails is kept for this batch and attached by its next Save,
   *  so a network hiccup never loses it. Best-effort: the batch and the imported values are already saved. */
  const attachRawFiles = async (abOid: string, files: Array<{ file: File; sysSampleCode: string | null }>): Promise<void> => {
    const failed: typeof files = [];
    for (const item of files) {
      let attached = false;
      for (let attempt = 1; attempt <= 2 && !attached; attempt++) {
        try {
          const warning = await qcBatchApi.archiveInstrumentRawFile(abOid, item.file, item.sysSampleCode);
          if (warning) notify.warning(warning);   // the server's own reason (a gate) — not retried
          attached = true;
        } catch (e) {
          console.warn(`[SDMS] raw file attach failed (attempt ${attempt})`, item.file.name, e);
        }
      }
      if (!attached) failed.push(item);
    }
    if (!failed.length) return;
    rawRetryRef.current = { abOid, files: [...(rawRetryRef.current?.abOid === abOid ? rawRetryRef.current.files : []), ...failed] };
    notify.warning(`The raw file ${failed.map(x => `"${x.file.name}"`).join(', ')} could not be attached yet. It will be attached when you Save again.`);
  };

  /** A Save is running. The sheet's own writes during it (the rounding wraps, the engine write-back) raise the same
   *  cell-changed events as typing, and on VOC a wrapped Result column counts as a NumericResult edit — so the
   *  dilution IsReport rule woke up 650 ms in and exported the whole workbook a second time in parallel with the
   *  save's own calculation and export (~1 s each on VOC, 2026-10-09). Its write-back would land after the save
   *  took its copy of the sheet anyway, so it is not scheduled while this is set. */
  const savingRef = useRef(false);
  const persistWorkbook = async (batchOid: string, qcBatchId?: string): Promise<boolean> => {
    savingRef.current = true;
    try { return await persistWorkbookInner(batchOid, qcBatchId); }
    finally { savingRef.current = false; }
  };

  const persistWorkbookInner = async (batchOid: string, qcBatchId?: string): Promise<boolean> => {
    // Engine B on SAVE (OG runs FormulaEngineHost Execute → ApplyEngineRounding on every save path
    // when HasFormulas): recompute the engine columns from the analyst's CURRENT inputs and stamp
    // them into the live Univer model NOW, so the getEditedXlsx below serialises the engine's values
    // into the persisted blob. A pure no-op for the unbound templates that are the norm today.
    // OG rounds first (ApplyRoundingOnsave, :5877) and runs the formula engine after (:6700).
    // ONE full calculation covers both (speed, 2026-10-09): the engine writes VALUES one cell at a time, each queueing a
    // partial calculation that the export below used to run into (0.9 s against 0.26 s idle on Alkalinity 24 rows), and a
    // second full pass after the rounding one cost ~1 s more. Rounding only rewrites Result / %Recovery / RPD, which the
    // engine never reads (its inputs are NumericResult / DF / SpikeAmount), so it can run before the calculation.
    // Commit an open cell editor ONCE, up front: every later step reads the live sheet, and each of their own commits
    // waited 150 ms (four on an Engine B save) for an editor that is no longer open.
    await sheetRef.current?.commitEditor?.();
    const engineOnSave = engineBRef.current;
    const needsFullCalc = await applyRoundingOnSave({ deferRecalc: engineOnSave });
    const engineWrote = await runEngineBOnSave();
    // Without new rounding wraps (a re-save) the engine's own partial calculation is enough — wait for it to settle
    // rather than recalculating the whole workbook, so the export reads the dependents of what the engine wrote.
    if (engineOnSave && needsFullCalc) await sheetRef.current?.recalc({ commitEditor: false });
    else if (engineWrote) await sheetRef.current?.whenCalcIdle();
    const edited = await sheetRef.current?.getEditedXlsx({ commitEditor: false });
    if (!edited) {
      // No sheet mounted / nothing loaded — don't silently claim a save happened.
      await confirm.alert({ title: 'Save', tone: 'warning',
        message: 'There is no loaded spreadsheet to save. Retrieve a batch first.' });
      return false;
    }
    // Stamp the QC Batch ID into the cell the merge left blank. ALPACA gets away
    // without this because its SDMS screen RE-MERGES from the template on Retrieve,
    // resolving FIELD("QCBATCHID") once the id exists; we render the STORED sheet, so
    // whatever the merge missed stays missing. Surgical, like every other write here —
    // one cell, nothing else in the package touched.
    let bytes = edited.bytes;
    // The per-cell edits the sheet diffed (old→new, keyed SysSampleCode|Parameter|Field).
    // Sent to the save so each changed cell is AUDITED against the batch. The QCBATCHID
    // system stamp below is NOT in this list, so it is never mistaken for an analyst edit.
    const cellChanges = edited.changes;
    const qbNow = qcBatchId ?? savedQb ?? s.qcBatchId ?? '';
    const qbCells = qcBatchIdCells();
    if (qbNow && qbCells.length) {
      const edits: Record<string, Array<{ row: number; col: number; value: string }>> = {};
      for (const c of qbCells) (edits[c.sheetName] ??= []).push({ row: c.row, col: c.col, value: qbNow });
      try {
        bytes = await patchWorkbookCells(bytes, edits);
      } catch (e) {
        // Non-fatal: the sheet still saves, the header cell just stays blank.
        console.warn('[SDMS] QCBATCHID stamp failed', e);
      }
    }
    // OG's saved workbook has the two summaries, Raw Data and (by setting) Reagents / Instruments PROTECTED; AI's
    // had none (OG vs AI comparison 2026-09-28, point 12). Persist-only: the view keeps its own locks.
    try { bytes = await protectSheetsLikeOg(bytes, [...OG_PROTECTED_SUMMARY_SHEETS, ...lockedSheets]); }
    catch (e) { console.warn('[SDMS] sheet protection skipped', e); }
    // Attach Calibration: when a calibration is attached (Stage 1 embedded its sheet(s) into the
    // blob), link it to the batch so a reopen re-embeds it (AnalyticalBatch.Calibration FK).
    // Values this session's imports gave rows that no sheet cell shows (VOC USERDEFINED3) — OG stores them too.
    const sm = sampleMergeRef.current;
    const importValues = sm && sm.records === engineRecordsRef.current ? importValuesToSave(sm.records, sm.overlay) : [];
    const saved = await qcBatchApi.saveSdmsEntryWorkbook(batchOid, bytes, cellChanges, attachedCalibRef.current?.oid ?? null, importValues,
      instrumentImportedRef.current);
    // The save created (or found) the batch's calibration: show its ID on the Calibration sheet, as OG's
    // CalibAndABIDSupport does after NewCBID. A system write, so it is not an analyst edit; it is stored with
    // the next save, and every open writes it again from the batch's calibration.
    const calCell = saved?.calibrationId ? calibrationIdCell(calibrationCellsRef.current) : null;
    if (calCell && saved?.calibrationId && sheetRef.current?.getCellValue(calCell.sheetName, calCell.row, calCell.col) !== saved.calibrationId)
      sheetRef.current?.setCellValue(calCell.sheetName, calCell.row, calCell.col, saved.calibrationId, undefined, { text: true, system: true });
    setDirty(false); userEditingRef.current = false;   // persisted — clear the unsaved-changes guard
    // OG adds the hover notes after a Save that ran the Formula Engine (bound templates) — display-only,
    // added AFTER the bytes were taken so they never reach the stored blob.
    if (engineBRef.current) refreshQcNotes();
    void refreshQualifierNotes();
    return true;
  };

  // Save a NEW calibration (calibration mode): read each Level sheet's entered readings
  // back (matched to the parameter identity keys captured at merge time) and POST them +
  // the .xlsx blob to the race-safe calibration writer. Create => reason-exempt.
  const doSaveCalibration = () => {
    const ctx = calibCtxRef.current;
    if (!ctx || !ctx.templateId || !ctx.testMethodId) { notify.warning('Start a calibration first.'); return; }
    return runSave({
      save: async () => {
        const edited = await sheetRef.current?.getEditedXlsx();
        if (!edited) throw new Error('There is no calibration sheet to save.');
        const rows = await extractCalibrationRows(edited.bytes, ctx.fieldCells, ctx.records);
        if (!rows.length) throw new Error('No calibration rows found on the Level sheets.');
        const res = await qcBatchApi.sdmsCalibrationSave({
          templateId: ctx.templateId, testMethodId: ctx.testMethodId,
          matrixName: s.matrix ?? null, instrument: s.instrument ?? null,
          workbookBase64: arrayBufferToBase64(edited.bytes), rows,
        });
        calibSavedRef.current = res.calibrationId;
      },
      successDialog: () => ({
        type: 'success',
        title: 'Calibration Saved',
        idLabel: 'Calibration ID',
        idValue: calibSavedRef.current ?? '',
        rows: [{ label: 'Test', value: effTest || '—' }],
        note: 'The calibration set has been created.',
      }),
      formName: 'SDMS Calibration',
      auditAction: 'create',
    });
  };

  // Save (Enter mode) — OG NewABID: the AnalyticalBatch is created FROM SDMS. The
  // QC Batch form handed over the built payload WITHOUT saving (OG defers the save
  // here), so this is where it persists — via the SAME race-safe create the
  // plain-batch OK uses. Shared save flow: confirm → audited create → milestone
  // success (minted QC Batch ID). Nav-opened Enter has no staged batch, so Save
  // there stays a stub until Enter-mode result entry lands.
  const doSave = () => {
    if (calibMode) return doSaveCalibration();
    const body = seed?.pendingCreate;
    const existingOid = seed?.qcBatchOid ?? null;

    // ── Case A-update: the QC form handed a STAGED add/remove for an EXISTING batch (OG
    // never commits an SDMS batch's structure from the QC form — it defers to the SDMS Save;
    // QCBatchController :7284-7289 / SpreadsheetEntryController :255-279 / ABCreateUpdate :5421).
    // Reconcile the sequence via updateBatch (added rows inserted incl. their SampleInfo, removed
    // QC GCRecord'd, removed samples unlinked back to the queue), THEN persist the entered
    // results — one atomic SDMS Save. Abandoning SDMS never touched the batch.
    if (body && existingOid && !savedOid) {
      return runSave({
        save: async () => {
          await qcBatchApi.updateBatch(existingOid, {
            roomTemp: body.roomTemp ?? null, humidity: body.humidity ?? null,
            comments: body.comments ?? null, sequence: body.sequence,
          });
          setSavedOid(existingOid);
          try { await persistWorkbook(existingOid); } catch { /* structure saved; results best-effort */ }
        },
        successDialog: () => ({
          type: 'success',
          title: 'QC Batch Updated',
          message: 'The sample/QC changes and your entries have been saved to this QC batch.',
          rows: [
            { label: 'QC Batch', value: effBatchLabel || '—' },
            { label: 'Test',     value: effTest || '—' },
          ],
        }),
        formName: 'QC Batches',
        auditAction: 'edit',
      });
    }

    // ── Case B: the batch already exists (nav-opened + Retrieved, or a second Save
    // after the create below). Persist the typed results onto it. This is what makes
    // Result Entry actually retain work — previously this path was a stub, so every
    // typed value was lost on leaving the screen.
    if (!body || savedOid) {
      const target = savedOid || effBatchOid;
      if (!target) { stub('Save'); return; }   // nothing loaded at all
      return runSave({
        save: async () => {
          await persistWorkbook(target);
          const retry = rawRetryRef.current;
          if (retry?.abOid === target) { rawRetryRef.current = null; await attachRawFiles(target, retry.files); }
        },
        successDialog: () => ({
          type: 'success',
          title: 'Results Saved',
          message: 'The spreadsheet has been saved to this QC batch.',
          rows: [
            { label: 'QC Batch', value: effBatchLabel || '—' },
            { label: 'Test',     value: effTest || '—' },
          ],
          note: 'Reopening this batch will load your entered results.',
        }),
        formName: 'SDMS Result Entry',
        auditAction: 'edit',
      });
    }

    // ── Case A: OG NewABID — the AnalyticalBatch is created FROM SDMS. The QC Batch
    // form handed over the built payload WITHOUT saving (OG defers the save here), so
    // create it via the SAME race-safe create the plain-batch OK uses, THEN persist
    // the workbook onto the batch it just minted (one user action = one Save).
    let minted: QcCreateResult | null = null;
    let sheetSaved = false;
    return runSave({
      save: async () => {
        const r = await qcBatchApi.create(body);
        minted = r; setSavedOid(r.abOid); setSavedQb(r.qcBatchId);
        // The batch is committed at this point regardless of the workbook step below, so tell
        // the parent QC form now — its unsaved guard must stand down even if the sheet fails.
        onCreated?.({ abOid: r.abOid, qcBatchId: r.qcBatchId });
        // Best-effort: the batch is already committed, so a workbook failure must not
        // present as "nothing saved". Reported honestly in the dialog note below.
        try { sheetSaved = await persistWorkbook(r.abOid, r.qcBatchId); } catch { sheetSaved = false; }
        // The merge left the sheet's QC Batch ID header blank (the id did not exist yet); the save stamped the
        // stored workbook — show it on screen as well (user 2026-09-30). A system stamp: never an analyst edit.
        for (const c of qcBatchIdCells()) sheetRef.current?.setCellValue(c.sheetName, c.row, c.col, r.qcBatchId, undefined, { system: true });
        // Files imported before the batch existed: archive them against it now (OG's Save-time
        // RawDataAttachment write). Best-effort — the batch and the imported values are already saved.
        const rawFiles = pendingRawFilesRef.current;
        pendingRawFilesRef.current = [];
        await attachRawFiles(r.abOid, rawFiles);
      },
      successDialog: () => ({
        type: 'success',
        title: 'QC Batch Created',
        idLabel: 'QC Batch ID',
        idValue: minted?.qcBatchId ?? '',
        rows: [
          { label: 'Test', value: effTest || '—' },
          { label: 'Runs', value: String(minted?.seqCount ?? body.sequence.length) },
          { label: 'Samples linked', value: String(minted?.samplesLinked ?? 0) },
          { label: 'Parameters linked', value: String(minted?.parametersLinked ?? 0) },
          { label: 'Spreadsheet', value: sheetSaved ? 'Saved' : 'Not saved' },
        ],
        note: sheetSaved
          ? 'The batch and your spreadsheet are saved. You can now enter and submit results.'
          : 'The batch is saved, but the spreadsheet could not be stored — press Save again.',
      }),
      formName: 'QC Batches',
      auditAction: 'create',
    });
  };

  // "Update" — the View-mode Edit toggle's SECOND click (OG :5667, the `Isedit ==
  // true` branch). OG does: BindSampleToGrid (read the sheet back), write
  // `qC.SpreadSheet = SaveDocument(...)`, re-read the batch's SpreadSheetEntry rows,
  // `ABCreateUpdate(qC, spreadSheets)` to persist the values, CommitChanges, set the
  // sheet ReadOnly again, then show "Updatesuccess".
  //
  // AI's equivalent of that whole middle section is `persistWorkbook` — the PUT
  // routes through SaveSdmsResultEditsAsync, which writes the typed values back to
  // SpreadSheetEntry_SampleInfo and stores the workbook. So Update = the same
  // persistence Save/Case-B already does, PLUS leaving edit mode on success.
  //
  // Previously this button was `setEditing(e => !e)` — "Update" just dropped out of
  // edit mode and DISCARDED the edits, the exact opposite of what it says. (Its
  // Cancel twin was fixed on 2026-08-10; this closes the pair.)
  const doUpdate = () => {
    const target = savedOid || effBatchOid;
    if (!target) {                     // OG "NotDataLoaded"
      void confirm.alert({ title: 'Update', tone: 'warning',
        message: 'There is no loaded spreadsheet to update. Retrieve a batch first.' });
      return;
    }
    return runSave({
      save: async () => { await persistWorkbook(target); },
      successDialog: () => ({
        type: 'success',
        title: 'Updated',
        message: 'The spreadsheet has been updated on this QC batch.',
        rows: [
          { label: 'QC Batch', value: effBatchLabel || '—' },
          { label: 'Test',     value: effTest || '—' },
        ],
      }),
      // Leave edit mode ONLY once the write succeeded (OG sets ReadOnly=true at the
      // same point). A failed save keeps the sheet editable, so the analyst's work
      // is never stranded behind a read-only view.
      onSaved: () => setEditing(false),
      formName: 'SDMS Result Entry',
      auditAction: 'edit',
    });
  };

  // Docked island stepper (issue: it vanished when SDMS opened because the QC
  // page — which rendered the pill — unmounts on the full-page swap). The QC-batch
  // steps are DONE when we arrived through the Analysis Queue → QC Batch flow
  // (seeded), then Save → Submit.
  //
  // Only consumed by <WorkflowStepperBar>, which as of 2026-08-10 renders for
  // SEEDED opens only — so this always describes the "came through the QC flow"
  // case. (Supersedes the 2026-07-06 requirement that a DIRECT nav open start a
  // FRESH pill with Open SDMS active: that pill showed five create-steps as "todo"
  // for a flow the user is not in, so a nav-opened SDMS now shows no stepper.)
  // Delete (legacy SpreadsheetEntry Delete :8963) — REUSES the QC-batch delete
  // (drops SpreadSheetEntry + QC/ISTD/surrogate rows, resets real samples to Pending
  // Entry). Shared danger-confirm + "Deleted." via useDeleteFlow, then clear the screen like OG
  // (:9245-9246 DeleteSuccess → clearcontrols) — it stays on SDMS, it does NOT navigate back.
  const doDelete = () => {
    const target = savedOid || effBatchOid;
    if (!target) { stub('Delete'); return; }
    return runDelete({
      formName: 'QC Batches',
      message: `Delete QC Batch ${effBatchLabel}? Its results are cleared and its samples return to Pending Entry.`,
      delete: () => qcBatchApi.deleteBatch(target).then(() => undefined),
      onDeleted: () => clearControls(),
      onError: (e) => notify.error((e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Deleting the QC batch failed.'),
    });
  };

  // RollBack (legacy SpreadsheetEntry RollBack :8854, View mode) — REUSES the QC-batch
  // rollback: mandatory reason (legacy SDMSRollback popup) → rollback endpoint (the
  // reported-sample guard is enforced server-side, same as the QC Batches list).
  const doRollback = async () => {
    const target = savedOid || effBatchOid;
    if (!target) { stub('Rollback'); return; }
    const reason = await confirm.prompt({
      title: 'QC Batch Rollback', message: 'Enter a reason for the rollback.',
      placeholder: 'Rollback reason…', required: true, confirmLabel: 'Rollback',
    });
    if (reason === null) return;
    try {
      await qcBatchApi.rollback(target, reason);
      notify.success('Rollback completed successfully.');
      onBack?.();
    } catch (e) {
      notify.error((e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Rollback failed.');
    }
  };

  // BindGrid (OG SpreadsheetEntryController :5646) is NOT on this ribbon (removed on user request 2026-09-30). OG
  // needs it to copy the sheet into its in-memory data table before other actions; every AI action reads the live
  // sheet itself, and its remaining effect — a full recalculation — is what Recalculate does.
  // Engine B (server-side SDMS Formula Engine) — for a template with Fixed Formula-Library
  // bindings, compute %Recovery / RPD / QCStatus / Result server-side from the batch's rows and
  // stamp them into the sheet. Gated on engineBRef (a pure no-op for the unbound templates that
  // are the norm today). Called after the sheet paints (onReady) and on Recalculate. Read-only +
  // best-effort: any failure logs and leaves the sheet exactly as-is (never blocks the analyst).
  // Shared Engine B inputs: the template's Field→cell map + per-parameter REC/RPD rounding
  // (from sdms/rounding-settings) and per-QCType root role (from qcTypes). Used by both the
  // full compute (load/Recalculate) and the on-edit sample-group recompute.
  const loadEngineLookups = async (meta: NonNullable<typeof roundMetaRef.current>) => {
    const [rounding, qcTypeList] = await Promise.all([
      qcBatchApi.sdmsRoundingSettings(meta.testMethodId, meta.templateId),
      qcBatchApi.qcTypes(meta.testMethodId).catch(() => []),
    ]);
    if (!rounding.fieldCells) return null;
    const byParam = new Map<string, RoundParamLite>();
    for (const s of rounding.settings) if (s.parameterName)
      byParam.set(s.parameterName.trim().toUpperCase(), {
        recSigFig: s.recSigFig, recCutOff: s.recCutOff, recDecimal: s.recDecimal,
        rpdSigFig: s.rpdSigFig, rpdCutOff: s.rpdCutOff, rpdDecimal: s.rpdDecimal,
      });
    const rootByType = new Map<string, string>();
    for (const t of qcTypeList) if (t.qcTypeName && t.qcRootRole)
      rootByType.set(t.qcTypeName.trim().toUpperCase(), t.qcRootRole);
    // Per-row Result / Rec / RPD settings by parameter + QC type — OG PreloadRoundingSettings, the same
    // resolution Apply Rounding uses, so the engine and Apply Rounding round every row alike.
    const round = buildRoundResolver(rounding.settings);
    // Where the engine may WRITE: its output fields, minus any whose cell the template binds to another
    // field (8078 AM8 = DupSourceResult AND Analyst — OG shows the Analyst there). Inputs keep fieldCells.
    const owned = await ownFieldCells(rounding.fieldCells, templateBufRef.current?.buf);
    // …and only the outputs one of the template's OWN engine formulas computes. HAA 8075's only formula is the
    // earliest Analyzed Date per sample, and its Result is a SHEET formula: writing every output wrote the seeded
    // input over that formula (2026-10-16). Result counts when any formula targets Result or FinalResult; QCStatus
    // when any result / recovery / RPD formula exists — the rows the engine judges.
    const targetList = await qcBatchApi.sdmsEngineTargets(meta.templateId).catch(() => null);
    const targets = new Set((targetList ?? []).map(t => t.trim().toUpperCase()));
    const computes = (field: string): boolean => {
      if (!targetList) return true;   // targets unknown (request failed): write as before rather than nothing
      if (field === 'RESULT') return targets.has('RESULT') || targets.has('FINALRESULT');
      if (field === 'QCSTATUS') return ['QCSTATUS', 'RESULT', 'FINALRESULT', '%RECOVERY', 'RPD'].some(t => targets.has(t));
      return targets.has(field);
    };
    const outCells: Record<string, string> = {};
    for (const [field, cellRef] of Object.entries(owned))
      if (!ENGINE_OUTPUT_FIELD_KEYS.has(field.toUpperCase()) || computes(field.toUpperCase())) outCells[field] = cellRef;
    engineOutColsRef.current = new Set(['RESULT', '%RECOVERY', 'RPD', 'QCSTATUS', 'DUPSOURCERESULT', 'SPIKESOURCERESULT']
      .map(f => parseCellRef(outCells[f] ?? '')?.col).filter((c): c is number => c != null).map(c => c - 1));
    return { fieldCells: rounding.fieldCells, outCells, byParam, rootByType, round };
  };

  const runEngineBCompute = async (): Promise<boolean> => {
    const meta = roundMetaRef.current;
    if (!engineBRef.current || !meta?.templateId || !sheetRef.current) return false;
    const records = engineRecordsRef.current;
    if (!records.length) return false;
    // Suppress the write-back's own SheetValueChanged (like the recompute / on-save paths): without
    // this the on-open compute's stamps were taken for edits and scheduled a recompute of themselves,
    // whose late stamps then landed as "edits" after the analyst's first click (user 2026-09-23).
    engineRecomputingRef.current = true;
    try {
      const edited = await sheetRef.current.getEditedXlsx({ commitEditor: false });
      if (!edited) return false;
      const lk = await loadEngineLookups(meta);
      if (!lk) return false;
      const rows = assembleCalcRows(records, lk.round, lk.rootByType);
      const computed = await qcBatchApi.sdmsCompute(meta.templateId, rows);
      await writeEngineResultsToSheet(edited.bytes, lk.outCells, indexComputed(computed), sheetRef.current);
      return true;
    } catch (e) {
      console.warn('[SDMS] Engine B compute failed — leaving the sheet as-is', e);
      return false;
    } finally {
      window.setTimeout(() => { engineRecomputingRef.current = false; }, 500);
    }
  };

  // On-EDIT recompute (bound templates only). Unlike the full compute above — which recomputes
  // from the load-time merge records — this overlays the analyst's CURRENT sheet inputs
  // (NumericResult / DF / SpikeAmount) onto those records, then calls recompute-sample-group for
  // the edited sample (OG FormulaEngineHost.ExecuteSampleGroup: the engine still needs every row
  // for cross-row Spike/Dup resolution, but only the edited sample + its MS/MSD/DUP dependents are
  // written back). Falls back to a full compute when the debounce window spans >1 sample.
  const runEngineBRecompute = async (): Promise<void> => {
    const meta = roundMetaRef.current;
    if (!engineBRef.current || !meta?.templateId || !sheetRef.current) return;
    const records = engineRecordsRef.current;
    if (!records.length) return;
    const editedCoords = new Set(enginePendingEditsRef.current);
    enginePendingEditsRef.current.clear();
    engineRecomputingRef.current = true;   // suppress the write-back's own SheetValueChanged
    try {
      const edited = await sheetRef.current.getEditedXlsx({ commitEditor: false });
      if (!edited) return;
      const lk = await loadEngineLookups(meta);
      if (!lk) return;
      const parsed = await loadSdmsWorkbook(edited.bytes);   // one parse: the overlay AND the write-back read it
      const { records: overlaid, editedSamples } =
        await overlaySheetInputs(edited.bytes, lk.fieldCells, records, editedCoords, parsed);
      const rows = assembleCalcRows(overlaid, lk.round, lk.rootByType);
      const computed = editedSamples.size === 1
        ? await qcBatchApi.sdmsRecomputeSampleGroup(meta.templateId, rows, [...editedSamples][0]!)
        : await qcBatchApi.sdmsCompute(meta.templateId, rows);
      await writeEngineResultsToSheet(edited.bytes, lk.outCells, indexComputed(computed), sheetRef.current, parsed);
      // Hover notes already on the sheet (after a Save / Recalculate) describe the values the edit just replaced —
      // the Dup RPD note kept "= 0.0 %" next to a recomputed 0.9 (user report 2026-10-09). Rebuild them once the
      // write-back's own calculation has settled; a sheet without notes yet keeps none, as OG.
      if (qcNotesShownRef.current) { await sheetRef.current.whenCalcIdle(); refreshQcNotes(); }
    } catch (e) {
      console.warn('[SDMS] Engine B on-edit recompute failed — leaving the sheet as-is', e);
    } finally {
      // Tail delay so the write-back's trailing SheetValueChanged events are still ignored.
      window.setTimeout(() => { engineRecomputingRef.current = false; }, 500);
    }
  };

  // Engine B on SAVE (OG SpreadsheetEntryController: every save path runs FormulaEngineHost
  // Execute → ApplyEngineRounding when HasFormulas, over the live dtsample). A FULL compute from
  // the analyst's CURRENT sheet inputs — the on-edit path's overlay (NUMERICRESULT/DF/SpikeAmount
  // → the load-time records) but for ALL samples, not one group — so the PERSISTED blob carries
  // the engine's %Recovery / RPD / QCStatus / Result, not just whatever a prior on-edit /
  // Recalculate left (and it covers a Save with no explicit Recalculate). Called synchronously by
  // persistWorkbook BEFORE getEditedXlsx; best-effort; a no-op for the unbound templates that are
  // the norm today. engineRecomputingRef suppresses the write-back's own SheetValueChanged.
  const runEngineBOnSave = async (): Promise<boolean> => {
    const meta = roundMetaRef.current;
    if (!engineBRef.current || !meta?.templateId || !sheetRef.current) return false;
    const records = engineRecordsRef.current;
    if (!records.length) return false;
    engineRecomputingRef.current = true;
    try {
      const edited = await sheetRef.current.getEditedXlsx({ commitEditor: false });   // persistWorkbook committed
      if (!edited) return false;
      const lk = await loadEngineLookups(meta);
      if (!lk) return false;
      const parsed = await loadSdmsWorkbook(edited.bytes);   // one parse: the overlay AND the write-back read it
      const { records: overlaid } = await overlaySheetInputs(edited.bytes, lk.fieldCells, records, new Set(), parsed);
      const rows = assembleCalcRows(overlaid, lk.round, lk.rootByType);
      const computed = await qcBatchApi.sdmsCompute(meta.templateId, rows);
      return await writeEngineResultsToSheet(edited.bytes, lk.outCells, indexComputed(computed), sheetRef.current, parsed) > 0;
    } catch (e) {
      console.warn('[SDMS] Engine B on-save compute failed — persisting the sheet as-is', e);
      return false;
    } finally {
      window.setTimeout(() => { engineRecomputingRef.current = false; }, 500);
    }
  };

  // Cache the 0-based NumericResult / DF / ISReport columns from the template's Field→cell map, so
  // the edit handler can cheaply decide whether an edit is dilution-relevant. Best-effort.
  const loadDilutionCols = async (meta: NonNullable<typeof roundMetaRef.current>): Promise<void> => {
    try {
      const lk = await loadEngineLookups(meta);
      if (!lk) { dilutionColsRef.current = null; return; }
      const col0 = (a1?: string): number => {
        const m = /^([A-Z]+)\d+$/.exec((a1 ?? '').toUpperCase());
        if (!m) return -1;
        let c = 0; for (const ch of m[1]!) c = c * 26 + (ch.charCodeAt(0) - 64);
        return c - 1;
      };
      const fc = lk.fieldCells;
      dilutionColsRef.current = {
        numericResult: col0(fc.NUMERICRESULT), df: col0(fc.DF ?? fc.DILUTION), isReport: col0(fc.ISREPORT),
      };
    } catch { dilutionColsRef.current = null; }
  };

  // Dilution IsReport recompute (OG SpreadsheetEntryController.ApplyDilutionIsReport :22740):
  // pick the least-dilute in-range run of each sample×parameter (or QC lineage) as the ONE
  // reportable row and set every other run False. Reads live NumericResult/DF off the sheet +
  // the static identity fields from the merge records, then writes True/False into the ISReport
  // column. Runs for ALL templates (not gated on Engine B). Best-effort — a failure leaves the
  // sheet as-is. The guard ref suppresses the rule's OWN IsReport write-back from re-triggering.
  /** Resolves to the number of ISReport cells it changed (0 when there is nothing to do), or null when the
   *  template / batch cannot take the rule (no merge records, no template, no ISReport column). */
  const runDilutionIsReport = async (): Promise<number | null> => {
    if (!sheetRef.current) return null;
    const records = engineRecordsRef.current;
    if (!records.length) return null;                  // no merge records → no dilution grouping
    const meta = roundMetaRef.current;
    if (!meta?.templateId) return null;
    try {
      const edited = await sheetRef.current.getEditedXlsx({ commitEditor: false });
      if (!edited) return null;
      const lk = await loadEngineLookups(meta);
      if (!lk?.fieldCells?.ISREPORT) return null;      // template has no ISReport column → nothing to do
      const parsed = await loadSdmsWorkbook(edited.bytes);   // one parse: the rows AND the write-back read it
      const rows = await assembleDilutionRows(edited.bytes, lk.fieldCells, records, parsed);
      if (rows.length === 0) return null;
      const reportable = computeDilutionIsReport(rows, isDFAdjustedFromRecords(records));
      return await writeDilutionIsReportToSheet(edited.bytes, lk.fieldCells, reportable, sheetRef.current, parsed);
    } catch (e) {
      console.warn('[SDMS] dilution IsReport recompute failed — leaving the sheet as-is', e);
      return null;
    }
  };

  // Right-click "Apply Dilution Rule" (OG SDMS context menu, SpreadsheetEntryController :2124 → ApplyDilutionIsReport):
  // run the rule on demand over the live sheet. It writes the ISReport column, so it needs Enter mode, and the
  // changed flags are unsaved work like any other edit (rule 15). User request 2026-10-22.
  const applyDilutionRuleFromMenu = (): void => {
    if (!sheetEdit) { notify.info('Switch Mode to Enter to apply the dilution rule.'); return; }
    void runLoading(runDilutionIsReport(), { label: 'Applying the dilution rule…' }).then(changed => {
      if (changed === null) { notify.warning('The dilution rule cannot be applied to this sheet (no batch loaded, or the template has no IsReport column).'); return; }
      if (changed > 0) setDirty(true);
      notify.success(changed > 0 ? `Dilution rule applied — ${changed} IsReport value(s) changed.` : 'Dilution rule applied — no change needed.');
    });
  };
  const applyDilutionRuleFromMenuRef = useRef(applyDilutionRuleFromMenu);
  applyDilutionRuleFromMenuRef.current = applyDilutionRuleFromMenu;
  const sheetContextMenu = useMemo(() => [{ id: 'sdms.contextMenu.apply-dilution-rule', title: 'Apply Dilution Rule',
    run: () => applyDilutionRuleFromMenuRef.current() }], []);

  // SdmsUniverSheet edit callback. The Engine-B on-edit recompute runs only for bound templates.
  // The dilution IsReport rule does NOT run on an edit any more (owner decision 2026-10-22, OG parity
  // SpreadsheetEntryController :5351): what the analyst sets stays. It runs after an import and from the
  // right-click "Apply Dilution Rule" only.
  const onSheetCellEdited = (edits: Array<{ sheetName: string; row: number; col: number }>): void => {
    // Only a value change that FOLLOWS a real user edit-start counts as unsaved —
    // programmatic writes (load compute, recompute, rounding) leave dirty alone.
    if (userEditingRef.current) setDirty(true);
    // Any value change → rebuild the Qualifier hover notes (debounced). Not filtered to the Qualifier
    // column: the event's reported column did not always match the merge's Qualifier anchor, so a new
    // qualifier got no note (user 2026-09-24); the rebuild is one display read per result sheet.
    if (qualifierNotesTimer.current) window.clearTimeout(qualifierNotesTimer.current);
    qualifierNotesTimer.current = window.setTimeout(() => { void refreshQualifierNotes(); }, 400);
    if (!engineBRef.current || engineRecomputingRef.current) return;
    // Only an INPUT change needs the engine. A write into an OUTPUT column (Result / %Recovery / RPD /
    // QCStatus) — Apply Rounding's _LIMSRound wraps, a Qualifier-side stamp — must not trigger a
    // recompute: that stamped the engine's values back over the wraps ~0.6 s later ("the rounding
    // changes for a second and returns back", user 2026-09-24).
    const inputEdits = edits.filter(e => !engineOutColsRef.current.has(e.col));
    if (!inputEdits.length) return;
    for (const e of inputEdits) enginePendingEditsRef.current.add(`${e.sheetName}${EDIT_KEY_SEP}${e.row}`);
    if (engineEditTimer.current) window.clearTimeout(engineEditTimer.current);
    engineEditTimer.current = window.setTimeout(() => { void runEngineBRecompute(); }, 600);
  };

  // OG AddQcCellComments (2026-09-23 build): hover notes explaining Result / %Recovery / RPD, built from
  // the DISPLAYED sheet values + the merge records' source values. OG adds them after BindGrid (every
  // template) and after a Save that ran the Formula Engine — never on open; G6 ALSO adds them on open
  // (user 2026-09-24, onReady). Display-only.
  const refreshQcNotes = () => {
    const h = sheetRef.current;
    if (!h) return;
    qcNotesShownRef.current = true;
    try {
      const names = h.getSheetNames();
      const model = templateBufRef.current?.model ?? 0;
      const codes = new Set(sequenceCodes(engineRecordsRef.current).map(c => c.toUpperCase()));
      const targets = model === 1 ? names.filter(n => codes.has(n.toUpperCase())) : names.slice(0, 1);
      const sheets = targets.map(name => ({ name, display: h.getSheetDisplayValues(name) ?? [] }));
      const fieldCells: Record<string, string> = {};
      for (const [k, v] of Object.entries(fieldRefsRef.current)) if (v) fieldCells[k] = v;
      h.setQcNotes(buildQcNotes(sheets, fieldCells, engineRecordsRef.current));
    } catch (e) { console.warn('[SDMS] QC hover notes failed', e); }
  };

  // Engine B templates: the columns the template's formulas WRITE are the engine's, so lock them like a
  // formula cell even where the merge left a plain value (a pure FIELD("RESULT") cell has no formula
  // and was editable). Only the pure OUTPUTS are locked — AnalyzedDate (HAA) and NumericResult (UV) are
  // also analyst INPUTS the engine reads, so they stay open. Runs on every sheet paint (onReady).
  const ENGINE_OUTPUT_FIELDS = ['RESULT', '%RECOVERY', 'RPD', 'QCSTATUS', 'DUPSOURCERESULT', 'SPIKESOURCERESULT'];
  const refreshEngineLocks = async () => {
    const templateId = roundMetaRef.current?.templateId ?? 0;
    if (!engineBRef.current || !templateId) { setEngineLockCols(null); return; }
    try {
      const targets = new Set((await qcBatchApi.sdmsEngineTargets(templateId)).map(t => t.trim().toUpperCase()));
      const cols: number[] = []; let fromRow = Number.POSITIVE_INFINITY;
      for (const field of ENGINE_OUTPUT_FIELDS) {
        if (!targets.has(field)) continue;
        const ref = fieldRefsRef.current[field];   // "Sheet!A1" — only pure FIELD cells land here; a formula cell is locked already
        const at = ref ? parseCellRef(ref.slice(ref.lastIndexOf('!') + 1)) : null;
        if (!at) continue;
        cols.push(at.col - 1); fromRow = Math.min(fromRow, at.row - 1);
      }
      setEngineLockCols(cols.length ? { cols, fromRow } : null);
    } catch (e) { console.warn('[SDMS] Engine B output columns could not be locked', e); setEngineLockCols(null); }
  };

  // Qualifier hover notes: every Qualifier cell shows what its symbol(s) mean (G6 addition, user
  // 2026-09-24). Rebuilt on every sheet paint and after any change in the Qualifier column.
  const refreshQualifierNotes = async () => {
    const h = sheetRef.current;
    const ref = fieldRefsRef.current.QUALIFIER;
    const at = ref ? parseCellRef(ref.slice(ref.lastIndexOf('!') + 1)) : null;
    if (!h) return;
    if (!at) { h.setQcNotes([], 'qualifier'); return; }
    try {
      const defs = await fetchQualifierDefinitions();
      const sheets = h.getSheetNames()
        .filter((n, i) => isResultSheet(n, i) && n.toUpperCase() !== 'QUALIFIERLIST')
        .map(name => ({ name, display: h.getSheetDisplayValues(name) ?? [] }));
      const notes = buildQualifierNotes(sheets, at.col - 1, at.row - 1, defs);
      const placed = h.setQcNotes(notes, 'qualifier');
      // eslint-disable-next-line no-console
      console.info('[SDMS] qualifier notes: %o built, %o placed (col %o from row %o, %o defs)',
        notes.length, placed, at.col - 1, at.row - 1, defs.size);
    } catch (e) { console.warn('[SDMS] qualifier notes failed', e); }
  };

  // OG Recalculate's "reset formulas to the pristine template" effect for AI: strip every
  // Apply-Rounding _LIMSRound wrap back to its inner formula (AI templates carry no built-in
  // _LIMSRound). Typed values are untouched — only formula cells change. Returns the count restored.
  const resetTemplateFormulas = async (): Promise<number> => {
    const h = sheetRef.current;
    if (!h) return 0;
    const edited = await h.getEditedXlsx();
    if (!edited) return 0;
    return stripLimsRoundWraps(edited.bytes, h);
  };

  // OG Recalculate proper (SpreadsheetEntryController :26057): reload the PRISTINE template and
  // re-push every template formula cell into the live sheet — header-area cells as-is, the
  // detail-band row copied down over every data row (relative refs shifted, exactly as the merge
  // did) — so a formula the analyst TYPED OVER comes back and any Apply-Rounding wrap is gone.
  // Skipped: the FIELD merge cells (OG's DisplayText '[' rule) and, for AI, any formula that
  // embeds FIELD()/[[…]] (the merge spliced those into literals; the raw template text would
  // put an unresolvable FIELD(…) back). Typed values / non-formula cells are untouched; the
  // cell's number format is left as OG leaves it. Model 2 (calibration levels) is not covered
  // by OG's routine either. Returns the number of cells re-pushed. (2026-09-22)
  const restoreTemplateFormulas = async (): Promise<number> => {
    const h = sheetRef.current;
    const tpl = templateBufRef.current;
    if (!h || !tpl || tpl.model === 2) return 0;
    const ExcelJS = (await import('exceljs')).default;
    const twb = new ExcelJS.Workbook();
    await twb.xlsx.load(tpl.buf);
    const tws = twb.worksheets[0];
    if (!tws) return 0;
    // Detail band = the template row with the most FIELD() cells (the merge's own detection).
    let band = -1, best = 0;
    for (let r = 1; r <= Math.min(tws.rowCount, 60); r++) {
      let n = 0;
      tws.getRow(r).eachCell({ includeEmpty: false }, c => { if (typeof c.formula === 'string' && /FIELD\(/i.test(c.formula)) n++; });
      if (n > best) { best = n; band = r; }
    }
    if (band < 0 || best < 5) return 0;
    // Where the band's row-identity column is (SysSampleCode, else Parameter) — used to count the
    // LIVE data rows of each sheet, since the merged sheet has one band row per record.
    let idCol = -1;
    tws.getRow(band).eachCell({ includeEmpty: false }, (c, col) => {
      const f = typeof c.formula === 'string' ? c.formula : '';
      if (idCol < 0 && /FIELD\(\s*"(SYSSAMPLECODE|PARAMETER)"\s*\)/i.test(f)) idCol = col;
    });
    if (idCol < 0) return 0;
    // Live data sheets: the main sheet (Model 0), every per-run sheet (Model 1), or SampleResults + QCResults (Model 4).
    const edited = await h.getEditedXlsx();
    if (!edited) return 0;
    const lwb = new ExcelJS.Workbook();
    await lwb.xlsx.load(edited.bytes);
    const { boundWholeColumnRefs, WHOLE_COL_HEADROOM_ROWS } = await import('./SdmsUniverSheet');   // loaded with the sheet
    const edits: Array<{ sheetName: string; row: number; col: number; formula: string }> = [];
    const allow = await resultSheetFilter();   // OG: main sheet (Model 0) / run sheets (Model 1) / the two split sheets (Model 4) ONLY
    for (const lws of lwb.worksheets) {
      if (!allow(lws.name)) continue;
      let rows = 0;
      for (let r = band; r <= lws.rowCount + 1; r++) {
        const v = h.getCellValue(lws.name, r - 1, idCol - 1);
        if (v === null || v.trim() === '') break;
        rows++;
      }
      if (rows === 0) continue;
      // The formula the sheet LOADED with is the template formula already through the load's rewrites — above all
      // whole-column ranges clipped to the data. The raw template text puts Calcium 8067's
      // AVERAGEIFS(H$1:H$1048467, B$1:B$1048467, …) back, and each calculation then scanned a million rows per
      // range: Recalculate hung the tab for over a minute (user report 2026-10-10). The raw text is the fallback
      // for a cell that loaded without a formula, clipped the same way.
      const lastRow = band - 1 + rows + WHOLE_COL_HEADROOM_ROWS;
      const formulaAt = (row0: number, col0: number, raw: string): string =>
        h.getLoadedFormula?.(lws.name, row0, col0) ?? `=${boundWholeColumnRefs(raw, lastRow)}`;
      tws.eachRow({ includeEmpty: false }, (trow, r) => {
        if (r > band) return;                       // author notes below the band are trimmed on merge
        trow.eachCell({ includeEmpty: false }, (c, col) => {
          const f = typeof c.formula === 'string' ? c.formula : '';
          const cached = c.result != null ? String(c.result) : (typeof c.value === 'string' ? c.value : '');
          if (!isRepushableTemplateFormula(f, cached)) return;
          if (r < band) { edits.push({ sheetName: lws.name, row: r - 1, col: col - 1, formula: formulaAt(r - 1, col - 1, f) }); return; }
          for (let k = 0; k < rows; k++)
            edits.push({ sheetName: lws.name, row: band - 1 + k, col: col - 1, formula: formulaAt(band - 1 + k, col - 1, shiftFormula(f, k)) });
        });
      });
    }
    return writeFormulas(h, edits);
  };

  // Recalculate (OG SpreadsheetEntryController :9577 → Recalculate :26057 + ClearRoundingMarker).
  // 1. restoreTemplateFormulas — OG's pristine-template re-push (restores typed-over formulas,
  //    drops Apply-Rounding wraps on those cells); 2. resetTemplateFormulas — unwrap any wrap the
  //    re-push could not reach (a pure-FIELD Result cell); 3. recompute; 4. clear the
  //    rounding-applied marker so Submit re-warns. Distinct from OG's BindGrid (a plain recompute).
  const doRecalculate = async () => {
    // OG IsSheetloaded guard (:5655, NotDataLoaded). The BLANK canvas (blankBuf) is not loaded data —
    // the old `batchBuf ?? blankBuf` test let both buttons run on an empty sheet and report success.
    if (!sheetRef.current || !batchBuf) { notify.error('Data not loaded on the sheet.'); return; }
    const restored = await runLoading(
      (async () => (await restoreTemplateFormulas()) + (await resetTemplateFormulas()))(),
      { blocking: true, label: 'Recalculating…' });
    const ok = await runLoading(sheetRef.current.recalc(), { blocking: true, label: 'Recalculating…' });
    setRoundingApplied(false);   // OG resets cpRoundingApplied so Submit re-warns
    // Bound templates recompute via Engine B (server-side); that IS the recalc for them.
    if (engineBRef.current) {
      await runLoading(runEngineBCompute(), { blocking: true, label: 'Recalculating…' });
      notify.success('Recalculated successfully.'); return;
    }
    if (ok || restored > 0) notify.success('Recalculated successfully.');
    else notify.warning('Nothing to recalculate — the sheet has no active formula engine.');
  };

  // Import Files (OG SpreadsheetEntryController Import_Execute → InstrumentImportFromExcel).
  // Upload an instrument raw-data file; the SERVER parses it via the test's DataTransfer YAML
  // spec and returns the per-row field values to stamp (+ the Field→cell map). Here the files are
  // staged in the Import Instrument File dialog; its Import writes the returned values into the matching band rows (by
  // SysSampleCode+Parameter, the writeEngineResultsToSheet pattern) and recompute. All parsing
  // + column→field mapping is server-side (§1 — no business logic in the UI).
  const doImportFiles = async () => {
    if (!sheetEdit) { notify.warning('Switch to Enter or Edit mode to import a file.'); return; }
    // Calibration mode (New Calibration, OG EnumModel.CalibrationSet → InstrumentImportCalibFromExcel
    // :1919): the set is not a batch — the upload is parsed against the calibration template + its
    // level records and the writes come back keyed by the Level sheet names.
    const calib = calibMode ? calibCtxRef.current : null;
    if (calibMode && (!calib || !calib.testMethodId)) { notify.warning('Start a calibration first.'); return; }
    const target = savedOid || effBatchOid;
    // OG imports straight after the QC Batch Ok, BEFORE the first Save — into the batch it is still
    // building. An unsaved batch imports against its pending payload (runImportFiles). (2026-09-28)
    if (!calib && !target && seed?.pendingCreate?.templateId == null) { notify.warning('Load a batch before importing an instrument file.'); return; }
    // OG opens the "Import Instrument File" dialog (:9329): files are staged there, and imported on its Import.
    // Its "Name" dropdown lists the template's DataTransfer rows (OG DataTransfer_Sp), first selected.
    const tplId = calib ? calib.templateId : (roundMetaRef.current?.templateId || seed?.pendingCreate?.templateId || 0);
    setImportTransferNames([]);
    setImportOpen(true);
    if (tplId > 0) {
      qcBatchApi.sdmsDataTransferNames(tplId)
        .then(setImportTransferNames)
        .catch(e => console.warn('[SDMS] import: data-transfer names unavailable', e));
    }
  };

  /** The import itself — run by the dialog's Import with the staged files, in staging order. Resolves
   *  true when something was imported (the dialog closes), false to keep the dialog open. */
  /** An instrument import is running. It writes NumericResult / DF cells, which schedules the dilution IsReport rule —
   *  which then exported the whole workbook in parallel with the import's own re-merge and sheet refresh (VOC 8086,
   *  2026-10-09). The rule is held while this is set and run ONCE when the import ends, on the final sheet. */
  const importingRef = useRef(false);
  // NB the automatic recalculation the value writes start is NOT skipped, though the import ends with a forced full
  // one: on VOC a Result cell reads a Numeric Result that is itself an INDIRECT formula, and Univer needs the
  // automatic pass to settle it first — with one pass only, 858 results came out 0 (tried and measured 2026-10-09).
  const runImportFiles = async (files: File[], dataTransferName: string | null): Promise<boolean> => {
    importingRef.current = true;
    let wroteCells = false;
    try { wroteCells = await runImportFilesInner(files, dataTransferName); return wroteCells; }
    finally {
      importingRef.current = false;
      if (wroteCells && dilutionColsRef.current) void runDilutionIsReport();
    }
  };

  const runImportFilesInner = async (files: File[], dataTransferName: string | null): Promise<boolean> => {
    const calib = calibMode ? calibCtxRef.current : null;
    const target = savedOid || effBatchOid;
    const pc = !calib && !target ? seed?.pendingCreate ?? null : null;
    const pendingBody: SdmsMergeDataBody | null = pc && pc.templateId != null
      ? { templateId: pc.templateId, testMethodId: pc.testMethodId, sequence: pc.sequence, instrumentOids: pc.stagedInstrumentOids }
      : null;
    if (!calib && !target && !pendingBody) { notify.warning('Load a batch before importing an instrument file.'); return false; }
    try {
      // ONE blocking loader over the whole import (user 2026-09-28): the upload and the recalculation had one
      // each, but reading the sheet, stamping the values, placing pictures and adding the RawDataSheet ran in
      // between with nothing on screen — seconds on a VOC batch — so the import looked stalled.
      setImportProg({ step: 'upload', pct: null, files: files.length, sent: 0,
        total: files.reduce((n, f) => n + f.size, 0), rows: null, hasRefresh: !calib });
      return await runLoading((async (): Promise<boolean> => {
        // OG imports the whole staged list in ONE pass (Import_Execute → InstrumentImportFromExcel(list),
        // :1906), each file fully parsed in list order and matched by sample id into the existing rows —
        // a later file overwrites an earlier one's value for the same row. Here each file goes through
        // the same single-file endpoint IN ORDER (the server archives one RawDataAttachment per parsed
        // file, like OG's per-file Save write), and the combined writes are applied once below, in that
        // order, so the last file wins exactly as in OG. A file the server rejects is reported and
        // skipped; the rest still import.
        type ImportRes = Awaited<ReturnType<typeof qcBatchApi.uploadInstrumentFile>>;
        const results: ImportRes[] = [];
        const resultFiles: File[] = [];   // the file behind each result, same order
        const failed: string[] = [];
        // Upload progress → the panel (upload step); the server's reading of the files follows once all bytes are sent.
        const onUpload = (sent: number, total: number) => setImportProg(p => {
          if (!p || p.step !== 'upload') return p;
          if (total > 0 && sent >= total) return { ...p, step: 'read', pct: null, sent, total };
          return { ...p, sent, total: total || p.total, pct: total > 0 ? Math.round((sent * 100) / total) : null };
        });
        await (async () => {
          if (!calib) {
            // Every staged file in ONE request (2026-10-01): the per-file calls made the server read the batch's merge
            // data again for each file - 16 x 0.65 s of a 16-file VOC import, for the same rows. The server parses
            // the files in this same order and returns one result per file; a file it refuses (the single-file
            // call's 400) comes back with `error`, is reported and skipped, and the rest still import, as before.
            const batch = await (target
              ? qcBatchApi.uploadInstrumentFiles(target, files, dataTransferName, onUpload)
              : qcBatchApi.uploadInstrumentFilesPending(pendingBody!, files, dataTransferName, onUpload));
            batch.forEach((r, i) => {
              const file = files[i]!;
              if (r.error) { failed.push(files.length === 1 ? r.error : `${file.name}: ${r.error}`); return; }
              results.push(r); resultFiles.push(file);
            });
            return;
          }
          // One request per file here: the bar counts the bytes of the files already sent plus the current one.
          const totalBytes = files.reduce((n, f) => n + f.size, 0);
          let before = 0;
          for (const file of files) {
            try {
              results.push(await qcBatchApi.uploadCalibrationFile(calib.templateId, calib.testMethodId, file, dataTransferName,
                (sent, total) => {
                  const cur = Math.min(before + (total > 0 ? sent : 0), totalBytes);
                  setImportProg(p => (p && (p.step === 'upload' || p.step === 'read')
                    ? { ...p, step: cur >= totalBytes ? 'read' : 'upload', sent: cur, total: totalBytes,
                        pct: cur >= totalBytes || totalBytes === 0 ? null : Math.round((cur * 100) / totalBytes) } : p));
                }));
              before += file.size;
              resultFiles.push(file);
            } catch (e) {
              before += file.size;
              const msg = (e as { response?: { data?: { error?: { message?: string } } } })
                ?.response?.data?.error?.message ?? 'Import failed.';
              failed.push(files.length === 1 ? msg : `${file.name}: ${msg}`);
            }
          }
        })();
        // OG drops a no-sample-match Anions/VOC/THM file from its list without a message — do the same.
        const importedFiles = resultFiles.filter((_f, i) => !results[i]!.skipped);
        const imported = results.filter(r => !r.skipped);
        const skippedFiles = resultFiles.length - importedFiles.length;   // read, but no sample of this batch matched
        if (!imported.length) {
          for (const f of failed.slice(0, 3)) notify.error(f);
          if (!failed.length) notify.warning(files.length > 1
            ? 'The files parsed, but no matching sample rows were found in this batch.'
            : 'The file parsed, but no matching sample rows were found in this batch.');
          return false;
        }
        results.length = 0;
        results.push(...imported);
        // All calls target the same batch/template, so the FIELD→A1 map and template name are shared.
        const res: ImportRes = {
          // each row also takes its file's name as SHEETNAME (OG :22565, the run sheet's Filename column; point 12)
          writes:       mergeImportWritesAcrossFiles(results.map((r, i) => calib ? r.writes : withSheetName(r.writes, importedFiles[i]?.name ?? ''))),
          fieldCells:   Object.assign({}, ...results.map(r => r.fieldCells)),
          rowsParsed:   results.reduce((n, r) => n + r.rowsParsed, 0),
          warnings:     results.flatMap(r => r.warnings),
          images:       results.flatMap(r => r.images ?? []),
          templateName: results.find(r => r.templateName)?.templateName ?? null,
          zeroFillField: results.find(r => r.zeroFillField)?.zeroFillField ?? null,
        };
        for (const f of failed.slice(0, 3)) notify.warning(f);
        await importStep('write', { rows: res.writes.length });
        // The loaded workbook, not a full export (~1.6 s on VOC): the write reads only the band structure (which row
        // holds which sample / parameter) and each target cell's format, which the loaded workbook has as it is, and it
        // asks the LIVE sheet whether a cell is blank. Commit an open editor first, as the export did.
        await sheetRef.current?.commitEditor?.();
        const sourceBytes = sheetRef.current?.getBaseXlsx?.()
          ?? (await sheetRef.current?.getEditedXlsx({ commitEditor: false }))?.bytes;
        if (!sourceBytes || !sheetRef.current) { notify.error('No sheet to import into.'); return false; }
        // Calibration imports target the calibration template, not the batch template in templateBufRef.
        const n = await writeImportedValuesToSheet(sourceBytes, res.fieldCells, res.writes, sheetRef.current,
          calib ? null : templateBufRef.current?.buf ?? null, calib ? null : res.zeroFillField);
        if (!calib && n > 0) instrumentImportedRef.current = true;
        // Pictures (a per-sample-sheet upload such as PFAS): float each sample's chromatograms over
        // its own sheet by OG's BindImagestoSheet stacking rules and write the two label cells above
        // each band (sdmsImportImages.ts). A Model-0 template has no per-sample sheet, so — like OG —
        // nothing is placed there.
        let pics = 0;
        if (res.images?.length) {
          const tpl = (res.templateName ?? '').trim();
          const pfas = tpl === 'PFAS 533' || tpl === 'PFAS';          // OG's literal TemplateName test
          for (const group of res.images) {
            const used = sheetRef.current.getSheetValues(group.sysSampleCode);
            if (!used) continue;
            const placed = placeImportedPictures(group.sysSampleCode, group.pictures, Math.max(0, used.length - 1), pfas);
            for (const l of placed.labels) sheetRef.current.setCellValue(l.sheetName, l.row, l.col, l.text);
            pics += sheetRef.current.addImages(placed.images);
          }
        }
        // The written values are fully recalculated once, at the end: by the in-place sheet refresh below (replaceSheets
        // finishes with a full calculation) or, when nothing is refreshed, right after it.
        let refreshed = false;
        // Unsaved batch: keep the files — the first Save archives them against the batch it creates
        // (OG writes RawDataAttachment at Save time too).
        // A COPY in memory, not the picked file itself: a browser File only points at the file on disk, so if it is
        // re-saved (open in Excel), moved or renamed before the Save, reading it then fails and the upload is lost
        // ("The raw file … could not be archived with the batch", user report 2026-09-30). It was just read for the
        // import, so it can be read now; if not, the original is kept and the Save-time warning still reports it.
        // Re-importing a file replaces its earlier import: the sheet holds the latest values, so only that version is
        // attached — not both.
        if (pendingBody) {
          const copies = await Promise.all(importedFiles.map(async (file, i) => ({
            file: await copyFileInMemory(file), sysSampleCode: imported[i]?.rawSysSampleCode ?? null })));
          const key = (x: { file: File; sysSampleCode: string | null }) => `${x.file.name.toLowerCase()}|${x.sysSampleCode ?? ''}`;
          const replaced = new Set(copies.map(key));
          pendingRawFilesRef.current = [...pendingRawFilesRef.current.filter(x => !replaced.has(key(x))), ...copies];
        }
        if (!calib && importedFiles.length) {
          await importStep('refresh');
          // OG re-binds the workbook from its rowset (dtsample) after an import, so its Sequence sheet
          // shows each run's DataFile / AcqTime / Batch Data Path and — MultipleInstruments templates —
          // its "Raw Data" summary lists each sample's file. Same here, from the loaded records with
          // every import of this session applied (user report 2026-09-28; sdmsImportSequence.ts).
          let seqMerged: ArrayBuffer | null = null;
          let summaryRows: ReturnType<typeof rawDataSummaryRows> = [];
          const sm = sampleMergeRef.current;
          const canRemerge = !!sm && sm.records === engineRecordsRef.current && sm.records.length > 0;
          // Every other non-result sheet that shows an imported field (THM Summary2: Batch Data Path, the responses,
          // Report Time) is re-bound from the same re-merge, as OG re-binds the whole workbook (comparison 2026-09-29).
          // Worked out BEFORE the re-merge so the re-merge builds only the sheets this refresh copies.
          const importedFields = new Set(['SHEETNAME', ...res.writes.flatMap(w => w.fields.map(f =>
            f.field.trim().toUpperCase().replace(/^\[(.+)\]$/, '$1')))]);
          if (res.zeroFillField) importedFields.add(res.zeroFillField.trim().toUpperCase());
          let fieldSheets: string[] = [];
          if (canRemerge && sm) {
            try {
              fieldSheets = (await nonResultSheetsShowingFields(sm.template, importedFields, isResultSheet)).filter(nm =>
                nm.toUpperCase() !== 'SEQUENCE' && nm !== RAW_DATA_SUMMARY_SHEET && nm.toLowerCase() !== OG_IMPORT_RAW_SHEET.toLowerCase());
            } catch (fsErr) { console.warn('[SDMS] import: summary sheet refresh skipped', fsErr); }
          }
          if (canRemerge && sm) {
            try {
              applyImportToOverlay(sm.records, sm.overlay,
                importedFiles.map((f, i) => ({ fileName: f.name, writes: imported[i]?.writes ?? [] })));
              const recs = recordsWithImport(sm.records, sm.overlay);
              // Only the sheets copied below: on a Model-1 template the full re-merge rebuilt every run sheet only to
              // throw them away (VOC 8086, 15 files: 7.4 s of a 21 s import, 2026-10-09).
              seqMerged = await mergeSdmsTemplate(sm.template, sm.header, recs, sm.reagents, sm.instruments,
                {}, sm.traceability, sm.model, sm.entryFields, sm.continuousHeaderFields, new Set(['Sequence', ...fieldSheets]));
              if (imported.some(r => r.multipleInstruments)) summaryRows = rawDataSummaryRows(recs);
            } catch (seqErr) {
              console.warn('[SDMS] import: Sequence / Raw Data refresh failed', seqErr);
            }
          }
          const summaryTpl = summaryRows.length ? await rawDataSummaryTemplate() : null;
          // One parse + one write for the whole refresh (it was a parse of both workbooks + a write per sheet).
          const refreshSheets = (b: ArrayBuffer): Promise<ArrayBuffer> => refreshImportedSheets(b, seqMerged,
            seqMerged ? ['Sequence', ...fieldSheets] : [], summaryRows.length ? { rows: summaryRows, template: summaryTpl } : null);
          // OG's import also adds the instrument file itself as the "RawDataSheet" worksheet (:24957, first
          // file wins) and keeps it for PFAS_533 (:11975), whose G2:H4 ISTD averages read it. It goes in IN PLACE
          // with the other refreshed sheets — a formula naming a sheet the workbook did not have yet computes once
          // the sheet is appended and recalculated (probed headlessly) — so the first import no longer re-mounts
          // the whole sheet, exactly like a later import on the saved batch (user report 2026-09-30). The edits
          // and placed pictures stay in the open sheet and are saved from it, as with every in-place refresh.
          // OG names it "Raw Data" (import :26336), or "RawDataSheet" when the template's formulas read that
          // name (PFAS_533) — addImportRawDataSheet decides and skips it when the sheet is already there.
          const tplBuf = templateBufRef.current?.buf ?? null;
          const rawName = !tplBuf || await readsImportRawSheet(tplBuf) ? OG_IMPORT_RAW_SHEET : RAW_DATA_SUMMARY_SHEET;
          const hasRawSheet = sheetRef.current.getSheetNames()
            .some(nm => nm.toLowerCase() === rawName.toLowerCase());
          const rawFile = !hasRawSheet ? importedFiles[0]! : null;
          const rawBytes = rawFile ? await rawFile.arrayBuffer() : null;
          const transform = async (b: ArrayBuffer): Promise<ArrayBuffer> =>
            refreshSheets(rawFile && rawBytes ? (await addImportRawDataSheet(b, rawBytes, rawFile.name)) ?? b : b);
          const names = [...new Set([...(seqMerged ? ['Sequence', ...fieldSheets] : []),
            ...(summaryRows.length ? [RAW_DATA_SUMMARY_SHEET] : []), ...(rawFile ? (tplBuf ? [rawName] : [OG_IMPORT_RAW_SHEET, RAW_DATA_SUMMARY_SHEET]) : [])])];
          if (names.length && await sheetRef.current.replaceSheets(transform, names)) {
            refreshed = true; setDirty(true); userEditingRef.current = true;
          }
        }
        if (!refreshed) { await importStep('calc'); await sheetRef.current.recalc(); }
        // OG runs the formula engine straight after an import (Dc_AcceptingImport: FormulaEngineHost.Execute +
        // ApplyEngineRounding), so on an engine-bound template the engine's outputs — Result, %Recovery, RPD,
        // QCStatus — show at once. G6 left them for the next Save / Recalculate, and PFAS_533_Waters' Result
        // (a plain FIELD("RESULT") cell) stayed blank after the import (G6 feedback 2026-10-15, Bug 5).
        // A full pass over the imported sheet inputs; the on-edit recompute the value writes queued is dropped.
        if (!calib && engineBRef.current && n > 0) {
          if (engineEditTimer.current) window.clearTimeout(engineEditTimer.current);
          enginePendingEditsRef.current.clear();
          await importStep('calc');
          await sheetRef.current.whenCalcIdle();
          await runEngineBOnSave();
        }
        for (const w of res.warnings.slice(0, 3)) notify.warning(w);
        const fromFiles = files.length > 1 ? ` from ${results.length} of ${files.length} file(s)` : '';
        const skippedNote = skippedFiles > 0 ? `, ${skippedFiles} file(s) skipped (no matching sample)` : '';
        if (n > 0) notify.success(`Imported ${res.writes.length} row(s)${fromFiles} — ${n} cell(s) updated${pics ? `, ${pics} picture(s) placed` : ''}${skippedNote}.`);
        else notify.warning(files.length > 1
          ? 'The files parsed, but no matching sample rows were found in this batch.'
          : 'The file parsed, but no matching sample rows were found in this batch.');
        return n > 0;
      // hideBar: the import panel (SdmsImportProgress) is the indicator and blocks the page itself — no header
      // progress bar and no "Importing…" scrim over it (user 2026-10-05, design A).
      })(), { hideBar: true });
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Import failed.';
      notify.error(msg);
      return false;
    } finally {
      setImportProg(null);
    }
  };

  // Submit (legacy Complete, SpreadsheetEntryController :8368). OG's Complete does NOT advance the
  // batch — its whole status-advance is #region oldSubmitCode and COMMENTED OUT. It SAVES the sheet
  // (ABCreateUpdate) then find-or-creates the batch's DataPackage and opens the popup. The batch is
  // submitted (→ Pending Validation) only when the analyst clicks Submit INSIDE the Data Package
  // (OG QBIDSubmit) — wired via onSubmitted on <SdmsPackageDetail>. So here we only save + open the
  // package (all gates: Analyzed-By + duplicate-report run server-side at find-or-create); no batch
  // advance, no "submitted" toast, and we STAY in Enter mode so the popup's Submit has write access.
  const doSubmit = async () => {
    const target = savedOid || effBatchOid;
    if (!target) { stub('Submit'); return; }
    try {
      // OG's Complete saves (ABCreateUpdate) first. Under the blocking loader: the save rounds and recalculates the
      // whole sheet first, which showed nothing at all (user report 2026-09-30).
      const saved = await runLoading(persistWorkbook(target), { blocking: true, label: 'Saving…' });
      if (!saved) return;                             // persistWorkbook already explained why
      // Find-or-create the batch's Data Package (Analyzed-By + duplicate-report gates run server-side).
      const pkg = await runLoading(qcBatchApi.createSdmsDataPackage(target), { blocking: true, label: 'Preparing data package…' });
      // OG's Complete (:8343) asks the QC-fail CONFIRM *before* opening the popup — a failing QC row
      // with no qualifier needs the analyst to confirm. On cancel we do NOT open the package (nothing
      // has been submitted; the batch advance only happens on the package's own Submit).
      if ((pkg?.qcFailCount ?? 0) > 0) {
        const go = await confirm.confirm({ title: 'Submit', tone: 'danger',
          message: 'QC failed. Are you sure you want to submit?' });
        if (!go) return;
      }
      setPkgStatus(pkg?.status ?? null);   // 1 PendingSubmission → the popup's Submit button shows
      setPkgCreated({ by: pkg?.batchCreatedBy ?? null, date: pkg?.packageCreatedDate ?? null });   // OG header (persisted)
      const det = await qcBatchApi.getDetail(target).catch(() => null);
      setPkgHeader(det?.header ?? null);
      setPkgOpen(true);
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Could not open the Data Package.';
      notify.error(msg);
    }
  };

  // Apply Rounding (OG ApplyRounding / RoundCellInPlace :25843). Wrap each Result-band cell's
  // formula as =_LIMSRound(<inner>, sig, cut, dec, rpt, def) with the row's parameter settings
  // (from the merge records, matched by SysSampleCode+Parameter). _LIMSRound is a live Univer
  // function (limsRoundFn.ts). Result-only for now; %Recovery + RPD are the full-parity follow-up.
  /** OG's Save auto-applies rounding when ResultEntryDefaultSettings.ApplyRoundingOnsave is On (SpreadsheetEntryController
   *  :5877-5882, ApplyRounding before the save binds the sheet): Result / %Recovery / RPD are wrapped in =_LIMSRound(...)
   *  in the saved workbook, so the stored blob carries the rounded values. OG's VOC blob had 446 such wraps and
   *  AI's none (OG vs AI comparison 2026-09-28, point 12). Silent; a cell already wrapped is left alone. */
  /** Returns whether rounding rewrote cells that still need the full calculation — only ever true with
   *  `deferRecalc`, where the caller runs that calculation itself (after the Engine B write-back). */
  const applyRoundingOnSave = async (opts?: { deferRecalc?: boolean }): Promise<boolean> => {
    const meta = roundMetaRef.current;
    const h = sheetRef.current;
    if (!meta?.testMethodId || !h) return false;
    try {
      const r = await qcBatchApi.sdmsRoundingSettings(meta.testMethodId, meta.templateId);
      if (!r.applyOnSave || !r.fieldCells || !r.settings.length) return false;
      // The loaded workbook, not a full export (~2.8 s on VOC): rounding reads its rows and formulas, and everything a
      // fresh export would change there is answered by the live sheet (getCellValue / getRuntimeFormula). persistWorkbook
      // has already committed an open editor, so a value typed without Enter is rounded too.
      const source = h.getBaseXlsx?.() ?? (await h.getEditedXlsx({ commitEditor: false }))?.bytes;
      if (!source) return false;
      if (await applyResultRounding(source, r.fieldCells, buildRoundResolver(r.settings), h, await resultSheetFilter()) > 0) {
        setRoundingApplied(true);
        if (opts?.deferRecalc) return true;
        // OG recalculates the WHOLE workbook after rounding (CalculateFull). Rewriting a formula in an
        // already-calculated sheet recalculates only that cell, and a lookup the engine cannot trace
        // (INDIRECT) is then read as blank = 0: VOC's Dup RPD saved 200 / Fail where OG has 0.0 / Pass
        // (comparison 2026-09-29). A full calculation gives OG's value; the export below waits for it.
        await h.recalc({ commitEditor: false });   // persistWorkbook committed
      }
      return false;
    } catch (e) {
      console.warn('[SDMS] apply rounding on save failed', e);   // the save itself goes on, as before
      return false;
    }
  };

  const doApplyRounding = async () => {
    if (!sheetEdit) { notify.warning('Switch to Enter or Edit mode to apply rounding.'); return; }
    const meta = roundMetaRef.current;
    if (!meta?.testMethodId) { notify.warning('Load a batch before applying rounding.'); return; }
    // The loaded workbook, not a full export — see applyRoundingOnSave.
    await sheetRef.current?.commitEditor?.();
    const source = sheetRef.current?.getBaseXlsx?.() ?? (await sheetRef.current?.getEditedXlsx({ commitEditor: false }))?.bytes;
    if (!source) { notify.warning('No sheet to round.'); return; }
    // Fetch the rounding context on demand (field positions + per-parameter settings) so this
    // works on a retrieved/reopened batch too, not just a freshly-merged one.
    const { fieldCells, settings } = await qcBatchApi.sdmsRoundingSettings(meta.testMethodId, meta.templateId);
    if (!fieldCells || !settings.length) { notify.warning('No rounding settings configured for this test.'); return; }
    const n = await runLoading((async () => {
      const count = await applyResultRounding(source, fieldCells, buildRoundResolver(settings), sheetRef.current!, await resultSheetFilter());
      // Full recalculation after the wraps, as OG (CalculateFull) - see applyRoundingOnSave.
      if (count > 0) await sheetRef.current?.recalc();
      return count;
    })(), { blocking: true, label: 'Applying rounding…' });
    if (n > 0) { setRoundingApplied(true); notify.success(`Rounding applied to ${n} result cell(s).`); }
    else notify.warning('No result cells found to round.');
  };

  // Data Package (OG SpreadsheetEntry DataPackage :8898): requires a saved QC Batch ID, then
  // find-or-creates the DataPackage record (server enforces the Analyzed-By / duplicate-report
  // gates) and opens the SAME package review view Result Validation uses (SdmsPackageDetail).
  // Feed the SAME package view Result Validation uses (SdmsPackageDetail) the batch's REAL header
  // (Status pill, Created date, identity) fetched on open — not a skeleton row — so its Batch
  // Information matches Result Validation. Submitted/Validated stay null: a batch opened from result
  // ENTRY has not been submitted/validated yet (those populate later in the workflow, as in OG).
  const pkgRow = (): SdmsPackageRow => {
    const h = pkgHeader;
    return {
      id: (savedOid || effBatchOid)!,
      batchId: h?.qcBatchId || effBatchLabel || savedQb || s.qcBatchId || null,
      // Prefer the NewStatus StatusDefinition label ("Pending SDMS Validation") over the
      // generic numeric enum ("Pending Validation") so the Data Package popup's Status pill
      // matches OG. (user UI feedback 2026-09-16 #1b)
      statusText: h?.newStatusText || h?.statusText || '', statusId: null,
      test: h?.test || effTest || s.test || null, method: h?.method ?? s.method ?? null,
      templateID: h?.templateId ?? seed?.pendingCreate?.templateId ?? null,
      // Submitted-by/date now come from the batch header (AI_Analysis_QcBatch_Header_SP), so a
      // reopened SUBMITTED batch's Data Package shows its real submitter (blank until submitted).
      validatedByList: null, validatedDateMax: null,
      submittedByList: h?.submittedByList ?? null, submittedDateMax: h?.submittedDateMax ?? null,
      matrix: h?.matrix ?? s.matrix ?? null, createdDate: h?.createdDate ?? null,
    };
  };
  const doDataPackage = async () => {
    const target = savedOid || effBatchOid;
    if (!target) { notify.warning('Save the SDMS result entry to create a QC Batch ID before the Data Package.'); return; }
    try {
      // OG's Data Package button (:8910) validates the in-memory sheet directly; our gates run
      // server-side against stored SampleInfo, so persist any unsaved edits first so the
      // Analyzed-By / duplicate-report gates (OG :8910 — no fail-qualifier gate) see current results.
      if (sheetEdit) { try { await persistWorkbook(target); } catch { /* save is best-effort */ } }
      const pkg = await runLoading(qcBatchApi.createSdmsDataPackage(target), { blocking: true, label: 'Preparing data package…' });
      setPkgStatus(pkg?.status ?? null);   // 1 PendingSubmission → Submit button shows; else view-only (validation is on the Result Validation form)
      setPkgCreated({ by: pkg?.batchCreatedBy ?? null, date: pkg?.packageCreatedDate ?? null });   // OG header (persisted)
      // Load the batch's real header so the package view shows the same Status / Created date /
      // identity as Result Validation (pkgRow reads it) instead of a blank Batch Information header.
      const det = await qcBatchApi.getDetail(target).catch(() => null);
      setPkgHeader(det?.header ?? null);
      setPkgOpen(true);
    } catch (e) {
      notify.warning((e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Could not prepare the data package.');
    }
  };

  // Qualifier — OG SpreadsheetEntryController :9599-9642 + Dc_AcceptingQualifier :10480. The
  // analyst points at ANY cell of a sample/QC row and clicks Qualifier; OG validates first and
  // then targets the row's QUALIFIER COLUMN (cpQualifierColumnIndex), never the clicked cell:
  //   1. the active sheet must be the data sheet (Model 0: the SampleID sheet; Model 1: a sheet
  //      named by a run's SysSampleCode) — else "The selected sheet is invalid…";
  //   2. the selected row must lie inside the detail band (cpDetailRowIndex ≤ row ≤ used range)
  //      — else "The selected row is invalid. Please choose a valid sample or QC to add a
  //      qualifier."; AI additionally requires the row's SysSampleCode cell to be non-blank,
  //      which is what "a valid sample or QC" means once trailing template rows are trimmed;
  //   3. the picker is pre-filled with the qualifier cell's CURRENT value, and OK writes the
  //      comma-joined symbols there (an empty pick clears it — the picker's onOk passes '').
  // Positions come from the template's DataParsing map (the same fieldCells Apply Rounding uses).
  // (Previously AI wrote to whichever cell was selected, with no validation — 2026-09-22.)
  const doQualifier = async () => {
    if (!sheetEdit) { notify.warning('Switch to Enter or Edit mode to add a qualifier.'); return; }
    const h = sheetRef.current;
    const cell = h?.getActiveCell();
    if (!h || !cell) { notify.warning('Select a sample or QC row first.'); return; }
    const meta = roundMetaRef.current;
    if (!meta?.testMethodId) { notify.warning('Load a batch before adding a qualifier.'); return; }
    const parse = (a1?: string) => {
      const m = /^([A-Z]+)(\d+)$/.exec((a1 ?? '').toUpperCase());
      if (!m) return null;
      let col = 0; for (const ch of m[1]!) col = col * 26 + (ch.charCodeAt(0) - 64);
      return { col: col - 1, row: parseInt(m[2]!, 10) - 1 };   // 0-based, matching the handle
    };
    let fieldCells: Record<string, string> | null = null;
    try { fieldCells = (await qcBatchApi.sdmsRoundingSettings(meta.testMethodId, meta.templateId)).fieldCells ?? null; }
    catch { fieldCells = null; }
    const qual = parse(fieldCells?.QUALIFIER);
    const sys  = parse(fieldCells?.SYSSAMPLECODE);
    if (!qual) { notify.warning('This template has no Qualifier column configured.'); return; }
    // 1. Data sheet only (sub-sheets — Sequence / Summary / Calibration / … — carry no result band).
    //    SampleQCSplit (Model 4): only SampleResults / QCResults (OG :9910).
    const splitModel = templateBufRef.current?.model === SAMPLE_QC_SPLIT_MODEL;
    if ((splitModel && !isSampleQcSplitSheet(cell.sheetName))
      || (!splitModel && NON_RESULT_SHEETS.has(cell.sheetName.toUpperCase()) && cell.sheetName !== (fieldRefsRef.current.PARAMETER ?? '').split('!')[0])) {   // the main sheet may be NAMED like a sub-sheet (THM "Summary")
      notify.info('The selected sheet is invalid. Please choose a valid sample or QC to add a qualifier.');
      return;
    }
    // 2. Inside the detail band AND on a real sample/QC row.
    const bandStart = (sys ?? qual).row;
    const sysVal = sys ? (h.getCellValue(cell.sheetName, cell.row, sys.col) ?? '') : '';
    if (cell.row < bandStart || (sys && sysVal.trim() === '')) {
      notify.info('The selected row is invalid. Please choose a valid sample or QC to add a qualifier.');
      return;
    }
    // 3. Target the row's Qualifier-column cell; pre-fill with what it holds now.
    qualTargetRef.current = { sheetName: cell.sheetName, row: cell.row, col: qual.col };
    setQualInitial(h.getCellValue(cell.sheetName, cell.row, qual.col) ?? '');
    setQualOpen(true);
  };

  // Attach/Open existing calibration: list the test's saved calibrations, then re-merge the
  // chosen one's SAVED rows into Level sheets read-only (OG regenerates from CalibrationInfo).
  const doAttachCalibration = async () => {
    if (!testSel) { notify.warning('Select a test first.'); return; }
    try {
      const rows = await qcBatchApi.sdmsCalibrations(testSel);
      if (!rows.length) { notify.warning('No saved calibrations for this test.'); return; }
      setCalibList(rows); setCalibListOpen(true);
    } catch (e) { console.warn('[SDMS] calibration list failed', e); notify.error('Could not load calibrations.'); }
  };

  const doOpenCalibration = async (oid: string, calId: string | null) => {
    setCalibListOpen(false);
    setRetrieving(true);
    try {
      const md = await qcBatchApi.sdmsCalibrationOpen(oid);
      if (!md) { setRetrieving(false); notify.warning('That calibration could not be opened.'); return; }
      // Re-merge the chosen calibration from its saved rows (AI never reopens from a blob).
      const calibBuf = await mergeSdmsTemplate(md.template, {
        TEST: s.test || '', METHOD: s.method ?? '', MATRIX: s.matrix ?? '', CALIBRATIONID: calId ?? '',
      }, md.records, [], [], fieldRefsRef.current, md.traceability, md.model, null /* calibration: no analyst-entry set */);

      // OG Dc_AcceptingAttachCalibation: with a QC batch loaded, ATTACH — embed the
      // calibration's sheet(s) INTO the current workbook and keep the batch loaded/editable.
      // The embedded sheets ride along in the batch's saved blob on the next Save.
      // OG C1063 (2026-10-03): also BEFORE the first save — the QC form's staged batch
      // (pendingCreate) is a result sheet too; the attached calibration is linked when Save
      // creates the batch (persistWorkbook sends it). Never replace a result sheet with the
      // standalone calibration view: that discarded the unsaved batch.
      const resultSheet = !calibMode && (hasBatch || !!seed?.pendingCreate);
      const live = resultSheet ? await sheetRef.current?.getEditedXlsx() : null;
      if (resultSheet && !live) { setRetrieving(false); notify.warning('Wait for the sheet to finish loading, then attach the calibration.'); return; }
      if (live) {
        const { bytes, copied } = await embedCalibrationSheets(live.bytes, calibBuf, md.model);
        if (!copied.length) { setRetrieving(false); notify.warning('That calibration has no sheet to attach.'); return; }
        attachedCalibRef.current = { oid, calibrationId: calId };
        attachDirtyRef.current = true;   // onReady re-marks dirty (buffer effect resets it)
        setBatchBuf(bytes);              // re-mount with the attached sheet(s); edits preserved
        notify.success(`Calibration ID ${calId ?? ''} attached successfully.`);
        return;                          // onReady clears retrieving
      }

      // No batch loaded → open the calibration standalone, read-only (AI fallback).
      calibCtxRef.current = null; setCalibMode(false);
      setMode('View'); setEditing(false);
      setBatchSel(''); setBatchText(calId ?? 'Calibration');
      engineBRef.current = false; engineRecordsRef.current = []; qcNotesShownRef.current = false;   // a calibration sheet is never Engine-B driven
      setBatchBuf(calibBuf);   // onReady clears retrieving
    } catch (e) {
      console.warn('[SDMS] open calibration failed', e);
      setRetrieving(false); notify.error('Could not open the calibration sheet.');
    }
  };

  // New Calibration (OG SDMS ribbon: NPNewCalibration -> CreateCalibDataSource ->
  // GenerateMailMerge CalibrationSet). AI resolves the test's Model=2 calibration
  // template + its level count on the server (calibration-merge-data with 0s), merges
  // one sheet PER LEVEL (Level1..LevelN) client-side, and renders it in place. The
  // analyst enters each level's readings; Save (calibration writer) is the next phase.
  const doNewCalibration = async () => {
    if (!testSel) { notify.warning('Select a test first.'); return; }
    setRetrieving(true);
    try {
      const md = await qcBatchApi.sdmsCalibrationMergeData({
        templateId: 0, testMethodId: testSel, levelNo: 0, userName: meName || 'Service',
      });
      if (!md) { setRetrieving(false); notify.warning('No calibration template is configured for this test.'); return; }
      const d = new Date();
      const p2 = (n: number) => String(n).padStart(2, '0');
      const nowStr = `${p2(d.getMonth() + 1)}/${p2(d.getDate())}/${d.getFullYear()} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
      templateBufRef.current = { buf: md.template, model: md.model ?? 0 };   // pristine template for Recalculate
      const merged = await mergeSdmsTemplate(md.template, {
        TEST: s.test || '', METHOD: s.method ?? '', MATRIX: s.matrix ?? '',
        CALIBRATEDBY: meName || 'Service', CALIBRATEDDATE: nowStr,
        ENTEREDBY: meName || 'Service', ENTEREDDATE: nowStr,
      }, md.records, [], [], fieldRefsRef.current, md.traceability, md.model, null /* calibration: no analyst-entry set */);
      calibCtxRef.current = {
        templateId: md.templateId ?? 0, testMethodId: testSel,
        fieldCells: md.fieldCells ?? {}, records: md.records ?? [],
      };
      setCalibMode(true);
      setBatchSel(''); setBatchText('');   // a calibration sheet is not a saved QC batch
      engineBRef.current = false; engineRecordsRef.current = []; qcNotesShownRef.current = false;   // a calibration sheet is never Engine-B driven
      setBatchBuf(merged);                 // <SdmsUniverSheet onReady> clears retrieving
    } catch (e) {
      console.warn('[SDMS] new calibration failed', e);
      setRetrieving(false); notify.error('Could not start the calibration sheet.');
    }
  };

  // Phase 1 read-only overlays (Project Notes · QC Batch Results · open the QC
  // Batch form). Each needs a loaded batch.
  const [notesOpen,    setNotesOpen]    = useState(false);
  const [resultsOpen,  setResultsOpen]  = useState(false);
  const [qcDetailOpen, setQcDetailOpen] = useState(false);
  // "Create a QC batch from here" — OG SpreadsheetEntryController :5507: in ENTER
  // mode with NO batch picked, Retrieve pops the `QCBatchsequence` dashboard as a
  // MODAL so the batch can be built without leaving SDMS. `handedSeed` is what that
  // popup gives back (its Ok → SDMS handoff); rendering a seeded SDMS with it is
  // how the created batch takes over this screen.
  const [newBatchOpen, setNewBatchOpen] = useState(false);
  const [handedSeed,   setHandedSeed]   = useState<SdmsEntrySeed | null>(null);

  useEffect(() => {
    // Always have the blank canvas ready — it's the fallback surface for BOTH
    // paths (nav-opened before Retrieve, and a seeded batch with no sheet on file).
    if (blankBuf) return;
    let cancelled = false;
    // Kick off the Univer chunk download IN PARALLEL with the blank buffer. Univer
    // is the single biggest load; it used to start only AFTER the buffer existed
    // (<SdmsUniverSheet> isn't mounted until a buf is ready), so the two heavy
    // steps ran back-to-back. Prefetching here overlaps them → the sheet paints
    // sooner. (No-op if the module warm-up already started it.)
    import('./SdmsUniverSheet').catch(() => {});
    blankWorkbook()
      .then(out => { if (!cancelled) setBlankBuf(out); })
      .catch(e => {
        console.warn('[SDMS] blank workbook build failed', e);
        if (!cancelled) notify.error('Could not open a blank spreadsheet.');
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blank]);

  // Full screen must hide the app's left navigation too (rail + sidebar), which
  // live OUTSIDE this component in AppShell — so drive it with a body class that
  // shell.css keys off. Cleaned up on unmount/toggle so leaving the screen (or
  // navigating away while full-screen) always restores the nav.
  useEffect(() => {
    document.body.classList.toggle('sdms-fullscreen', full);
    // The sheet container widens/narrows when the nav hides/shows; SdmsUniverSheet's own
    // ResizeObserver re-fits the Univer canvas to it, so nothing to do here beyond the class.
    return () => document.body.classList.remove('sdms-fullscreen');
  }, [full]);
  // Full Screen toggles; leaving it also brings the left navigation back EXPANDED (user request 2026-09-30,
  // which replaced the ribbon's Home button with this). Entering it hides the nav through the body class above.
  const toggleFullScreen = () => {
    if (full) window.dispatchEvent(new CustomEvent('ai:nav-expand'));
    setFull(f => !f);
  };

  // Test combo — the real SDMS feed (AI_Analysis_Sdms_Tests_SP): only tests that are
  // SDMS-CONFIGURED (have a SpreadSheetBuilder_TestParameter row) AND currently have
  // pending samples, with OG's per-mode criteria. Re-fetches when Mode flips, because
  // Enter and View count different samples.
  useEffect(() => {
    // Loaded in BOTH modes now (was blank-only): the ribbon Test/QCBatchID lookups are
    // live even when SDMS was opened FROM a QC batch, so the analyst can jump to another
    // test/batch without going back (user UI feedback 2026-09-16).
    let cancelled = false;
    (async () => {
      try {
        const rows = await qcBatchApi.sdmsTests(mode);
        if (!cancelled) setTestList(rows);
      } catch { if (!cancelled) notify.error('Could not load the test list.'); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Seeded open: resolve the ribbon Test selection from the seed once the test list
  // loads, so the QCBatchID cascade (Enter mode lists batches per-test) knows the
  // current test. Fills only when nothing is picked yet — never fights a user pick.
  // (user UI feedback 2026-09-16.)
  useEffect(() => {
    if (!seed || testSel || !testList.length) return;
    const m = testList.find(t => t.test === s.test
      && (!s.method || t.method === s.method)
      && (!s.matrix || t.matrix === s.matrix));
    if (m) setTestSel(m.testMethodId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed, testList, testSel]);

  // Grid-popup data. <LookupCombo> treats column 0 as the value it displays, and
  // hands the WHOLE row back on pick — so the real key (TestMethodId / AbOid) is
  // resolved through a row→id map instead of being shown as a GUID column.
  const ROW_KEY = (r: string[]) => r.join(' ');
  // OG Test columns: Test · Matrix · Method · TemplateName · Sx (ctrl :2167-2171).
  const testRow = (t: SdmsTestLookup) =>
    [t.test ?? '', t.matrix ?? '', t.method ?? '', t.templateName ?? '', String(t.sx)];
  const testLookup = useMemo<LookupData>(() => ({
    cols: ['Test', 'Matrix', 'Method', 'TemplateName', 'Sx'],
    rows: testList.map(testRow),
  }), [testList]);
  const testIdByRow = useMemo(
    () => new Map(testList.map(t => [ROW_KEY(testRow(t)), t.testMethodId])), [testList]);
  // The picked test's SDMS TemplateID — the lookup already carries it. Needed by the
  // "create a batch from here" popup: the QC form decides SDMS-vs-plain-batch on
  // templateId, so seeding it keeps that form on the SDMS path from the first render.
  const testTplByRow = useMemo(
    () => new Map(testList.map(t => [ROW_KEY(testRow(t)), t.templateID])), [testList]);
  // The full selected-test row — carries Matrix + Method, which the ribbon otherwise
  // drops (it only keeps testSel/testText/testTpl). The New QC Batch form seeds
  // effMatrix/effMethod straight from seed.matrix/seed.method when a testMethodId is
  // present, so without these the Matrix and Method fields open blank. (testMethodId
  // is unique per Test×Method×Matrix, so the match is unambiguous.)
  const selectedTestRow = useMemo(
    () => testList.find(t => t.testMethodId === testSel) ?? null, [testList, testSel]);

  // OG QCBatchID columns: JobID · Test · Matrix · Method · Template · QCBatchID ·
  // Status (ctrl :2184-2195). Status is VIEW-mode only (:16608 hides it in Enter,
  // :16639 shows it in View).
  // DEVIATION (deliberate): OG lists JobID first and shows the ABID in the CLOSED
  // combo via DevExpress `TextFormatString = "{5}"`. <LookupCombo> always displays
  // column 0, so QCBatchID leads here — the same COLUMN SET as OG, reordered so the
  // QCBatchID field shows a QC batch ID instead of a JobID.
  const batchRow = (b: SdmsQcBatchLookup) => {
    const base = [b.abid ?? '', b.jobID ?? '', b.test ?? '', b.matrix ?? '',
                  b.method ?? '', b.template ?? ''];
    return mode === 'View' ? [...base, b.status ?? ''] : base;
  };
  const batchLookup = useMemo<LookupData>(() => ({
    cols: mode === 'View'
      ? ['QCBatchID', 'JobID', 'Test', 'Matrix', 'Method', 'Template', 'Status']
      : ['QCBatchID', 'JobID', 'Test', 'Matrix', 'Method', 'Template'],
    rows: batchList.map(batchRow),
  }), [batchList, mode]);
  const batchIdByRow = useMemo(
    () => new Map(batchList.map(b => [ROW_KEY(batchRow(b)), b.uqAnalyticalBatchID])),
    [batchList, mode]);

  // QCBatchID options — from OG's own sproc, so the grid shows exactly its rows.
  // Re-runs on a Mode change too, which alters both the row set and whether Status
  // is a column.
  //
  // OG fills this list DIFFERENTLY per mode (ctrl :16608 vs :16702):
  //   Enter → EXEC … @Mode, @TestMethodID  — a Test is mandatory (the legacy
  //           sproc's Enter branch has no null-guard on it), so cascade from Test.
  //   View  → EXEC … @Mode                 — NO test filter: every test's batches.
  // So only Enter waits for a Test; View loads straight away and the grid's Test
  // column tells the rows apart. (Previously View was gated on a Test too, which
  // made it strictly narrower than OG.)
  useEffect(() => {
    // Reload the QC-batch list when Test or Mode changes. Clearing the picked batch +
    // loaded sheet is done by the Test / Mode combo handlers (a USER change), NOT here:
    // a PROGRAMMATIC mode flip from opening a status>1 batch (loadBatchSheet, OG parity)
    // must keep the sheet on screen, so it must not trip a clear.
    if (!testSel && mode !== 'View') { setBatchList([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const rows = await qcBatchApi.sdmsQcBatches(testSel || undefined, mode, dateFilterMonths ?? undefined);
        if (!cancelled) setBatchList(rows);
      } catch { if (!cancelled) setBatchList([]); }
    })();
    return () => { cancelled = true; };
  }, [testSel, mode, dateFilterMonths]);

  // Loads the selected batch's sheet — invoked by RETRIEVE only (OG :5505: the
  // AnalyticalBatch combo has no auto-load; Retrieve reads the batch's SpreadSheet,
  // else its empty template). Endpoint serves the SAVED workbook, else the
  // template, else 204.
  const loadBatchSheet = async (abOid: string) => {
    // Only the LATEST load may touch the screen. Dev StrictMode runs the seeded "Open SDMS" load twice and a
    // second Retrieve can start one mid-way; the older one then rebuilt the sheet AFTER the newer one had
    // painted — the panel came back as "Building the sheet… 99%" and the colours of the first build were
    // gone (user 2026-09-30).
    const seq = ++loadSeqRef.current;
    const stale = () => seq !== loadSeqRef.current;
    setCalibMode(false); calibCtxRef.current = null; attachedCalibRef.current = null;
    // The overlay stays up from here until the sheet has PAINTED — on success
    // it's cleared by <SdmsUniverSheet onReady>, NOT here, so there's no white
    // flash between "fetch done" and "sheet rendered". Failure paths clear it.
    setRetrieving(true);
    try {
      // AI DIVERGENCE FROM OG (owner decision 2026-09-09): OG's Retrieve (LoadfromAB) reloads
      // the SAVED .xlsx blob verbatim. AI instead RE-MERGES the template from the batch's SAVED
      // TABLE rows (SpreadSheetEntry_SampleInfo, via AI_Analysis_Sdms_OpenMergeData_SP) — so a
      // reopened batch always shows its samples (a never-saved batch → blank-and-ready) and never
      // a bare template. Save still stores the blob for OG coexistence; the blob is only a
      // fallback here (merge-data 204 = no template blob, or a merge failure).
      // Reagents: OG's ReagentDataSource is the batch's LINKED reagents on every open (:3629); the
      // re-merge used to pass none, so the Reagents sheet came back blank on reopen (parity harness
      // class 1, 2026-09-23). Column→source exactly as the Enter-mode handoff maps the staged picks.
      // The saved sheet (blob-only values + the Save's rounding) is fetched AND read now, while the server prepares
      // the merge data (VOC: ~2 s) - it used to start only after that data arrived. The fallback below reuses it.
      const savedSheetEarly = qcBatchApi.sdmsWorkbook(abOid).catch(() => null);
      // The instrument raw data (Raw Data / RawDataSheet carry) is downloaded now too, for the same reason.
      const rawDataEarly = prefetchRawDataCarry(abOid);
      const savedWorkbookEarly = savedSheetEarly.then(s => (s && s.source !== 'template'
        ? loadSdmsWorkbook(s.buf).catch(() => null) : null));
      const [md, detail, linkedReagents] = await Promise.all([
        qcBatchApi.sdmsOpenMergeData(abOid).catch(() => null),
        qcBatchApi.getDetail(abOid).catch(() => null),
        qcBatchApi.linkedReagents(abOid).catch(() => []),
      ]);
      if (stale()) return;
      await dataStep('fetch');
      // OG derives the Mode from the opened batch's status (SpreadsheetEntryController
      // :2612 / :2709): qcstatus 0|1 → Enter (still entering results), >1 → View
      // (read-only — already submitted / validated / approved). Safe to flip here: the
      // [testSel,mode] effect no longer clears the picked batch/sheet on a mode change
      // (that moved to the combo handlers), so the loaded sheet survives the flip.
      const st = detail?.header?.status;
      if (typeof st === 'number') {
        const want: 'Enter' | 'View' = st > 1 ? 'View' : 'Enter';
        if (want !== mode) { setMode(want); setEditing(false); }
      }
      // Apply Rounding context for a RETRIEVED batch (parity with the OK->SDMS merge).
      if (detail?.header) roundMetaRef.current = { testMethodId: detail.header.testMethodId, templateId: detail.header.templateId ?? 0 };
      // Re-merge from the tables (the primary open path).
      if (md?.template) {
        try {
          const h = detail?.header;
          const d = new Date();
          const p2 = (n: number) => String(n).padStart(2, '0');
          const nowStr = `${p2(d.getMonth() + 1)}/${p2(d.getDate())}/${d.getFullYear()} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
          const by = h?.createdByName || meName || 'Service';
          const dt = h?.createdDate ? fmtLocalDate(h.createdDate) : nowStr;
          templateBufRef.current = { buf: md.template, model: md.model ?? 0 };   // pristine template for Recalculate
          const mergeHeader = {
            TEST: h?.test ?? effTest ?? '', METHOD: h?.method ?? s.method ?? '', MATRIX: h?.matrix ?? s.matrix ?? '',
            INSTRUMENT: s.instrument ?? '', QCBATCHID: h?.qcBatchId ?? effBatchLabel ?? '',
            ENTEREDBY: by, ANALYZEDBY: by, ENTEREDDATE: dt, ANALYZEDDATE: dt,
          };
          const mergeReagents = linkedReagents.map(r => ({
            ReagentID: r.reagentID ?? '', ReagentName: r.reagentName ?? '', PrepType: r.prepType,
            ExpiredDate: r.expiredDate ? fmtLocalDate(r.expiredDate) : '',
            VendorLT: r.vendorLT, CreatedBy: r.preparedBy, Description: '',
          }));
          // Kept for Import Files, which re-merges the Sequence sheet from these records (sdmsImportSequence.ts).
          instrumentImportedRef.current = false;
          sampleMergeRef.current = { template: md.template, header: mergeHeader, records: md.records ?? [],
            reagents: mergeReagents, instruments: md.instruments ?? [], traceability: md.traceability ?? null,
            model: md.model ?? 0, entryFields: md.entryFields ?? null, continuousHeaderFields: md.continuousHeaderFields ?? null,
            overlay: new Map() };
          // One parse, one write (sdmsOpenChain.ts): the step-by-step chain parsed and re-wrote the whole
          // workbook at every step (~13 s of a VOC reopen). The reads the chain waits on start NOW, so the
          // network overlaps the merge instead of following it.
          const hasFormulas = qcBatchApi.sdmsHasFormulas(roundMetaRef.current?.templateId ?? 0).catch(() => false);
          const calibration = md.calibrationOid
            ? qcBatchApi.sdmsCalibrationOpen(md.calibrationOid)
                .catch(calErr => { console.warn('[SDMS] reopen calibration re-embed failed', calErr); return null; })
            : null;
          // A fresh field map per load: the previous batch's template put its fields elsewhere.
          const refs: Record<string, string> = {};
          const { buf: finalBuf, calibrationAttached } = await buildSdmsOpenWorkbook({
            template: md.template, header: mergeHeader, records: md.records, reagents: mergeReagents,
            instruments: md.instruments ?? [], fieldRefs: refs,
            continuousHeaderFields: md.continuousHeaderFields,
            traceability: md.traceability, model: md.model ?? 0,
            // REOPEN: the Write=1 (analyst-entered) fields re-merge as NUMBERS so the template's
            // number format renders the decimals the analyst saw ("10.00", not "10") — OG blob parity.
            entryFields: md.entryFields ?? null,
            fieldCells: md.fieldCells ?? {}, testName: h?.test ?? effTest,
            // Dept-chain analyst dropdown (OG SetUserDropdownsFromDepartmentChain): creator = the batch's
            // CreatedBy (`by`), matching OG's default pick + pre-fill. Qualifier list: OG runtime DataValidations.Add.
            deptChainDropdown: md.deptChainDropdown, creator: by, qualifierSymbols: fetchQualifierSymbols(),
            // Attach Calibration (OG QCBatchController-on-open sets strCB from the batch's Calibration):
            // a linked calibration's sheet(s) are re-embedded (AI re-merges from tables, not the blob).
            calibration,
            calibrationHeader: {
              TEST: h?.test ?? effTest ?? '', METHOD: h?.method ?? s.method ?? '', MATRIX: h?.matrix ?? s.matrix ?? '',
              CALIBRATIONID: md.calibrationId ?? '',
            },
            // OG CalibAndABIDSupport: the batch's Calibration ID + the user on the Calibration sheet (G2 / G8 on 8076).
            calibrationFields: { cells: (calibrationCellsRef.current = md.calibrationCells ?? null),
              values: { CALIBRATIONID: md.calibrationId ?? null, CALIBRATEDBY: meName || null } },
            // Raw Data carry (OG ReAttachRawData) + the SAVED sheet's blob-only values (owner decision D2,
            // 2026-09-26: 1014 "Sample Filtered", VOC Summary "IS Avg Resp.", pH "Temp Corr. Factor").
            rawDataBatchOid: abOid, sequence: sequenceCodes(md.records),
            savedSheet: savedSheetEarly, savedWorkbook: savedWorkbookEarly, rawDataPrefetch: rawDataEarly,
            step: dataStep, cancelled: stale,
          });
          if (stale()) return;
          fieldRefsRef.current = refs;
          if (calibrationAttached && md.calibrationOid) attachedCalibRef.current = { oid: md.calibrationOid, calibrationId: md.calibrationId ?? null };
          // Engine B gate: templates with Fixed Formula-Library bindings are driven by the server
          // compute after the sheet paints (onReady). Best-effort — a probe failure never breaks the load.
          engineRecordsRef.current = md.records ?? []; qcNotesShownRef.current = false;
          engineBRef.current = await hasFormulas;
          if (stale()) return;
          // onReady clears `retrieving` once Univer has painted — no success toast (the sheet IS the feedback).
          setBufRemerged(true);   // re-merge: no cached formula results → sheet must compute (even View)
          setLockedSheets(lockedSheetsFor(md.traceability));
          setBatchBuf(finalBuf);
          return;
        } catch (mergeErr) {
          if (stale()) return;
          console.warn('[SDMS] reopen re-merge failed — falling back to the stored blob', mergeErr);
        }
      }
      if (stale()) return;
      // Fallback: no merge-data (204: template blob gone) or the merge threw — load the saved
      // blob as OG does, so reopen never regresses below the previous behaviour.
      engineBRef.current = false; engineRecordsRef.current = []; qcNotesShownRef.current = false;   // blob fallback: no merge records → no Engine-B compute
      setBufRemerged(false);   // saved blob carries DevExpress caches → keep the fast capped value-only path
      setLockedSheets(lockedSheetsFor('Reagents,Instruments'));   // no template info here — lock the OG-protected names if present
      const wb = await savedSheetEarly;   // fetched at the start of this load
      if (stale()) return;
      if (!wb) {
        setBatchBuf(null);
        setRetrieving(false);
        notify.warning('No SDMS sheet on file for this batch yet.');
        return;
      }
      setBatchBuf(wb.buf);
    } catch {
      if (stale()) return;
      setBatchBuf(null);
      setRetrieving(false);
      notify.error('Could not load the SDMS sheet for this batch.');
    }
  };

  // Clear / Cancel — OG SpreadsheetEntryController :5646. ONE button with TWO
  // behaviours, chosen by edit state, and its label/enablement follow suit:
  //
  //   Enter mode           → "Clear"  · ENABLED  (modechange :16911)
  //        confirm "All Unssaved changes will be cleared. Do you want to continue?"
  //        (OG's verbatim wording, typo and all — kept, minus the typo) then the
  //        confirm's `true` callback runs `clearcontrols` (:5473 → :17864): it NULLs
  //        the test method / batch / QC ids, the template and every cached table,
  //        drops the loaded + edit flags and creates a NEW blank document — the
  //        nav-opened screen. So the ribbon Test + QCBatchID DO reset (the earlier
  //        "OG clears the sheet, not the ribbon" note was wrong — user report
  //        2026-09-22: Clear left QCBatchID/Test filled and the sheet seeded).
  //   View + editing       → "Cancel" · ENABLED  (:17053-17055 renames it and swaps
  //        the icon). Discards unsaved edits by RELOADING the sheet from the saved
  //        batch — OG `LoadfromAB(sheet, batch)` + ABGridrefresh + CalibRefresh —
  //        then leaves edit mode. Previously AI only did `setEditing(false)`, which
  //        left the user's unsaved edits sitting in the sheet: the "cancel" did not
  //        actually cancel anything.
  //   View, not editing    → DISABLED (:17070).
  //
  // The confirm goes through the shared dialog (standing rule 7), not a native
  // confirm() as OG uses.
  const doClear = async () => {
    if (editing) {
      setEditing(false);
      if (effBatchOid) await loadBatchSheet(effBatchOid);   // re-read = discard edits
      return;
    }
    const ok = await confirm.confirm({
      title: 'Clear',
      message: 'All unsaved changes will be cleared. Do you want to continue?',
      tone: 'danger',
    });
    if (!ok) return;
    clearControls();
  };

  // OG `clearcontrols` (SpreadsheetEntryController) — everything a loaded batch put on this screen
  // goes, leaving the blank nav-opened SDMS page. Shared by Clear and by Delete (OG :9245-9246:
  // DeleteSuccess message, then clearcontrols — the analyst stays on SDMS).
  function clearControls() {
    loadSeqRef.current++; setRetrieving(false);       // a load still in flight must not bring the sheet back
    setCalibMode(false); calibCtxRef.current = null;
    setBatchBuf(null); setBufRemerged(true);          // Document.CreateNewDocument()
    setCleared(true);                                 // seeded page → nav-opened page
    setSavedOid(null); setSavedQb(null);              // strAB / strqcid / QCBatchOid = null
    setTestSel(''); setTestText(''); setTestTpl(null); // strTest / OidTestMethod / templateid
    setBatchSel(''); setBatchText(''); setBatchList([]);
    setEditing(false); setSubmitted(false);           // Isedit / IsSheetloaded = false
    setDirty(false); userEditingRef.current = false;
    fieldRefsRef.current = {};                        // dtDataParsing / dtHeader / dtDetail
    engineRecordsRef.current = []; qcNotesShownRef.current = false; engineBRef.current = false;
    roundMetaRef.current = null; templateBufRef.current = null;
    attachedCalibRef.current = null;                  // CalibRefresh()
  }

  // Report — OG GenerateReports (SpreadsheetEntryController :9361): the per-template raw-data
  // DevExpress report, built from the LIVE sheet (BindSampleToReport :17919 reads the loaded
  // control — unsaved edits included) and rendered server-side from the template's stored
  // layout (ReportPreview :12042). OG's gate is "a minted batch AND a loaded sheet"; 0 reports →
  // info message; 1 → preview directly; >1 → the SDMSReportPopupDC picker (rendered below).
  // Mold tests take OG's separate BindMoldSampleToReport — not ported (Mold flow, team-DB).
  const [reportPick, setReportPick] = useState<{ info: SdmsReportInfo; data: SdmsReportSheetData } | null>(null);
  const [reportPickId, setReportPickId] = useState<number | null>(null);
  // The PDF call is `responseType: 'blob'`, so a 400 (e.g. "layout not converted") arrives as a
  // JSON *Blob* — read it back before looking for the envelope's error message.
  const reportErrMsg = async (e: unknown, fallback: string): Promise<string> => {
    const data = (e as { response?: { data?: unknown } })?.response?.data;
    try {
      const text = data instanceof Blob ? await data.text() : typeof data === 'string' ? data : data ? JSON.stringify(data) : '';
      const msg = (JSON.parse(text) as { error?: { message?: string } })?.error?.message;
      return msg || fallback;
    } catch { return fallback; }
  };
  const doGenerateReport = async () => {
    if (!effBatchOid || !batchBuf) { notify.info('No data loaded.'); return; }
    if ((effTest || '').startsWith('Mold')) {
      void confirm.alert({ title: 'Report', tone: 'info', message: 'Raw data reports for Mold tests are not available yet.' });
      return;
    }
    const h = sheetRef.current;
    if (!h) { notify.info('No data loaded.'); return; }
    try {
      const info = await qcBatchApi.sdmsReportInfo(effBatchOid);
      if (!info || info.reports.length === 0) {
        void confirm.alert({ title: 'Report', tone: 'info', message: 'No raw data report template is configured for this test.' });
        return;
      }
      const runs = runCodesOf(engineRecordsRef.current);
      const data = extractSdmsReportData({
        fieldRefs: fieldRefsRef.current,
        model: info.model ?? templateBufRef.current?.model ?? 0,
        runCodes: runs,
        headerPositions: info.headerMap.map(m => m.position),
        readSheet: name => h.getSheetValues(name),
      });
      if (!data || data.rows.length === 0) {
        // OG: dtDetailDataNew empty → nothing happens (no report). Say so instead of silence.
        notify.info('No data loaded.');
        return;
      }
      if (info.reports.length === 1) { await qcBatchApi.openSdmsReport(effBatchOid, info.reports[0]!.reportId, data); return; }
      setReportPickId(info.reports[0]!.reportId);
      setReportPick({ info, data });
    } catch (e) {
      notify.error(await reportErrMsg(e, 'Could not generate the report.'));
    }
  };

  // Seeded entry (Open SDMS button / the OK→SDMS handoff) — auto-load the batch's
  // workbook on mount (its saved sheet, else its empty template), so the analyst
  // lands directly on the sheet. Nav-opened has no seed → skipped (Retrieve loads).
  useEffect(() => {
    // Skip the DB re-merge when a STAGED sequence was handed over (create OR an add/remove
    // edit handoff): pendingCreate carries the not-yet-persisted sequence, and its own build
    // effect renders it. Loading from the DB here would drop the staged adds and show the
    // removed rows. A plain "Open SDMS" on a saved batch (no pendingCreate) still loads.
    if (blank || !s.qcBatchOid || s.pendingCreate) return;
    void loadBatchSheet(s.qcBatchOid);
    return () => { loadSeqRef.current++; };   // unmount (or StrictMode's first pass): drop the in-flight load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // OK→SDMS handoff — the batch is NOT saved yet (OG defers the save to SDMS), so
  // there's no server workbook to fetch. Render the handed-over sequence itself as
  // the sheet so the batch's samples/QC are visible immediately (the per-template
  // MailMerge OG does is a later phase). Save (below) then persists the batch.
  useEffect(() => {
    const pc = seed?.pendingCreate;
    if (!pc || !pc.sequence?.length) return;
    setRetrieving(true);
    let cancelled = false;
    (async () => {
      try {
        // OG-faithful: mail-merge the per-test template with the built sequence.
        // The backend returns the template + records; the merge runs client-side
        // (ExcelJS) so the result stays Univer-readable. A failure (no template
        // blob, or an unsupported template feature) degrades to the raw-sequence
        // sheet rather than erroring — the batch stays visible either way.
        if (pc.templateId != null) {
          try {
            // ADD/REMOVE EDIT handoff: the saved batch's linked reagents + entered results are read in
            // parallel with the merge data (they were fetched one after another once it had arrived).
            const editOid = seed?.qcBatchOid ?? null;
            const linkedLater = editOid ? startEarly(qcBatchApi.linkedReagents(editOid)) : null;
            const dbMdLater = editOid ? startEarly(qcBatchApi.sdmsOpenMergeData(editOid)) : null;
            const md = await qcBatchApi.sdmsMergeData({
              templateId: pc.templateId, testMethodId: pc.testMethodId, sequence: pc.sequence,
              // Instruments sub-sheet: resolved backend from the staged labware Oids
              // (OG InstrumentDataSource). Reagents sub-sheet is built client-side below.
              instrumentOids: pc.stagedInstrumentOids,
            });
            if (md) {
              // ADD/REMOVE EDIT handoff (saved batch → seed.qcBatchOid set): the staged merge
              // above blanks every result cell. OG's re-merge (GenerateMailMerge) re-hydrates each
              // RETAINED row's entered results from the persisted SpreadSheetEntry rows; mirror that
              // by overlaying sdmsOpenMergeData's entered values onto the staged records. A plain
              // CREATE handoff (no existing batch) has nothing to re-hydrate. Best-effort — a
              // failure leaves the (blank) staged sheet rather than erroring.
              const existingOid = seed?.qcBatchOid ?? null;
              let editCalib: { oid: string; calId: string | null } | null = null;
              // A SAVED batch's Others-tab edits are linked in the DB the moment they are picked
              // (QcBatchDetailPage linkReagent / linkInstrument), so its handoff carries NO staged
              // picks — the Reagents / Instruments sheets came back blank after the popup's Ok (user
              // 2026-09-23). Read the batch's CURRENT links instead, exactly as the reopen does.
              let linkedReagentRows: Record<string, unknown>[] | null = null;
              let linkedInstruments: Record<string, unknown>[] | null = null;
              if (existingOid) {
                try {
                  linkedReagentRows = (await linkedLater!()).map(r => ({
                    ReagentID: r.reagentID ?? '', ReagentName: r.reagentName ?? '', PrepType: r.prepType,
                    ExpiredDate: r.expiredDate ? fmtLocalDate(r.expiredDate) : '',
                    VendorLT: r.vendorLT, CreatedBy: r.preparedBy, Description: '',
                  }));
                } catch (rgErr) { console.warn('[SDMS] edit handoff: linked reagents read failed', rgErr); }
                try {
                  const dbMd = await dbMdLater!();
                  if (dbMd?.instruments) linkedInstruments = dbMd.instruments;
                  // `dbMd.entryFields` = THIS template's entered columns (server-derived, e.g.
                  // VolumeUsed on 8085) — without it only the fixed result slots came back and
                  // every entered volume/mass was blanked, so the Result formulas recomputed to
                  // nothing (user report 2026-09-22: "remove a sample → entire results cleared").
                  if (!cancelled && dbMd?.records?.length) overlaySdmsEnteredResults(md.records, dbMd.records, dbMd.entryFields);
                  // Attached calibration (FK) — re-embed its sheet(s) after the merge, same as the
                  // main reopen (loadBatchSheet), so an add/remove edit keeps the calibration tab(s).
                  if (dbMd?.calibrationOid) editCalib = { oid: dbMd.calibrationOid, calId: dbMd.calibrationId ?? null };
                } catch (rehydrateErr) {
                  console.warn('[SDMS] edit re-hydrate of entered results failed', rehydrateErr);
                }
              }
              roundMetaRef.current = { testMethodId: pc.testMethodId, templateId: md.templateId ?? 0 };   // Apply Rounding context
              const d = new Date();
              const p2 = (n: number) => String(n).padStart(2, '0');
              const nowStr = `${p2(d.getMonth() + 1)}/${p2(d.getDate())}/${d.getFullYear()} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
              templateBufRef.current = { buf: md.template, model: md.model ?? 0 };   // pristine template for Recalculate
              const mergeHeader = {
                TEST: s.test || '', METHOD: s.method ?? '', MATRIX: s.matrix ?? '',
                INSTRUMENT: s.instrument ?? '', QCBATCHID: savedQb ?? s.qcBatchId ?? '',   // blank until saved (OG parity)
                ENTEREDBY: s.analyst || meName || 'Service', ANALYZEDBY: s.analyst || meName || 'Service',
                ENTEREDDATE: nowStr, ANALYZEDDATE: nowStr,
              };
              // Reagents sub-sheet dataset — the batch's staged reagent picks mapped to
              // the "Reagents" sheet's FIELD names (OG ReagentDataSource). Exact rows the
              // user chose (Reagent.ReagentID isn't unique, so this beats a DB re-lookup).
              // Column→source EXACTLY as OG builds dtQCReagent (QCBatchController.cs
              // :8155-8161): note OG fills the sheet's "CreatedBy" from Reagent.PreparedBy.
              // ExpiredDate arrives as the wire datetime → render MM/DD/YYYY via the shared
              // helper (OG's FIELD() shows the date, not an ISO stamp). Description is the
              // one OG column the staged pick doesn't carry (needs the reagent Oid) → blank.
              const mergeReagents = linkedReagentRows ?? (pc.stagedReagents ?? []).map(r => ({
                ReagentID: r.reagentId, ReagentName: r.reagentName, PrepType: r.prepType,
                ExpiredDate: r.expiredDate ? fmtLocalDate(r.expiredDate) : '',
                VendorLT: r.vendorLT, CreatedBy: r.preparedBy, Description: '',
              }));
              const mergeInstruments = linkedInstruments ?? md.instruments;
              // Kept for Import Files, which re-merges the Sequence sheet from these records (sdmsImportSequence.ts).
              // Only by the LIVE run: this effect runs twice in dev (StrictMode) and the cancelled run's merge can
              // land last. Writing it then paired these records with the other run's (engineRecordsRef), so an
              // import on a new batch neither refreshed the Sequence / Raw Data sheets nor saved its import-only
              // values (VOC USERDEFINED3 blank on 427 rows; OG vs AI comparison 2026-09-29, QB26092901_001).
              if (!cancelled) instrumentImportedRef.current = false;
              if (!cancelled) sampleMergeRef.current = { template: md.template, header: mergeHeader, records: md.records ?? [],
                reagents: mergeReagents, instruments: mergeInstruments ?? [], traceability: md.traceability ?? null,
                model: md.model ?? 0, entryFields: md.entryFields ?? null, continuousHeaderFields: md.continuousHeaderFields ?? null,
                overlay: new Map() };
              await dataStep('fetch');
              // One parse, one write (sdmsOpenChain.ts) — see the reopen path.
              const hasFormulas = qcBatchApi.sdmsHasFormulas(roundMetaRef.current?.templateId ?? 0).catch(() => false);
              const calibration = editCalib?.oid
                ? qcBatchApi.sdmsCalibrationOpen(editCalib.oid)
                    .catch(calErr => { console.warn('[SDMS] edit re-embed calibration failed', calErr); return null; })
                : null;
              const refs: Record<string, string> = {};   // a fresh field map per run (see loadBatchSheet)
              const { buf: finalEdit, calibrationAttached } = await buildSdmsOpenWorkbook({
                template: md.template, header: mergeHeader, records: md.records, reagents: mergeReagents,
                instruments: mergeInstruments ?? [], fieldRefs: refs,
                continuousHeaderFields: md.continuousHeaderFields,
                // Traceability gates the Reagents / Instruments sheets exactly as OG does;
                // Model=1 (SampleSheetNavigation) makes the merge one worksheet PER RUN.
                traceability: md.traceability, model: md.model ?? 0, entryFields: md.entryFields ?? null,
                fieldCells: md.fieldCells ?? {}, testName: s.test,
                // Dept-chain dropdown: creator = the current user (the batch's creator on a fresh handoff).
                deptChainDropdown: md.deptChainDropdown, creator: meName || 'Service', qualifierSymbols: fetchQualifierSymbols(),
                // Re-embed the attached calibration (OG carries it across the add/remove re-open), matching
                // loadBatchSheet. No-op when the batch has no linked calibration.
                calibration,
                calibrationHeader: {
                  TEST: s.test || '', METHOD: s.method ?? '', MATRIX: s.matrix ?? '', CALIBRATIONID: editCalib?.calId ?? '',
                },
                // OG CalibAndABIDSupport (see loadBatchSheet): Calibration ID once the batch has one, and the user.
                calibrationFields: { cells: (calibrationCellsRef.current = md.calibrationCells ?? null),
                  values: { CALIBRATIONID: editCalib?.calId ?? null, CALIBRATEDBY: meName || null } },
                // Raw Data carry (OG ReAttachRawData) — same as the main reopen; needs a saved batch.
                rawDataBatchOid: existingOid, sequence: sequenceCodes(md.records),
                step: dataStep, cancelled: () => cancelled,
              });
              if (!cancelled) fieldRefsRef.current = refs;
              if (calibrationAttached && editCalib?.oid) attachedCalibRef.current = { oid: editCalib.oid, calibrationId: editCalib.calId };
              // Engine B gate (see reopen path) — bound templates compute server-side after paint.
              if (!cancelled) {
                engineRecordsRef.current = md.records ?? []; qcNotesShownRef.current = false;
                engineBRef.current = await hasFormulas;
              }
              if (!cancelled) {
                setLockedSheets(lockedSheetsFor(md.traceability));
                setBatchBuf(finalEdit);
              }
              return;   // onReady clears retrieving
            }
          } catch (mergeErr) {
            console.warn('[SDMS] template merge failed — falling back to the sequence table', mergeErr);
          }
        }
        engineBRef.current = false; engineRecordsRef.current = []; qcNotesShownRef.current = false;   // raw-sequence fallback: no merge records → no Engine-B compute
        const buf = await buildSequenceWorkbook(pc.sequence);
        if (!cancelled) setBatchBuf(buf);
      } catch (e) {
        console.warn('[SDMS] merge/sequence build failed', e);
        if (!cancelled) { setRetrieving(false); notify.error('Could not render the batch sheet.'); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The popup handed us a built batch (OG :5507 flow completed) — hand this screen
  // over to a SEEDED SDMS for it. Re-rendering ourselves with the seed reuses the
  // exact same path as the Analysis Queue → QC Batch → Ok handoff, rather than
  // teaching this instance to adopt a seed mid-life. Back returns to the blank
  // nav-opened screen. (No infinite recursion: the seeded instance has blank=false,
  // so its Retrieve never reaches the create branch.)
  if (handedSeed) {
    return <SdmsResultEntryPage seed={handedSeed} onBack={() => setHandedSeed(null)} />;
  }

  // "QC Batch" ribbon action — rendered as a POPUP below (next to the New-QC-Batch modal),
  // not a full-page swap: OG QCBatchDetail (:9644) shows the `QCBatchsequence` dashboard as a
  // NewModalWindow over the sheet, editable by status (user OG side-by-side 2026-09-22).

  return (
    // The SDMS Result Entry page opts OUT of the AUTOMATIC unsaved-changes tracker
    // (data-unsaved-ignore): its controls are SELECTION/navigation (mode, test/method/batch
    // pickers — choosing WHAT to load) and the Univer sheet's own cell-editor / formula-bar
    // inputs — none are record fields, so the DOM tracker would flag a freshly-OPENED sheet as
    // dirty (picking a batch to open counts as a "user edit"), warning on the very next nav.
    // The record being edited is the SPREADSHEET, tracked EXPLICITLY instead: a genuine Univer
    // edit-start → onUserEditStart → userEditingRef → setDirty(true) → useUnsavedGuard(dirty)
    // (see :242 / :309). So an untouched sheet never warns, but an edited one still does.
    <div className={`sdms-root${full ? ' sdmsfull' : ''}`} data-unsaved-ignore>
      {/* Workflow stepper REMOVED (user UI feedback 2026-09-16 — hide it on the SDMS
          screen). It only ever rendered on the QC-batch-seeded open (nav-opened and
          the New-QC-Batch popup already suppressed it), which is exactly the case the
          user wants clean. */}
      {onBack && (
        <div className="sdms-back">
          <button className="btn" onClick={guardedBack}>‹ Back to QC Batch</button>
        </div>
      )}

      <div className="crumb">
        {onBack ? <a onClick={guardedBack}>QC Batch</a> : <span>Analysis</span>} / <b>SDMS</b>
      </div>
      {/* Form-name heading + description/hint strip REMOVED (user UI feedback
          2026-09-16 — cleaner form). The form name now shows in the top-bar title
          (always "SDMS", via `ai:topbar-title`). Back = the breadcrumb / Back button. */}

      {/* Excel tabs — only the SDMS tab. The File / Home / Insert / … tabs were
          decorative (no handlers) and are removed per request. */}
      <div className="xltabs">
        {/* Minimize the Ribbon (Windows SDMS parity, 2026-10-22): double-click toggles; while minimized a single
            click shows the ribbon for one use. useSdmsRibbonMinimize. */}
        <span className={`xt on${ribbon.minimized ? ' xt-min' : ''}`} onClick={ribbon.onTabClick} onDoubleClick={ribbon.toggle}
          title={ribbon.minimized ? 'Click to show the ribbon · double-click to keep it open' : 'Double-click to minimize the ribbon'}>SDMS</span>
        {/* Date Filter — narrows the QCBatchID list to batches created within the window
            (default 3M). Shown ALWAYS now (user UI feedback 2026-09-16): the ribbon
            Test/QCBatchID lookups are live even when opened FROM a QC batch, so the date
            window applies to them too. */}
        <span className="sdms-datefilter">
          <label htmlFor="sdms-datefilter">Date Filter</label>
          <select id="sdms-datefilter" value={dateFilter}
            onChange={e => setDateFilter(e.target.value as typeof dateFilter)}>
            <option value="1M">1M</option>
            <option value="3M">3M</option>
            <option value="6M">6M</option>
            <option value="1Y">1Y</option>
            <option value="All">All</option>
          </select>
        </span>
        {ribbon.minimized && (
          <button type="button" className="sdms-rib-toggle" onClick={ribbon.toggle}
            title={`Expand the Ribbon (${formatCombo('mod+f1')})`} aria-label="Expand the Ribbon">
            <i className="ti ti-chevron-down" />
          </button>
        )}
      </div>

      {/* Excel ribbon */}
      <div className={`sdms-rib${ribbon.ribbonVisible ? '' : ' sdms-rib--hidden'}${ribbon.minimized && ribbon.ribbonVisible ? ' sdms-rib--peek' : ''}`} title={sheetBusy ? SHEET_BUSY_TIP : actionBusy ? 'Please wait — the previous action is still running.' : undefined}>
        <div className="grp">
          <div className="fld2">Mode
            <select value={mode} disabled={ribbonBusy} onChange={e => { setMode(e.target.value as 'Enter' | 'View'); setEditing(false); setBatchSel(''); setBatchText(''); setBatchBuf(null); }}>
              <option value="View">View</option>
              <option value="Enter">Enter</option>
            </select>
          </div>
          {/* Test + QCBatchID are XAF GRID lookups in OG — a dropdown containing a
              multi-COLUMN grid, not a plain list. Reuses the shared <LookupCombo>
              (promoted to components/ for this). When opened FROM a QC Batch the
              pair is fixed by the seed, so the trigger just shows that value. */}
          {/* Test + QCBatchID are ALWAYS the grid lookups now — even when SDMS was
              opened from a QC batch (was a read-only text box). Pre-filled from the seed;
              picking another test/batch + Retrieve loads it. (user UI feedback 2026-09-16) */}
          <div className="fld2">Test
            <LookupCombo lookup={testLookup} value={testText || (seed ? s.test : '')} disabled={ribbonBusy}
                  placeholder="Select test…" className="sdms-lk"
                  showFilter={false} columnFilters noFilterCols={[4]} wrapCells popupWidth={860}
                  colWidths={SDMS_TEST_COL_W}
                  onPick={row => {
                    setTestSel(testIdByRow.get(ROW_KEY(row)) ?? '');
                    setTestText(row[0] ?? '');
                    setTestTpl(testTplByRow.get(ROW_KEY(row)) ?? null);
                    // Switching Test invalidates the picked batch + loaded sheet
                    // (moved here from the [testSel,mode] effect — see loadBatchSheet).
                    setBatchSel(''); setBatchText(''); setBatchBuf(null);
                  }} />
          </div>
          <div className="fld2">QCBatchID
            <LookupCombo lookup={batchLookup} value={batchText || (seed ? qb : '')} disabled={ribbonBusy}
                  placeholder={testSel || mode === 'View' ? 'Select QC batch…' : 'Select a test first'}
                  className="sdms-lk"
                  showFilter={false} columnFilters wrapCells
                  popupWidth={mode === 'View' ? 980 : 860}
                  colWidths={mode === 'View' ? SDMS_BATCH_COL_W_VIEW : SDMS_BATCH_COL_W}
                  // Enter mode is NOT blocked. An earlier hint here told the user to
                  // "switch Mode to View to open an existing QC batch" because Enter
                  // "lists no batches" — that was wrong and made the batch unpickable
                  // in Enter at all. OG opens batches in Enter routinely: Enter covers
                  // qcstatus 0|1, i.e. Pending Result Entry AND **Pending Submission**
                  // (ctrl :2612/:2685), which is how a part-entered batch is reopened
                  // to finish it. Enter's list is only ever empty when no Test is
                  // picked, because the legacy sproc's Enter branch has no null-guard
                  // on @TestMethodID. (Fixed 2026-08-10.)
                  blockedReason={
                    mode === 'Enter' && !testSel
                      ? 'Pick a Test first — in Enter mode the QC batch list is per-test.'
                      : undefined}
                  onBlocked={r => notify.warning(r)}
                  onPick={row => {
                    // Picking a QCBatchID only SELECTS it — OG's AnalyticalBatch
                    // combo has no SelectedIndexChanged (only the Test combo does,
                    // ctrl :2181); the sheet loads on RETRIEVE, not on select.
                    setBatchSel(batchIdByRow.get(ROW_KEY(row)) ?? ''); setBatchText(row[0] ?? '');
                    setBatchBuf(null);   // clear any previously-loaded sheet
                  }} />
          </div>
        </div>
        <div className="grp">
          {/* OG: Retrieve (re)loads the batch chosen in the Test/QCBatchID combos.
              Picking a batch already loads it; this re-reads it from the server. */}
          <button className="rbtn" disabled={editing || ribbonBusy} onClick={ribbonAction(async () => {
            if (!blank) {
              // Seeded open: the ribbon Test/QCBatchID lookups are live too — Retrieve
              // loads the batch the analyst picked there. With nothing newly picked it's
              // a no-op (the seeded sheet is already shown). (user UI feedback 2026-09-16)
              if (batchSel) {
                // Current flow matches OG (the switch is allowed), but guard against silent
                // data loss: a not-yet-saved handoff (a new batch, or a staged add/remove edit)
                // is DISCARDED when the analyst jumps to another batch. Confirm first.
                // (user UI feedback 2026-09-16 — point 5.)
                if (seed?.pendingCreate && !savedOid) {
                  const ok = await confirm.confirm({
                    title: 'Discard unsaved batch?',
                    message: 'This SDMS batch has not been saved yet. Switching to another batch will discard it. Do you want to continue?',
                    tone: 'danger',
                  });
                  if (!ok) return;
                }
                void loadBatchSheet(batchSel); return;
              }
              stub('Retrieve'); return;
            }
            // OG :5507 — Enter mode + NO batch picked = "build one from here":
            // Retrieve opens the QC Batch sequence form as a modal instead of
            // loading. (OG also requires SDMSIsWrite; AI has no SDMS permission
            // wiring yet, so that gate is not reproduced — see the change log.)
            // G6 deviation (user decision 2026-09-24): OG opens the popup with no Test too, because OG's
            // QC Batch form lets the analyst pick Test/Method there; G6's seeded popup keeps them read-only,
            // so a Test must be chosen in the ribbon first.
            if (mode === 'Enter' && !batchSel && !testSel) { notify.warning('Select a Test first.'); return; }
            if (mode === 'Enter' && !batchSel) {
              // G6 addition (user request 2026-09-24): don't open an empty QC Batch form. Re-read the
              // Test lookup's pending-sample count (Sx, AI_Analysis_Sdms_Lookups — the same rule the QC
              // Batch form's sample list uses) at click time, since the ribbon list can be stale once
              // samples are batched elsewhere; the refreshed list also drops a test that ran out.
              const fresh = await qcBatchApi.sdmsTests(mode).catch(() => null);
              if (fresh) {
                setTestList(fresh);
                if (!((fresh.find(t => t.testMethodId === testSel)?.sx ?? 0) > 0)) {
                  notify.warning('There are no pending samples for the selected test.'); return;
                }
              }
              setNewBatchOpen(true); return;
            }
            if (!batchSel) { notify.warning('Pick a QC Batch first.'); return; }
            void loadBatchSheet(batchSel);
          })}><span className="gi">🔍</span>Retrieve</button>
          {perm.write && <button className="rbtn" disabled={ribbonBusy || mode !== 'Enter' || !hasBatch} onClick={ribbonAction(doSubmit)}><span className="gi">⬆️</span>Submit</button>}
          {/* Save stays ENABLED after the batch is created — OG modechange("Enter") :17590-17592
              enables Save on Enter mode + write permission alone, never on "no batch yet", so the
              analyst keeps entering results and re-saving (doSave Case B persists the workbook onto
              the minted batch). The old `|| !!savedOid` gate greyed it out right after the create
              (user OG side-by-side 2026-09-22). Only View mode disables it (OG :17689). */}
          {perm.write && <button className="rbtn" disabled={ribbonBusy || mode !== 'Enter'} onClick={ribbonAction(doSave)} title={sheetBusy ? SHEET_BUSY_TIP : savedOid ? 'Saves the entered results to this QC Batch' : 'Creates the QC Batch ID and saves the sheet'}><span className="gi">💾</span>Save</button>}
        </div>
        <div className="grp">
          {perm.create && <button className="rbtn" disabled={ribbonBusy || mode !== 'Enter'} onClick={ribbonAction(doNewCalibration)}><span className="gi">📄</span>New<br />Calibration</button>}
          {perm.write && <button className="rbtn" disabled={ribbonBusy || !testSel} onClick={ribbonAction(doAttachCalibration)}><span className="gi">📎</span>Attach<br />Calibration</button>}
          <button className="rbtn" disabled={ribbonBusy || !hasBatch} onClick={() => setQcDetailOpen(true)}><span className="gi">🧪</span>QC<br />Batch</button>
          <button className="rbtn" disabled={ribbonBusy || !hasBatch} onClick={() => setResultsOpen(true)}><span className="gi">✅</span>QC Batch<br />Results</button>
        </div>
        <div className="grp">
          {/* RollBack — View mode + saved batch (OG :16879 disabled in Enter). */}
          {perm.write && <button className="rbtn" disabled={ribbonBusy || mode !== 'View' || !hasBatch || editing} onClick={ribbonAction(doRollback)}><span className="gi">↩️</span>RollBack</button>}
          {perm.delete && <button className="rbtn" disabled={ribbonBusy || !hasBatch || (mode === 'View' && !editing)} onClick={ribbonAction(doDelete)}><span className="gi">❌</span>Delete</button>}
          {dataPackageEnabled && (
            <button className="rbtn" disabled={ribbonBusy || !hasBatch} onClick={ribbonAction(doDataPackage)}><span className="gi">📦</span>Data<br />Package</button>
          )}
          {/* Clear ↔ Cancel while editing (OG :5646 handler, :17046/:16911 enable). */}
          {perm.write && <button className="rbtn" disabled={ribbonBusy || (mode !== 'Enter' && !editing)} onClick={ribbonAction(doClear)}>
            <span className="gi">🧽</span>{editing ? 'Cancel' : 'Clear'}</button>}
          {/* Edit ↔ Update — View mode toggles the sheet editable (OG :5648). */}
          {/* Edit ↔ Update — first click makes the sheet editable, second PERSISTS
              (OG :5667; Update is not a plain toggle). Cancel is the discard path. */}
          {perm.write && <button className={`rbtn${editing ? ' active' : ''}`} disabled={ribbonBusy || mode !== 'View' || !hasBatch}
            onClick={ribbonAction(() => (editing ? doUpdate() : setEditing(true)))}
          ><span className="gi">✏️</span>{editing ? 'Update' : 'Edit'}</button>}
        </div>
        <div className="grp">
          {/* Recalculate — OG modechange: enabled only in Enter (:17293) or View while
              editing (:17441), DISABLED in View non-edit (:17454). Same gate as its
              group-mate Import Files. */}
          {/* The four sheet-changing actions below (Recalculate · Import Files · Apply Rounding ·
              Qualifiers) are hidden — not greyed — for a role without the MAC Write right. */}
          {perm.write && <button className="rbtn" disabled={ribbonBusy || !sheetEdit} onClick={ribbonAction(doRecalculate)}><span className="gi">🧮</span>Recalculate</button>}
          {perm.write && <button className="rbtn" disabled={ribbonBusy || !sheetEdit} onClick={ribbonAction(doImportFiles)}><span className="gi">📥</span>Import<br />Files</button>}
          {/* Plain action button, NO pressed/toggle state: OG's ApplyRounding is an ordinary
              RibbonButtonItem (:2315) and its "not applied" Submit warning is commented out
              (:8455), so nothing in OG shows rounding as "on". The AI-only `active` highlight
              stuck on after a click and read as a stuck toggle (user 2026-09-22). The
              roundingApplied flag is kept only as state (reset by Recalculate, like OG). */}
          {perm.write && <button className="rbtn" disabled={ribbonBusy || !sheetEdit} onClick={ribbonAction(doApplyRounding)} title={sheetBusy ? SHEET_BUSY_TIP : 'Apply rounding'}><span className="gi">🎯</span>Apply<br />Rounding</button>}
        </div>
        <div className="grp">
          <button className="rbtn" disabled={ribbonBusy || !hasBatch || !batchBuf} onClick={ribbonAction(doGenerateReport)}
            title={sheetBusy ? SHEET_BUSY_TIP : 'Generate the raw data report for this batch (OG GenerateReports)'}><span className="gi">📝</span>Report</button>
          <button className="rbtn" disabled={!hasBatch} onClick={() => setNotesOpen(true)}><span className="gi">🔔</span>Project<br />Notes{hasBatch ? ` (${notesCount})` : ''}</button>
          {perm.write && <button className="rbtn" disabled={ribbonBusy || !sheetEdit} onClick={ribbonAction(doQualifier)}><span className="gi">☑️</span>Qualifiers</button>}
          {/* Audit Trail — OG shows it in VIEW mode only (hidden in Enter, :16883). */}
          {mode === 'View' && (
            <button className="rbtn" disabled={!effBatchOid} onClick={() => setAuditOpen(true)}
              title={effBatchOid ? 'QC batch audit log' : 'Save the batch to view its audit trail'}>
              <span className="gi">📜</span>Audit<br />Trail</button>
          )}
        </div>
        <div className="grp">
          {/* OG keeps the caption "Full Screen" and just shows it as ACTIVE while
              full-screen is on (it does not switch to "Exit Screen"). */}
          <button className={`rbtn${full ? ' active' : ''}`} onClick={toggleFullScreen}
            title={full ? 'Leave full screen' : 'Full screen'} aria-pressed={full}>
            <span className="gi">⛶</span>Full<br />Screen
          </button>
        </div>
        {/* Minimize the Ribbon — Windows SDMS's arrow at the ribbon's right end (useSdmsRibbonMinimize). */}
        <button type="button" className="sdms-rib-toggle sdms-rib-toggle--end" onClick={ribbon.toggle}
          title={`${ribbon.minimized ? 'Keep the Ribbon open' : 'Minimize the Ribbon'} (${formatCombo('mod+f1')})`}
          aria-label={ribbon.minimized ? 'Keep the Ribbon open' : 'Minimize the Ribbon'}>
          <i className={`ti ${ribbon.minimized ? 'ti-pin' : 'ti-chevron-up'}`} />
        </button>
      </div>

      {/* No batch loaded → the REAL spreadsheet control (shared with the Result
          Validation preview), so columns/rows keep coming as you scroll, exactly
          like OG's blank SDMS sheet. Lazy + Suspense because Univer is a heavy
          chunk we don't want on the QC-Batch path. */}
      {/* ONE spreadsheet surface for both entry points — the real Univer control.
          Nav-opened: a blank canvas until Retrieve loads a batch. Seeded (Open
          SDMS / OK→SDMS handoff): the batch's workbook auto-loads on mount (see
          the seed-load effect). The grid stays MOUNTED across a workbook swap —
          SdmsUniverSheet reloads in place on a `buf` change (no key remount,
          which flashed white); a scrim overlays it until the new sheet has
          painted (onReady). */}
      <div className="xlwrap xluniver" onMouseDown={ribbon.closePeek}>
        {(batchBuf ?? blankBuf) ? (
          <Suspense fallback={<div className="xl-loading">Loading spreadsheet…</div>}>
            <SdmsUniverSheet ref={sheetRef} contextMenuItems={sheetContextMenu} buf={(batchBuf ?? blankBuf)!} chartSourceBuf={batchBuf ? templateBufRef.current?.buf ?? null : null} editable={sheetEdit} remerged={bufRemerged} lockedSheets={lockedSheets}
              lockedColumns={engineLockCols}
              /* Lift the "Loading the batch sheet…" scrim only once the BATCH sheet has
                 painted — NOT the empty blankBuf placeholder that mounts first (so Univer
                 inits in parallel with the merge fetch). Clearing on the blank paint was
                 the "empty sheet for a few seconds, then data" flash: the scrim lifted
                 while blankBuf was showing, before batchBuf arrived. batchBuf is always
                 set on every retrieving path, so the scrim still clears. */
              onLoadStage={(stage, pct) => {
                if (!batchBuf) return;          // the blank placeholder sheet — no batch is loading
                if (stage === 'building' && pct === null) setPrepRun(n => n + 1);   // a new build starts
                markOpen(stage === 'building' ? 'handover' : 'build');
                setSheetBuilding(true);
                setPrepStage(stage === 'building' ? 'build' : 'calc');
                setPrepPct(pct);
              }}
              onRecalc={onSheetRecalc}
              onReady={() => {
                setSheetBuilding(false);
                if (batchBuf) { setRetrieving(false); markOpen('calc'); }
                // Warm the "QC Batch Results" query in the background (no progress bar). Its SQL takes SQL Server
                // ~9 s to COMPILE and ~0.3 s to run, so the popup took ~10 s whenever the plan was not cached; the
                // popup's own request then runs on a compiled plan. Nothing is kept here, so it can never go stale.
                if (batchBuf && effBatchOid) void qcBatchApi.sdmsBatchResults(effBatchOid, { silent: true }).catch(() => undefined);
                // A just-attached calibration is an unsaved change; the [batchBuf] effect
                // reset dirty=false on the re-mount, so re-mark it here (after that effect).
                if (attachDirtyRef.current) { attachDirtyRef.current = false; setDirty(true); userEditingRef.current = true; }
                // QC calculation tips on open too (user 2026-09-24 — OG adds them only after BindGrid / an
                // engine-bound Save). Built once the values are final: after the on-open engine run on an
                // Engine B template, else now (onReady fires after Univer's compute + cache restore).
                // The panel stays up through the on-open engine run, so its values never change under the analyst.
                if (engineBRef.current) {
                  if (batchBuf) setEngineOpenBusy(true);
                  // …and until the recalculation its stamps start has settled.
                  void runEngineBCompute()
                    .then(async ok => { await sheetRef.current?.whenCalcIdle(); return ok; })
                    .finally(() => {
                      setEngineOpenBusy(false);
                      markOpen('engine'); logOpenTimes(effBatchLabel);
                    }).then(() => refreshQcNotes());
                } else {
                  if (batchBuf) logOpenTimes(effBatchLabel);
                  refreshQcNotes();
                }
                void refreshEngineLocks();       // lock the Engine B output columns (null when not engine-driven)
                void refreshQualifierNotes();    // qualifier definitions as hover notes
                // Cache the dilution columns so a NumericResult/DF edit can drive ApplyDilutionIsReport.
                if (roundMetaRef.current?.templateId) void loadDilutionCols(roundMetaRef.current);
              }}
              /* On-edit server-side recompute for Fixed-bound templates (dormant otherwise —
                 onSheetCellEdited returns immediately when engineBRef is false). */
              onCellEdited={onSheetCellEdited}
              onUserEditStart={() => { userEditingRef.current = true; }}
              /* OG's SDMS shows a formula bar above the grid (the selected cell's
                 formula, e.g. =IF(OR(Y7="",Z7=""),"",VALUE(Y7)&" - "&VALUE(Z7))).
                 Opt-in here only — the Result-Validation preview stays chrome-free. */
              formulaBar limsRound />
          </Suspense>
        ) : <div className="xl-loading">Loading spreadsheet…</div>}
        {/* The "sheet not ready" panel — until the first calculation (and the on-open Engine B compute)
            has settled; then the recalculation chip for any later recalculation that runs 1 s or more. */}
        {sheetBusy && (
          <SdmsSheetPreparing key={prepRun}
            stage={!retrieving && !sheetBuilding ? 'calc' : prepStage}
            pct={!retrieving && !sheetBuilding ? null : prepPct}
            preparing={sheetBuilding && prepStage === 'calc' && prepPct === null} />
        )}
        {!sheetBusy && !importProg && recalc && <SdmsRecalcChip pct={recalc.pct} />}
        {importProg && <SdmsImportProgress progress={importProg} />}
      </div>

      {/* Audit Trail — OG opens AuditData filtered FormName='QC Batches',
          Source = the QC batch Oid (SpreadsheetEntryController :9444). */}
      {auditOpen && effBatchOid && (
        <AuditTrailPopup formName="QC Batches" sourceId={effBatchOid} onClose={() => setAuditOpen(false)} />
      )}

      {/* Project Notes — the SAME batch-scoped notes popup the Result-Validation
          flow uses (OG "Project Notes", :9xxx). Reused, not reinvented. */}
      {notesOpen && effBatchOid && (
        <NotesListPopup
          batchId={effBatchOid}
          batchTest={effTest}
          batchLabel={effBatchLabel}
          businessObject="SDMS Result Entry"
          readOnly={!perm.write}
          onClose={() => setNotesOpen(false)}
        />
      )}

      {/* QC Batch Results — read-only (sample × analyte) grid from the same
          batch-results endpoint the Result-Validation package view reads
          (OG QCbatchIDResults → SDMSDCAB_ListView_Copy, :9301). */}
      {resultsOpen && effBatchOid && (
        <SdmsBatchResultsPopup
          batchOid={effBatchOid}
          batchLabel={effBatchLabel}
          onClose={() => setResultsOpen(false)}
        />
      )}

      {/* Create-a-QC-batch-from-SDMS modal (OG :5507 — the `QCBatchsequence`
          dashboard shown as a PopupWindow/NewModalWindow). Same QC Batch sequence
          form the Analysis Queue uses, just hosted in a dialog; its Ok hands the
          built seed back through `onSdmsSeed` instead of swapping in its own SDMS.
          NOTE: this is a deliberate deviation from standing rule 12 (New/Edit opens
          a full-page detail) — a popup is what OG does here and what was asked for. */}
      {newBatchOpen && (
        <div className="sdms-brp-scrim"
             onMouseDown={e => { if (e.target === e.currentTarget) setNewBatchOpen(false); }}>
          <div className="sdms-brp sdms-newqc" role="dialog" aria-label="New QC Batch">
            <div className="sdms-brp-hd">
              <span>New QC Batch{testText ? ` — ${testText}` : ''}</span>
              <button className="sdms-brp-x" onClick={() => setNewBatchOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="sdms-brp-body">
              <Suspense fallback={<div className="xl-loading" style={{ padding: 24 }}>Opening QC Batch…</div>}>
                <QcBatchDetailPage
                  seed={{
                    // A Test picked in the ribbon pre-scopes the form (OG keeps
                    // OidTestMethod in that case and only clears it when no Test
                    // was chosen, :5509-5517). With none, the form opens on its own
                    // Matrix → Test → Method cascade, exactly like "New" from the
                    // QC Batches list.
                    testMethodId: testSel || null,
                    test:         testText || null,
                    // Matrix + Method come from the picked test row (the ribbon keeps
                    // only testSel/text/tpl); the form seeds them directly when a
                    // testMethodId is present, so passing null left both fields blank.
                    method:       selectedTestRow?.method ?? null,
                    matrix:       selectedTestRow?.matrix ?? null,
                    // The ribbon's Test lookup already carries its SDMS TemplateID —
                    // pass it through so the QC form is on the SDMS path from the
                    // start. (It decides SDMS-vs-plain-batch on templateId; with a
                    // null it would fall through to the plain "Do you want to Save?"
                    // path instead of handing back here.)
                    templateID:   testTpl,
                  }}
                  embedded
                  onBack={() => setNewBatchOpen(false)}
                  onSdmsSeed={hs => { setNewBatchOpen(false); setHandedSeed({ ...hs, fromSdmsPopup: true }); }}
                />
              </Suspense>
            </div>
          </div>
        </div>
      )}

      {/* "QC Batch" ribbon action — OG QCBatchDetail (SpreadsheetEntryController :9644):
          the SAME `QCBatchsequence` dashboard the New flow hosts above, shown as a
          NewModalWindow POPUP over the sheet (not a full-page swap, which is what AI did),
          seeded with the OPEN batch. Edit-vs-view follows the batch STATUS exactly as OG:
          `mode` is derived from that status on load (:1306 — 0|1 → Enter, >1 → View) and a
          just-created batch is Enter, so `viewOnly = mode !== 'Enter'` — the popup opens
          editable (add/remove QC, Room Temp/Humidity, Others tab) for a Pending batch and
          read-only once submitted. Its Ok hands a staged add/remove back through
          `onSdmsSeed` (the existing Case A-update: updateBatch + persist on SDMS Save),
          mirroring OG's Dc_Accepting_QCbatchPopUp handoff (:10245). (2026-09-22) */}
      {qcDetailOpen && effBatchOid && (
        <div className="sdms-brp-scrim"
             onMouseDown={e => { if (e.target === e.currentTarget) setQcDetailOpen(false); }}>
          <div className="sdms-brp sdms-newqc" role="dialog" aria-modal="true" aria-label="QC Batch">
            <div className="sdms-brp-hd">
              <span>QC Batch{effBatchLabel ? ` — ${effBatchLabel}` : ''}</span>
              <button className="sdms-brp-x" onClick={() => setQcDetailOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="sdms-brp-body">
              <Suspense fallback={<div className="xl-loading" style={{ padding: 24 }}>Opening QC Batch…</div>}>
                <QcBatchDetailPage
                  seed={{
                    testMethodId: null, test: effTest || null, method: s.method ?? null,
                    matrix: s.matrix ?? null, templateID: null,
                    existingAbOid: effBatchOid, viewOnly: mode !== 'Enter',
                  }}
                  embedded
                  onBack={() => setQcDetailOpen(false)}
                  onSdmsSeed={hs => { setQcDetailOpen(false); setHandedSeed({ ...hs, fromSdmsPopup: true }); }}
                />
              </Suspense>
            </div>
          </div>
        </div>
      )}

      {calibListOpen && (
        <div className="sdms-brp-scrim"
             onMouseDown={e => { if (e.target === e.currentTarget) setCalibListOpen(false); }}>
          <div className="sdms-brp" role="dialog" aria-label="Open Calibration" style={{ width: 640, maxWidth: '92vw' }}>
            <div className="sdms-brp-hd">
              <span>Open Calibration{testText ? ` — ${testText}` : ''}</span>
              <button className="sdms-brp-x" onClick={() => setCalibListOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="sdms-brp-body" style={{ maxHeight: '60vh', overflow: 'auto' }}>
              <table className="adg" style={{ width: '100%' }}>
                <thead><tr><th>Calibration ID</th><th>Created</th><th>By</th><th>Status</th></tr></thead>
                <tbody>
                  {calibList.map(c => (
                    <tr key={c.oid} style={{ cursor: 'pointer' }}
                        onClick={() => void doOpenCalibration(c.oid, c.calibrationId)}>
                      <td>{c.calibrationId ?? '—'}</td>
                      <td>{c.createdDate ? fmtLocalDate(c.createdDate) : '—'}</td>
                      <td>{c.createdByName ?? '—'}</td>
                      <td>{c.status ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {pkgOpen && (
        <SdmsPackageDetail
          batch={pkgRow()}
          approval={false}
          canWrite={sheetEdit}
          windowed  /* OG TargetWindow.NewModalWindow — show as a centered popup, not full-content */
          fromSdms  /* opened from inside SDMS → hide the redundant SDMS button (user 2026-09-16) */
          packageCreatedBy={pkgCreated.by} packageCreatedDate={pkgCreated.date}  /* OG header (persisted) */
          packageStatus={pkgStatus}  /* Submit (PendingSubmission) vs Validate (PendingValidation) */
          allBatches={[{
            id: (savedOid || effBatchOid)!, batchId: effBatchLabel || savedQb || s.qcBatchId || null,
            test: effTest || s.test || null, method: s.method ?? null, matrix: s.matrix ?? null,
            templateId: seed?.pendingCreate?.templateId ?? null, templateName: null, jobIds: null,
            statusText: '',
          }]}
          onClose={() => {
            setPkgOpen(false);
            // OG View_Closing: main view SDMS + batch now submitted + Enter mode → open the Analysis Queue
            // (only when the user has that navigation item; otherwise stay, as before). The QC Batch screen's
            // own Data Package is a different component and keeps its behaviour.
            if (submittedInPkgRef.current) {
              submittedInPkgRef.current = false;
              navigateToPage('analysisq', undefined, { onlyIfAllowed: true });
            }
          }}
          onRefresh={() => { /* entry screen has no list to reload */ }}
          onValidated={() => { /* validation happens in the package view; entry list N/A */ }}
          onSubmitted={() => {
            // OG QBIDSubmit advanced the batch to Pending Validation from inside the package —
            // reflect that on the entry screen (drop to View) so the submitted batch can't be re-edited.
            if (mode === 'Enter') submittedInPkgRef.current = true;
            setSubmitted(true); setMode('View'); setEditing(false);
          }}
        />
      )}

      {/* Report picker — OG SDMSReportPopupDC_ListView (:9391-9414): shown only when the
          template has MORE than one raw-data report; Ok previews the selected one. */}
      {reportPick && (
        <AppDialog open title="SDMS" width={480} onClose={() => setReportPick(null)}
          footer={
            <>
              <button className="ca-btn" onClick={() => setReportPick(null)}>Cancel</button>
              <button className="ca-btn ca-btn-solid" disabled={reportPickId == null} onClick={async () => {
                const pick = reportPick; const id = reportPickId;
                if (!pick || id == null) { notify.info('Please select a report.'); return; }
                setReportPick(null);
                try { await qcBatchApi.openSdmsReport(effBatchOid!, id, pick.data); }
                catch (e) { notify.error(await reportErrMsg(e, 'Could not generate the report.')); }
              }}>OK</button>
            </>
          }>
          <div className="sdms-report-pick" role="listbox" aria-label="Report">
            {reportPick.info.reports.map(r => (
              <label key={r.reportId} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 2px', cursor: 'pointer' }}>
                <input type="radio" name="sdms-report" checked={reportPickId === r.reportId}
                  onChange={() => setReportPickId(r.reportId)} data-unsaved-ignore />
                <span>{r.name}</span>
              </label>
            ))}
          </div>
        </AppDialog>
      )}

      <SdmsImportFilesDialog open={importOpen} accept=".csv,.txt,.xls,.xlsx" transferNames={importTransferNames}
        onClose={() => setImportOpen(false)} onImport={runImportFiles} />

      {qualOpen && (
        <SdmsQualifierPicker
          initial={qualInitial}
          onOk={(symbols) => {
            const t = qualTargetRef.current;
            if (t) {
              const ok = sheetRef.current?.setCellValue(t.sheetName, t.row, t.col, symbols);
              if (ok) notify.success('Qualifier applied.');
              else notify.error('Could not write the qualifier to the cell.');
            }
            setQualOpen(false);
          }}
          onCancel={() => setQualOpen(false)}
        />
      )}
    </div>
  );
}


/** Read the analyst's entered readings back from the merged calibration workbook.
 *  Each `Level{n}` sheet's detail band starts at the PARAMETER cell's row; PARAMETER
 *  (col) identifies the row and READING (col) holds the value. Matched to the merge
 *  records by (level, parameter name) to recover the parameter identity keys the save
 *  needs. fieldCells comes from the calibration merge endpoint (SpreadSheetBuilder_
 *  DataParsing), e.g. { PARAMETER: 'A19', READING: 'D19' }. */
async function extractCalibrationRows(
  buf: ArrayBuffer,
  fieldCells: Record<string, string>,
  records: Record<string, unknown>[],
): Promise<Array<{ levelNo: number; uqTestParameterId: string | null; parameterId: string | null; reading: number | null }>> {
  const parse = (a1?: string) => {
    const m = /^([A-Z]+)(\d+)$/.exec((a1 ?? '').toUpperCase());
    if (!m) return null;
    let col = 0; for (const ch of m[1]!) col = col * 26 + (ch.charCodeAt(0) - 64);
    return { col, row: parseInt(m[2]!, 10) };
  };
  const par = parse(fieldCells.PARAMETER);
  const rdg = parse(fieldCells.READING);
  if (!par || !rdg) return [];
  const key = (lvl: unknown, name: unknown) => `${lvl}|${String(name ?? '').trim().toUpperCase()}`;
  const recByKey = new Map<string, Record<string, unknown>>();
  for (const r of records) recByKey.set(key(r.LEVELNO, r.PARAMETER), r);
  const num = (v: unknown) => (v == null || v === '' ? null : Number(v));
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const out: Array<{ levelNo: number; uqTestParameterId: string | null; parameterId: string | null; reading: number | null }> = [];
  wb.eachSheet((ws) => {
    const m = /^Level(\d+)$/i.exec(ws.name);
    if (!m) return;
    const level = parseInt(m[1]!, 10);
    for (let row = par.row; row <= ws.rowCount; row++) {
      const pCell = ws.getRow(row).getCell(par.col);
      const pName = String((pCell.result ?? pCell.value ?? pCell.text ?? '')).trim();
      if (!pName) break;   // blank parameter = end of the detail band
      const rCell = ws.getRow(row).getCell(rdg.col);
      const rec = recByKey.get(key(level, pName));
      out.push({
        levelNo: level,
        uqTestParameterId: (rec?.UQTESTPARAMETERID as string) ?? null,
        parameterId: (rec?.PARAMETERID as string) ?? null,
        reading: num(rCell.result ?? rCell.value),
      });
    }
  });
  return out;
}

/** Sheet names that are NOT result-band sheets (sub-sheets OG never rounds). */
/** Apply Rounding (Result column only): wrap each Result-band cell's formula as
 *  =_LIMSRound(<inner>, sig, cut, dec, rpt, def) using the row's parameter settings from the
 *  merge records (matched by SysSampleCode+Parameter). _LIMSRound is a live Univer function.
 *  Idempotent: a cell already wrapped in _LIMSRound is left as-is. Returns the count wrapped. */
/** Per-parameter rounding settings (result + %Recovery + RPD), from GET sdms/rounding-settings. */
export type RoundParamSettings = {
  parameterName: string | null;
  /** OG ResolveTestparameter keys: a normal parameter resolves by name + QC type + Default
   *  component; an IS/Surrogate parameter by name alone. */
  qcTypeName: string | null; isDefaultComponent: boolean; isIsOrSurrogate: boolean;
  sigFig: string | null; cutOff: string | null; decimalPlaces: string | null;
  rptLimit: string | null; defaultResult: string | null;
  recSigFig: string | null; recCutOff: string | null; recDecimal: string | null;
  rpdSigFig: string | null; rpdCutOff: string | null; rpdDecimal: string | null;
};

/** Apply Rounding: wrap each Result-band row's Result / %Recovery / RPD cells as
 *  =_LIMSRound(inner, sig,cut,dec,rpt,def) using that parameter's settings (matched by the
 *  Parameter cell). Works on ANY loaded sheet (freshly merged or retrieved) since the settings
 *  come from the per-parameter map, not the per-run merge records. Idempotent; blank cells skipped.
 *  %Recovery + RPD use their REC / RPD settings with rptLimit and defaultResult omitted (OG). */
/** OG ResolveTestparameter (SpreadsheetEntryController :26084) as a lookup: an Internal-Standard /
 *  Surrogate parameter matches by NAME alone (QC-type-agnostic); every other parameter matches by
 *  name + the sheet row's QC TYPE + `Component = 'Default'`. Keying by parameter alone (the old
 *  `byParam`) let whichever Testparameter row came last win, so Alkalinity's Dup RPD settings
 *  (3 / 10 / 1) were replaced by a sibling QC type's NULLs and the Dup RPD rounded to "0" instead
 *  of OG's "0.0" (user side-by-side 2026-09-22). */
export type RoundResolver = (parameterName: string, qcType: string | null) => RoundParamSettings | undefined;
export function buildRoundResolver(settings: readonly RoundParamSettings[]): RoundResolver {
  const isOrSurr = new Map<string, RoundParamSettings>();
  const normal   = new Map<string, RoundParamSettings>();
  for (const r of settings) {
    const p = (r.parameterName ?? '').trim().toUpperCase();
    if (!p) continue;
    if (r.isIsOrSurrogate) { if (!isOrSurr.has(p)) isOrSurr.set(p, r); continue; }
    if (!r.isDefaultComponent) continue;                       // OG: [Component.Components] = 'Default'
    const q = (r.qcTypeName ?? '').trim().toUpperCase();
    if (q && !normal.has(`${p}|${q}`)) normal.set(`${p}|${q}`, r);
  }
  return (parameterName, qcType) => {
    const p = parameterName.trim().toUpperCase();
    const hit = isOrSurr.get(p);
    if (hit) return hit;
    const q = (qcType ?? '').trim().toUpperCase();
    return q ? normal.get(`${p}|${q}`) : undefined;            // OG: no QC type → no match → skip
  };
}

export async function applyResultRounding(
  buf: ArrayBuffer,
  fieldCells: Record<string, string>,
  resolve: RoundResolver,
  handle: SdmsUniverHandle,
  /** Which sheets to round -- OG's ApplyRounding: the main sheet (Model 0) or the per-run sheets
   *  (Model 1), from resultSheetSelector. Default (tests / legacy callers): main sheet = index 0. */
  allowSheet?: (sheetName: string) => boolean,
): Promise<number> {
  const parse = (a1?: string) => {
    const m = /^([A-Z]+)(\d+)$/.exec((a1 ?? '').toUpperCase());
    if (!m) return null;
    let col = 0; for (const ch of m[1]!) col = col * 26 + (ch.charCodeAt(0) - 64);
    return { col, row: parseInt(m[2]!, 10) };   // 1-based
  };
  const res = parse(fieldCells.RESULT);
  const par = parse(fieldCells.PARAMETER);
  const rec = parse(fieldCells['%RECOVERY']);
  const rpd = parse(fieldCells.RPD);
  // The row's QC type — OG GetParsingPosition(drrSelect, "QCType"); templates name the column
  // QCType / SampleType / RunType. Without one OG's normal lookup never matches (no rounding).
  const qct = parse(fieldCells.QCTYPE) ?? parse(fieldCells.SAMPLETYPE) ?? parse(fieldCells.RUNTYPE);
  if (!res || !par) return 0;
  const startRow = res.row;
  // Every setting goes in QUOTED, exactly as stored, and a missing one as "" — OG QuoteForExcel (RoundCellInPlace
  // :26909-26978): OG writes _LIMSRound(x,"3","10.0","1","",""), AI wrote _LIMSRound(x,3,10,1,,), dropping the stored
  // text ("10.0") and leaving empty arguments in a blob OG opens (side-by-side VOC batches 2026-09-30).
  const arg = (v: unknown): string => `"${v === null || v === undefined ? '' : String(v).replace(/"/g, '""')}"`;
  // OG BuildNumberFormat (RoundCellInPlace :26264): the display format is derived from the ROUNDED
  // value's own decimals (e.g. 91.0 -> "0.0", 0.00456 -> "0.00000"), NOT the Decimal setting —
  // so a sig-fig result keeps all its figures. There is deliberately NO Decimal-setting fallback:
  // the old `numFmtFor(dec)` fallback fired whenever the EXPORTED cell carried no cached result
  // (every formula cell on a freshly merged sheet) and forced e.g. Decimal 0/NULL -> "0"/General,
  // which wiped the decimals off NumericResult-driven Result cells (user OG side-by-side
  // 2026-09-22). A cell with no numeric value keeps its template format, as OG leaves it.
  const buildNumberFormat = (roundedStr: string): string => {
    const dot = roundedStr.indexOf('.');
    if (dot < 0) return '0';
    const decimals = roundedStr.length - dot - 1;
    return decimals <= 0 ? '0' : '0.' + '0'.repeat(Math.min(decimals, 20));
  };
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  // The wraps are queued and written in ONE batch per sheet (setCellFormulas): one write per cell (446 on VOC)
  // re-rendered and re-tracked the sheet each time and held a Save for minutes (user 2026-09-29).
  const edits: Array<{ sheetName: string; row: number; col: number; formula: string; numberFormat?: string }> = [];
  const wrapCell = (ws: import('exceljs').Worksheet, row: number, col: number,
    sig: unknown, cut: unknown, dec: unknown, rpt: unknown, def: unknown): boolean => {
    const cell = ws.getRow(row).getCell(col);
    // Already wrapped in the LIVE sheet (Apply Rounding pressed, then again on Save): its value is the ROUNDED one,
    // and rounding it again reset the format — PFAS Rec % 99.97 → "100.0" (0.0) became "100" (0). OG rounds once.
    // Asked of the live sheet, not the export, which may not carry the wrap yet (side-by-side PFAS, 2026-10-01).
    const liveF = handle.getCellFormula?.(ws.name, row - 1, col - 1);
    if (liveF && /_LIMSRound\s*\(/i.test(liveF)) return false;
    const fv = cell.value as { formula?: string } | number | string | null;
    // The STORED value, not the displayed one: the display applies the cell's number format first (99.97 in a 0.0
    // cell reads "100.0"), and rounding that rounded the value twice: PFAS Rec % saved "100" where OG has "100.0"
    // (side-by-side QB26100102_001 / QB26100101_001, 2026-10-01). OG RoundCellInPlace rounds the cell's real value.
    const live = handle.getCellRawValue?.(ws.name, row - 1, col - 1) ?? handle.getCellValue(ws.name, row - 1, col - 1);
    // OG RoundCellInPlace (:26882) rounds a cell only when its CURRENT value is a number or numeric text; an empty,
    // "<0.5" or other text value is left as it is (formula included). AI wrapped every Result / Rec / RPD formula
    // cell: 1464 wraps on VOC against OG's 446 (comparison 2026-09-29).
    const current = live !== null && live.trim() !== '' ? live
      : (fv && typeof fv === 'object' && 'result' in fv) ? (fv as { result?: unknown }).result : fv;
    if (current === null || current === undefined || String(current).trim() === '' || !Number.isFinite(Number(String(current).trim()))) return false;
    let inner: string;
    // A formula the page set at runtime (an import splice) wins over the workbook's: the save persists it (getEditedXlsx),
    // so wrapping the stored one would round a value the cell no longer computes.
    const runtimeF = handle.getRuntimeFormula?.(ws.name, row - 1, col - 1);
    if (runtimeF) inner = runtimeF;
    else if (fv && typeof fv === 'object' && typeof fv.formula === 'string') inner = fv.formula;
    else if (typeof cell.formula === 'string' && cell.formula) inner = cell.formula;
    else {
      // A VALUE cell (a pure FIELD cell the merge filled — TOC's Result / Rec % / RPD %): wrap what the
      // sheet SHOWS NOW, not the exported copy. The export is the merged buffer patched with tracked
      // edits only, so it could still hold the SAVED value (Dup1 1.2, MS1 Rec 78.0) while the sheet
      // showed Engine B's (1.0, -138.0) — Apply Rounding then put the stale values back (user
      // 2026-09-24). OG RoundCellInPlace wraps the cell's current value.
      const v = live !== null && live.trim() !== '' ? live : fv;
      if (v === null || v === undefined || String(v).trim() === '') return false;   // nothing to round
      const n = typeof v === 'number' ? v : Number(String(v).trim());
      inner = typeof v === 'number' || (String(v).trim() !== '' && Number.isFinite(n))
        ? String(n) : `"${String(v).replace(/"/g, '""')}"`;
    }
    if (/_LIMSRound\s*\(/i.test(inner)) return false;   // already wrapped
    const formula = `=_LIMSRound(${inner},${arg(sig)},${arg(cut)},${arg(dec)},${arg(rpt)},${arg(def)})`;
    // Per-value trailing-zero format (OG): round the cell's CURRENT value the same way and take
    // its decimals. Read the LIVE value Univer holds (a formula cell's computed result) — the
    // exported xlsx has no cached result for formula cells on a freshly merged sheet, which is
    // exactly when the old code fell back to the Decimal setting and stripped the decimals.
    // The exported cached value is only a secondary source. No numeric value -> leave the format.
    const cached = (fv && typeof fv === 'object' && 'result' in fv) ? (fv as { result?: unknown }).result : fv;
    const raw = live !== null && live.trim() !== '' ? live : cached;
    let nf: string | undefined;
    const inNum = Number(raw);
    if (raw !== null && raw !== undefined && String(raw).trim() !== '' && Number.isFinite(inNum)) {
      const rtext = limsRoundText(inNum, sig, cut, dec, rpt, def);
      if (/^-?\d*\.?\d+$/.test(rtext)) nf = buildNumberFormat(rtext);
    }
    edits.push({ sheetName: ws.name, row: row - 1, col: col - 1, formula, numberFormat: nf });
    return true;
  };
  const allow = allowSheet ?? ((name: string) => name === wb.worksheets[0]?.name);
  for (const ws of wb.worksheets) {
    if (!allow(ws.name)) continue;
    for (let row = startRow; row <= ws.rowCount; row++) {
      const pCell = ws.getRow(row).getCell(par.col);
      const pName = String((pCell.result ?? pCell.value ?? pCell.text ?? '')).trim();
      if (!pName) break;   // end of the detail band
      // QC type from the LIVE sheet (a merged FIELD cell — the export has it too, but live is
      // authoritative after edits); the Sample rows carry "Sample", matching the tp's QCType.
      const qcType = qct ? (handle.getCellValue(ws.name, row - 1, qct.col - 1)
        ?? String(ws.getRow(row).getCell(qct.col).value ?? '')) : null;
      const st = resolve(pName, qcType);
      if (!st) continue;
      wrapCell(ws, row, res.col, st.sigFig, st.cutOff, st.decimalPlaces, st.rptLimit, st.defaultResult);
      if (rec) wrapCell(ws, row, rec.col, st.recSigFig, st.recCutOff, st.recDecimal, null, null);
      if (rpd) wrapCell(ws, row, rpd.col, st.rpdSigFig, st.rpdCutOff, st.rpdDecimal, null, null);
    }
  }
  if (!edits.length) return 0;
  if (handle.setCellFormulas) return handle.setCellFormulas(edits);
  return edits.filter(e => handle.setCellFormula(e.sheetName, e.row, e.col, e.formula, e.numberFormat)).length;
}

/** SDMS Import Files write-back: stamp each server-parsed instrument row's field values into the
 *  matching result-band row, keyed by (SysSampleCode|Parameter). Mirrors writeEngineResultsToSheet
 *  but writes the ARBITRARY fields the server returned (NUMERICRESULT / READING / USERDEFINED* / …),
 *  each positioned via the template's fieldCells (FIELD→A1). setCellValue no-ops in read-only mode.
 *
 *  A field whose template cell EMBEDS its token in a formula (HAA J8 NUMERICRESULT, VOC J8, THM BA8
 *  ANALYZEDDATE, PFAS_533_Waters J8 `IF(ISTD,TEXT(G8,0),FIELD("USERDEFINED14"))`) keeps its formula:
 *  OG re-binds the sheet from dtsample, so only the token's value changes. With `templateBuf` (the
 *  pristine template) the cell's formula is rebuilt with the new value spliced in
 *  (resubstituteFieldTokens); a pure-FIELD cell, or a formula that cannot be matched, is stamped as a
 *  literal as before.
 *
 *  `zeroFillField` (from the server — OG's post-import zero-fill, SpreadsheetEntryController :22703-22718):
 *  after the file values, every band row whose field is STILL blank — a run no file reached, e.g. VOC LOQ1 —
 *  gets "0.000" the same way, except THAA5. So such a run shows 0.00 / 0.0 % / Fail as in OG instead of
 *  staying blank and hiding the failure (OG vs AI comparison 2026-09-28, defect 2). Returns the number of
 *  cells written. */
export async function writeImportedValuesToSheet(
  buf: ArrayBuffer,
  fieldCells: Record<string, string>,
  writes: readonly SdmsImportWrite[],
  handle: SdmsUniverHandle,
  templateBuf?: ArrayBuffer | null,
  zeroFillField?: string | null,
): Promise<number> {
  const parse = (a1?: string) => {
    const m = /^([A-Z]+)(\d+)$/.exec((a1 ?? '').toUpperCase());
    if (!m) return null;
    let col = 0; for (const ch of m[1]!) col = col * 26 + (ch.charCodeAt(0) - 64);
    return { col, row: parseInt(m[2]!, 10) };   // 1-based
  };
  const par = parse(fieldCells.PARAMETER);
  if (!par || writes.length === 0) return 0;
  const sysP = parse(fieldCells.SYSSAMPLECODE);
  const startRow = par.row;
  const norm = (s: string) => s.trim().toUpperCase();
  const rowKey = (s: string, p: string) => `${norm(s)}|${norm(p)}`;
  const byKey = new Map<string, SdmsImportWrite>();
  for (const w of writes) byKey.set(rowKey(w.sysSampleCode, w.parameter), w);
  // Where each imported value goes. OG re-merges the imported rowset into the whole template after an import
  // (GenerateMailMerge(..., true) in Dc_AcceptingImport), so a value lands in EVERY cell that shows its
  // FIELD("<field>") token: on the band row, each cell of the template's main sheet that shows it (a pure FIELD cell
  // takes the value as text, a formula embedding it is re-spliced); above the band, each header cell that shows it
  // (the sheet's last matched row's value). THM shows ANALYZEDDATE in AB8 besides its bound BA8 and USERDEFINED2 in
  // D2/E2 besides its bound U8, and NUMERICRESULT only in BC8 while binding J8, a formula over BC8 - AI filled one
  // cell per field, or the wrong one (OG vs AI THM comparisons 2026-09-29). A field no template cell shows keeps the
  // old rule: its DataParsing position (a header cell when above the band, else that band column).
  type Target = { row: number; col: number; formula: string | null; shows: boolean };
  const bandTargets = new Map<string, Target[]>();
  const headTargets = new Map<string, Target[]>();
  const addTarget = (map: Map<string, Target[]>, field: string, t: Target) => {
    const list = map.get(field);
    if (list) { if (!list.some(x => x.row === t.row && x.col === t.col)) list.push(t); } else map.set(field, [t]);
  };
  const bare = (n: string) => n.trim().toUpperCase().replace(/^\[(.+)\]$/, '$1');
  const zeroField = zeroFillField ? bare(zeroFillField) || null : null;
  const fieldNames = new Set(writes.flatMap(w => w.fields.map(f => bare(f.field))));
  if (zeroField) fieldNames.add(zeroField);
  const tokenRe = /FIELD\("([^"]*)"\)|\[\[([^\]]+)\]\]/gi;
  const pureRe = /^\s*=?\s*(?:FIELD\("([^"]*)"\)|\[\[([^\]]+)\]\])\s*$/i;
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  let twb: import('exceljs').Workbook | null = null;
  if (templateBuf) {
    try {
      twb = new ExcelJS.Workbook();
      await twb.xlsx.load(templateBuf.slice(0));
      const main = twb.worksheets[0];
      if (main) for (let r = 1; r <= startRow; r++) {
        main.getRow(r).eachCell({ includeEmpty: false }, (c, col) => {
          const text = typeof c.formula === 'string' ? c.formula : typeof c.value === 'string' ? c.value : null;
          if (text === null) return;
          const pure = pureRe.exec(text);
          if (!pure && typeof c.formula !== 'string') return;   // text that merely mentions a token is left alone
          const named = pure ? [bare(pure[1] ?? pure[2] ?? '')] : [...text.matchAll(tokenRe)].map(m => bare(m[1] ?? m[2] ?? ''));
          for (const field of new Set(named)) {
            if (!fieldNames.has(field)) continue;
            addTarget(r < startRow ? headTargets : bandTargets, field, { row: r, col, formula: pure ? null : text, shows: true });
          }
        });
      }
    } catch (e) {
      console.warn('[SDMS] import: template unreadable - values go to their bound cells', e);
      twb = null;
    }
  }
  // Fallback: a field no template cell shows goes to its bound DataParsing cell (a formula there embedding it, on any
  // template sheet, is still re-spliced).
  for (const field of fieldNames) {
    if (bandTargets.has(field) || headTargets.has(field)) continue;
    const at = parse(fieldCells[field] ?? fieldCells['[' + field + ']']);
    if (!at) continue;
    if (at.row < startRow) { addTarget(headTargets, field, { row: at.row, col: at.col, formula: null, shows: false }); continue; }
    let formula: string | null = null;
    for (const tws of twb?.worksheets ?? []) {
      const f = tws.getRow(at.row).getCell(at.col).formula;
      if (typeof f !== 'string' || pureRe.test(f)) continue;
      if ([...f.matchAll(tokenRe)].some(m => bare(m[1] ?? m[2] ?? '') === field)) { formula = f; break; }
    }
    addTarget(bandTargets, field, { row: at.row, col: at.col, formula, shows: false });
  }
  // The zero-fill checks the field's own cell: a pure FIELD cell first.
  const zeroTargets = zeroField ? bandTargets.get(zeroField) ?? [] : [];
  const zeroCol = (zeroTargets.find(t => t.formula === null) ?? zeroTargets[0])?.col ?? 0;
  // Blank NOW: the live sheet value when the sheet can say (a formula's computed result), else the exported
  // cell - where a formula with no cached result counts as blank (its object must not read as text).
  const isBlankNow = (ws: import('exceljs').Worksheet, row: number, col: number): boolean => {
    const live = handle.getCellValue?.(ws.name, row - 1, col - 1);
    if (live != null) return live.trim() === '';
    const c = ws.getRow(row).getCell(col);
    const v = c.value as unknown;
    if (v && typeof v === 'object' && 'formula' in (v as object)) return String(c.result ?? '').trim() === '';
    return String(v ?? '').trim() === '';
  };
  const cellText = (ws: import('exceljs').Worksheet, row: number, col?: number): string => {
    if (!col) return '';
    return sheetCellText(ws.getRow(row).getCell(col));
  };
  // A literal. A date bound for a date cell goes in as a real date (serial), as OG's DateTime rowset column puts it;
  // text made formulas reading it (VOC ACQTIME = $AY$8) cache text (point 12). Any other value in a cell that shows
  // the FIELD token is FIELD text: saved as text, as DevExpress FIELD() writes it (THM FileResults "9.8").
  // The writes are COLLECTED and handed to the sheet in bulk at the end (handle.setCellValues / setCellFormulas,
  // one Univer command per contiguous run of cells in a column, 2026-10-01): each single setCellValue makes Univer
  // rebuild every sheet's formula data, ~16 ms a cell on a VOC batch, so the 2,941 stamps of a 16-file VOC import
  // took 46 s (PFAS_533_Waters 2,145 cells: 33 s). Nothing read below depends on a write made above - a band row's
  // cells are its own, isBlankNow looks only at that row's zero-fill cell, and the header cells came last in the
  // per-cell order too - so the deferred writes give the same sheet (verified cell for cell on the four templates).
  // A handle without the bulk methods (tests) takes the per-cell path, call for call in the old order.
  type ValueEdit = { kind: 'value'; sheetName: string; row: number; col: number; value: string | number; opts?: { text?: boolean } };
  type FormulaEdit = { kind: 'formula'; sheetName: string; row: number; col: number; formula: string };
  const pending: Array<ValueEdit | FormulaEdit> = [];
  const stamp = (ws: import('exceljs').Worksheet, row: number, col: number, value: string, shows: boolean): void => {
    const tc = ws.getRow(row).getCell(col);
    const serial = importDateSerial(value, tc.value, tc.numFmt);
    pending.push({ kind: 'value', sheetName: ws.name, row: row - 1, col: col - 1, value: serial ?? value,
      opts: shows && serial === null ? { text: true } : undefined });
  };
  const setFormula = (ws: import('exceljs').Worksheet, row: number, col: number, formula: string): void => {
    pending.push({ kind: 'formula', sheetName: ws.name, row: row - 1, col: col - 1, formula });
  };
  let count = 0;
  for (const ws of wb.worksheets) {
    if (!isResultSheet(ws.name, wb.worksheets.indexOf(ws))) continue;
    const headerVals = new Map<string, string>();   // field -> the last matched row's value on this sheet
    for (let row = startRow; row <= ws.rowCount; row++) {
      const pName = cellText(ws, row, par.col);
      if (!pName) break;   // end of the detail band
      // Per-sample-sheet templates name each sheet by its SysSampleCode.
      const sys = sysP ? cellText(ws, row, sysP.col) : ws.name;
      const hit = byKey.get(rowKey(sys, pName));
      const fields: Array<{ field: string; value: string | null }> = hit ? [...hit.fields] : [];
      // OG's zero-fill: this row's field is still blank after the files (none gave it a value, and the sheet
      // holds none) -> "0.000", spliced like an imported value. THAA5 is the one exception.
      if (zeroField && zeroCol && pName.toUpperCase() !== 'THAA5'
          && !fields.some(f => bare(f.field) === zeroField && f.value != null && f.value !== '')
          && isBlankNow(ws, row, zeroCol))
        fields.push({ field: zeroField, value: '0.000' });
      if (!fields.length) continue;
      // Group the row's values by target cell - one formula can embed several imported tokens.
      const byCol = new Map<number, { formula: string | null; tplRow: number; shows: boolean; vals: Array<{ field: string; value: string }> }>();
      for (const f of fields) {
        if (f.value == null || f.value === '') continue;
        const field = bare(f.field);
        if (headTargets.has(field)) headerVals.set(field, f.value);
        for (const t of bandTargets.get(field) ?? []) {
          let slot = byCol.get(t.col);
          if (!slot) { slot = { formula: t.formula, tplRow: t.row, shows: t.shows, vals: [] }; byCol.set(t.col, slot); }
          else if (t.formula && !slot.formula) { slot.formula = t.formula; slot.tplRow = t.row; }
          slot.vals.push({ field, value: f.value });
        }
      }
      for (const [col, slot] of byCol) {
        const curCell = ws.getRow(row).getCell(col);
        const cur = curCell.formula;
        if (slot.formula && typeof cur === 'string') {
          // A date-formatted cell takes a date as its serial (OG HAA BF8), not quoted text.
          const next = resubstituteFieldTokens(slot.formula, row - slot.tplRow, cur,
            Object.fromEntries(slot.vals.map(v => [v.field, v.value])), isDateNumFmt(curCell.numFmt));
          if (next != null) { setFormula(ws, row, col, '=' + next); continue; }
          console.warn('[SDMS] import: ' + ws.name + ' R' + row + 'C' + col + ' formula did not match the template - value stamped');
        }
        for (const v of slot.vals) stamp(ws, row, col, v.value, slot.shows);
      }
    }
    // Header cells: the sheet's last matched row's value, once per cell (a formula embedding several is spliced once).
    const headCells = new Map<string, { t: Target; vals: Record<string, string> }>();
    for (const [field, value] of headerVals) for (const t of headTargets.get(field) ?? []) {
      const key = t.row + ',' + t.col;
      const cell = headCells.get(key) ?? headCells.set(key, { t, vals: {} }).get(key)!;
      cell.vals[field] = value;
    }
    for (const { t, vals } of headCells.values()) {
      const curCell = ws.getRow(t.row).getCell(t.col);
      const cur = curCell.formula;
      if (t.formula && typeof cur === 'string') {
        const next = resubstituteFieldTokens(t.formula, 0, cur, vals, isDateNumFmt(curCell.numFmt));
        if (next != null) { setFormula(ws, t.row, t.col, '=' + next); continue; }
      }
      const value = Object.values(vals).pop();
      if (value !== undefined) stamp(ws, t.row, t.col, value, t.shows);
    }
  }
  // Bulk write: per sheet and column, each contiguous run of rows as ONE call, so the command's effected range is
  // exactly the cells written (the SheetValueChanged listeners then see the same cells the per-cell path showed them).
  // A cell is in one list only (a formula slot never also stamps), so the two orders are independent.
  const runsOf = <T extends { sheetName: string; row: number; col: number }>(edits: readonly T[]): T[][] => {
    const byKey = new Map<string, T[]>();
    for (const e of edits) { const k = `${e.sheetName}\u0000${e.col}`; const l = byKey.get(k); if (l) l.push(e); else byKey.set(k, [e]); }
    const runs: T[][] = [];
    for (const list of byKey.values()) {
      const sorted = [...list].sort((a, b) => a.row - b.row);
      let i = 0;
      while (i < sorted.length) {
        let j = i;
        while (j + 1 < sorted.length && sorted[j + 1]!.row <= sorted[j]!.row + 1) j++;
        runs.push(sorted.slice(i, j + 1)); i = j + 1;
      }
    }
    return runs;
  };
  if (handle.setCellFormulas && handle.setCellValues) {
    for (const run of runsOf(pending.filter((e): e is FormulaEdit => e.kind === 'formula'))) count += handle.setCellFormulas(run);
    count += handle.setCellValues(pending.filter((e): e is ValueEdit => e.kind === 'value'));
  } else {
    for (const e of pending) {
      if (e.kind === 'formula' ? handle.setCellFormula(e.sheetName, e.row, e.col, e.formula)
        : handle.setCellValue(e.sheetName, e.row, e.col, e.value, undefined, e.opts)) count++;
    }
  }
  return count;
}

/** Engine B write-back: stamp the SERVER-computed Result / %Recovery / RPD / QCStatus into each
 *  result-band row, keyed by (SysSampleCode|Parameter|RunNo). Used ONLY for templates whose SDMS
 *  Formula Setup has runnable (Fixed) bindings — the caller gates on `sdmsHasFormulas`, so for the
 *  common case (no bindings) this never runs and the sheet's own Univer formulas are untouched.
 *  Mirrors the applyResultRounding band-walk but writes STATIC computed values (setCellValue)
 *  rather than _LIMSRound wraps. In View/read-only mode setCellValue no-ops (nothing is written).
 *  Returns the number of cells written. */
export async function writeEngineResultsToSheet(
  buf: ArrayBuffer,
  fieldCells: Record<string, string>,
  computedByKey: ReadonlyMap<string, SdmsCalcRow>,
  handle: SdmsUniverHandle,
  /** `buf` already parsed — the caller's one parse shared with overlaySheetInputs (speed, 2026-09-30). Only read. */
  parsed?: import('exceljs').Workbook,
): Promise<number> {
  const parse = (a1?: string) => {
    const m = /^([A-Z]+)(\d+)$/.exec((a1 ?? '').toUpperCase());
    if (!m) return null;
    let col = 0; for (const ch of m[1]!) col = col * 26 + (ch.charCodeAt(0) - 64);
    return { col, row: parseInt(m[2]!, 10) };   // 1-based
  };
  const par = parse(fieldCells.PARAMETER);
  if (!par) return 0;
  const res = parse(fieldCells.RESULT);
  const rec = parse(fieldCells['%RECOVERY']);
  const rpd = parse(fieldCells.RPD);
  const qc  = parse(fieldCells.QCSTATUS);
  // AnalyzedDate is Engine B's native AnalyzedDate_MinBySample output (the min AnalyzedDate per sample);
  // write it back alongside the other computed columns so the whole engine contract lands on the sheet.
  const ad  = parse(fieldCells.ANALYZEDDATE);
  // The engine's cross-row source results (Dup's parent result, MS/MSD's parent result) — OG's re-merge
  // after Execute shows them in these columns and its save stores them (QB26092401_001, 2026-09-24).
  const dsr = parse(fieldCells.DUPSOURCERESULT);
  const ssr = parse(fieldCells.SPIKESOURCERESULT);
  if (!res && !rec && !rpd && !qc && !ad && !dsr && !ssr) return 0;   // no output columns positioned in this template
  const sysP = parse(fieldCells.SYSSAMPLECODE);
  const runP = parse(fieldCells.RUNNO);
  const startRow = par.row;
  const cellText = (ws: import('exceljs').Worksheet, row: number, col?: number): string => {
    if (!col) return '';
    return sheetCellText(ws.getRow(row).getCell(col));
  };
  let wb = parsed;
  if (!wb) {
    const ExcelJS = (await import('exceljs')).default;
    wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buf);
  }
  let count = 0;
  // TYPED write-back (parity class 3, 2026-09-23). OG's engine writes Result / Rec % / RPD as
  // DOUBLES, so a numeric output lands as a NUMBER (the template's "0.00" then formats it exactly as
  // OG); "<0.25" / "ND" stay text. AnalyzedDate is Engine B's echo of the merged value: when it is
  // unchanged the merged DATE cell is left alone (a text write would turn the serial back into text
  // in the saved blob); only a genuinely different value is written.
  const put = (ws: import('exceljs').Worksheet, row: number, cell: { col: number } | null, v: string | null | undefined,
               kind: 'number' | 'text' | 'date'): void => {
    if (!cell || v == null || v === '') return;
    if (kind === 'date') {
      const want = canonicalDateText(v);
      if (want) {
        // The cell's current date — a plain value, or a formula's cached result (HAA BF8
        // `IF(D8="THAA5",BF7,serial)`): an unchanged date leaves the cell, formula included, alone.
        const raw = ws.getRow(row).getCell(cell.col).value as unknown;
        const cur = raw && typeof raw === 'object' && !(raw instanceof Date) && 'result' in raw
          ? (raw as { result?: unknown }).result : raw;
        const curMs = cur instanceof Date ? cur.getTime()
          : typeof cur === 'number' ? (cur - 25_569) * 86_400_000 : NaN;
        if (Math.abs(curMs - want.getTime()) < 60_000) return;
        // A different date lands as its Excel SERIAL so the cell's own date format shows it (OG writes a
        // DateTime there); as text it bypassed the format and showed "2026-09-30 14:53:04-05:00".
        if (handle.setCellValue(ws.name, row - 1, cell.col - 1, want.getTime() / 86_400_000 + 25_569)) count++;
        return;
      }
    }
    const tv: string | number = kind === 'number' && Number.isFinite(Number(v)) ? Number(v) : v;
    // OG's engine stamp sets the cell's format from the value's decimals (QCBatchReportingRecalc :766).
    const fmt = typeof tv === 'number' ? decimalFormatOf(v) : undefined;
    if (handle.setCellValue(ws.name, row - 1, cell.col - 1, tv, fmt)) count++;
  };
  const putSource = (ws: import('exceljs').Worksheet, row: number, cell: { col: number } | null, v: string | null | undefined,
                     hasSource: boolean): void => {
    if (!cell) return;
    if (v == null || v.trim() === '') {
      if (!hasSource) return;                       // not a Dup / spiked row — its cell is never touched
      if (cellText(ws, row, cell.col) !== '' && handle.setCellValue(ws.name, row - 1, cell.col - 1, '')) count++;
      return;
    }
    put(ws, row, cell, v, 'number');
  };
  for (const ws of wb.worksheets) {
    if (!isResultSheet(ws.name, wb.worksheets.indexOf(ws))) continue;
    for (let row = startRow; row <= ws.rowCount; row++) {
      const pName = cellText(ws, row, par.col);
      if (!pName) break;   // end of the detail band
      // Per-sample-sheet (SampleSheetNavigation) templates name each sheet by its SysSampleCode.
      const sys = sysP ? cellText(ws, row, sysP.col) : ws.name;
      const hit = computedByKey.get(calcRowKey(sys, pName, runP ? cellText(ws, row, runP.col) : ''));
      if (!hit) continue;
      put(ws, row, res, hit.computedResult,       'number');
      put(ws, row, rec, hit.computedRecovery,     'number');
      put(ws, row, rpd, hit.computedRpd,          'number');
      put(ws, row, qc,  hit.computedQcStatus,     'text');
      put(ws, row, ad,  hit.computedAnalyzedDate, 'date');
      // The source sample's reported Result, shown as that Result shows (42.3 — a NUMBER under its own decimals,
      // so a template "0.00" format cannot make it 42.30, user report 2026-10-10). BLANK while the source has no
      // result (the engine clears it, OG parity), which also empties a value an earlier entry left behind.
      putSource(ws, row, dsr, hit.computedDupSourceResult, !!hit.dupSource?.trim());
      putSource(ws, row, ssr, hit.computedSpikeSourceResult, !!hit.spikeSource?.trim());
    }
  }
  return count;
}

/** OG Recalculate's "reset formulas to the pristine template" effect for AI: unwrap every
 *  Apply-Rounding `_LIMSRound` wrap back to its inner formula (AI templates carry no built-in
 *  _LIMSRound — every wrap is from Apply Rounding), and clear the rounding display format so the
 *  recomputed raw value shows. Reuses the sheet's setCellFormula; only formula cells change (typed
 *  values are untouched). Returns the number of cells restored. */
export async function stripLimsRoundWraps(buf: ArrayBuffer, handle: SdmsUniverHandle): Promise<number> {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  const edits: Array<{ sheetName: string; row: number; col: number; formula: string; numberFormat?: string }> = [];
  for (const ws of wb.worksheets) {
    if (!isResultSheet(ws.name, wb.worksheets.indexOf(ws))) continue;
    ws.eachRow({ includeEmpty: false }, (row, rowNum) => {
      row.eachCell({ includeEmpty: false }, (cell, colNum) => {
        const fv = cell.value as { formula?: string } | null;
        const expr = (fv && typeof fv === 'object' && typeof fv.formula === 'string')
          ? fv.formula
          : (typeof cell.formula === 'string' ? cell.formula : '');
        if (!expr || !/_LIMSRound\s*\(/i.test(expr)) return;
        const inner = unwrapLimsRound(expr);
        if (inner) edits.push({ sheetName: ws.name, row: rowNum - 1, col: colNum - 1, formula: inner, numberFormat: 'General' });
      });
    });
  }
  return writeFormulas(handle, edits);
}

/** Write many formulas through the handle's batch setter — ONE write per sheet. Recalculate's re-push wrote
 *  them one cell at a time, and every single write makes Univer rebuild the formula data of every sheet and
 *  restart the running calculation: on a large template (Calcium, 2026-10-10) Recalculate hung the tab.
 *  Falls back to the per-cell setter when the handle has no batch one. Returns the cells written. */
function writeFormulas(handle: SdmsUniverHandle,
  edits: ReadonlyArray<{ sheetName: string; row: number; col: number; formula: string; numberFormat?: string }>): number {
  if (!edits.length) return 0;
  if (handle.setCellFormulas) return handle.setCellFormulas(edits);
  let count = 0;
  for (const e of edits) if (handle.setCellFormula(e.sheetName, e.row, e.col, e.formula, e.numberFormat)) count++;
  return count;
}

/** ArrayBuffer -> base64 (chunked to avoid arg-length limits). */
function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  return btoa(bin);
}
